// ============================================================
// pdfRelatorio.js — Gerador do RELATÓRIO DE FREQUÊNCIA em PDF.
// Modelo institucional "Modelo 2" (aprovado): capa, resumo do núcleo,
// e uma seção por modalidade (só atletas). Marca oficial Revelar
// Talentos + régua de parceiros em todas as páginas.
//
// Roda no NAVEGADOR (usa pdf-lib). Consome os mesmos dados do
// calcularFrequencia (fonte única) — NÃO recalcula nada aqui.
//
// Uso:
//   await gerarPDFRelatorio({ totalTreinos, membros, nucleo, mes, ano });
// ============================================================
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { normalizarModalidade, mediaPctTotal, somenteAtivos } from "./calculo";
import { MESES as MESES_DO_ANO } from "./tema";
import { nucleoParaArquivo } from "./nomeDeArquivo";
import { ehMaiorDeIdade, idadeEm } from "./idade";
// Os títulos das seções da avaliação vêm da MESMA lista que a tela usa.
// Renomeado com sufixo para não colidir com nada deste arquivo.
import { SECOES as SECOES_DA_AVALIACAO } from "./avaliacaoCiclo";
import {
  rotuloDoProtocolo, rotuloDoEstagio, laudoDoSomatotipo, laudoDaMaturacao,
  // A ficha de coleta em papel imprime os mesmos campos que a tela
  // lança, e por isso lê as mesmas listas: papel e formulário não podem
  // pedir medidas diferentes.
  DOBRAS, PERIMETROS_POR_CAMPO, DIAMETROS, ALTURAS,
  PROTOCOLOS_DE_GORDURA, ESTAGIOS_MATURACIONAIS,
} from "./avaliacaoFisica";
// A conta da carga semana a semana mora num arquivo só: a tela e o
// papel têm de dar o mesmo número, e eram duas contas em dois lugares.
import { semanasDaLinha } from "./cargaDeTreino";
// A avaliação técnica e os testes físicos, pela mesma regra das outras
// seções: o laudo, a bateria e os critérios saem do módulo, e a folha só
// desenha. Os apelidos existem para não colidir com nomes deste arquivo,
// que já tem `TESTES` e `CONVERSOES` querendo dizer outra coisa.
import {
  modeloDe as modeloDeCriterio, laudoTecnico,
  rotuloDaAusencia as rotuloDaAusenciaDoCriterio,
} from "./avaliacaoTecnica";
import {
  bateriaDa as bateriaDeTestes, capacidadesSugeridas as capacidadesDeModalidade,
  CONVERSOES as CONVERSOES_DE_TESTE,
  faixaEtariaDe as faixaEtariaDoTesteEm, FAIXAS_DO_CIRCUITO,
  FAIXAS_ETARIAS as FAIXAS_ETARIAS_DO_TESTE, AUSENCIAS as AUSENCIAS_DO_TESTE,
  FAIXA_MORTA as FAIXA_MORTA_DO_TESTE, laudoDosTestes,
  direcaoEmTexto as direcaoDosTestesEmTexto,
} from "./testesFisicos";
import { evolucaoDoEscore as evolucaoDoEscoreGlobal } from "./desempenho";
import {
  legendaDoQuadro as legendaDoQuadroDeMedalhas, disputaVariasCategorias,
} from "./competicoes";

const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

// ── Paleta (mesma identidade das logos e do painel) ──
const AZUL = rgb(0x2e / 255, 0x4e / 255, 0x9e / 255);
const VERDE = rgb(0x4c / 255, 0xa6 / 255, 0x4c / 255);
const AMARELO = rgb(0xc9 / 255, 0x9a / 255, 0x00 / 255);
const TEXTO = rgb(0x1a / 255, 0x22 / 255, 0x33 / 255);
const CINZA = rgb(0x64 / 255, 0x74 / 255, 0x8b / 255);
const CINZA_CL = rgb(0xf8 / 255, 0xfa / 255, 0xfc / 255);
const BORDA = rgb(0xe5 / 255, 0xe7 / 255, 0xeb / 255);
const BRANCO = rgb(1, 1, 1);
const BARRA_BG = rgb(0xee / 255, 0xf2 / 255, 0xf7 / 255);
const BORDA_FORTE_PDF = rgb(0xd1 / 255, 0xd5 / 255, 0xdb / 255);

// Cores das classificações (iguais ao painel/Excel, em RGB)
function hexRgb(h) { return rgb(parseInt(h.slice(0, 2), 16) / 255, parseInt(h.slice(2, 4), 16) / 255, parseInt(h.slice(4, 6), 16) / 255); }
const CLS = {
  mb: { c: hexRgb("00853B"), bg: hexRgb("D1FAE5"), l: "Muito bom" },
  b: { c: hexRgb("1D6FA4"), bg: hexRgb("DBEAFE"), l: "Bom" },
  r: { c: hexRgb("D97706"), bg: hexRgb("FFF3CD"), l: "Regular" },
  ru: { c: hexRgb("EA580C"), bg: hexRgb("FFE4CC"), l: "Ruim" },
  mr: { c: hexRgb("DC2626"), bg: hexRgb("FEE2E2"), l: "Muito ruim" },
};
function classifPct(pct) {
  if (pct >= 90) return CLS.mb;
  if (pct >= 80) return CLS.b;
  if (pct >= 70) return CLS.r;
  if (pct >= 50) return CLS.ru;
  return CLS.mr;
}

// Identificação do PROGRAMA na capa e no rodapé.
//
// O programa é um só, Revelar Talentos, e o núcleo é onde ele acontece.
// Por um tempo esta linha recebeu o nome do núcleo e saía como "Programa
// CFO - Ceará", o que trocava uma coisa pela outra: dava ao núcleo o
// status de programa e escondia o nome do programa de quem lê a capa.
//
// O núcleo continua identificado onde deve, no bloco de informações da
// capa (linha NÚCLEO) e no cabeçalho de todas as páginas internas, então
// nada se perde ao fixar estas duas linhas.
const IDENTIFICACAO = ["Programa Revelar Talentos", "Núcleo de Treinamento de Esporte de Base"];

// A4 em pontos
// A data como o banco guarda. Montada por partes porque a barra
// invertida dentro de texto some em algumas ferramentas de edição, e a
// expressão quebrada aceitaria qualquer dígito solto como data.
// O gênero escrito como a Gestão escreve, e não a chave do banco:
// "Masculino", não "masculino".
const ROTULO_DO_GENERO = { masculino: "Masculino", feminino: "Feminino" };

const DIGITO = "[0-9]";
const DATA_ISO = new RegExp("^" + DIGITO + "{4}-" + DIGITO + "{2}-" + DIGITO + "{2}$");

const A4W = 595.28, A4H = 841.89;
const MX = 42;              // margem lateral
const TOP_CONTENT = A4H - 74; // início do conteúdo (abaixo do cabeçalho)
const BOTTOM_LIMIT = 92;   // acima do rodapé

async function fetchBytes(url) {
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`Falha ao carregar ${url}`);
  return new Uint8Array(await resp.arrayBuffer());
}

// O Helvetica padrão (WinAnsi) não codifica alguns símbolos (≥, –, …, 🌟).
// Troca por equivalentes seguros. Acentos do português (á, ã, ç, é…) SÃO
// suportados pelo WinAnsi, então permanecem.
// Data ISO para o formato de quem lê.
function fmtDataBR(iso) {
  const p = String(iso || "").split("-");
  return p.length === 3 ? p[2] + "/" + p[1] + "/" + p[0] : String(iso || "");
}

function txtSeguro(s) {
  return String(s == null ? "" : s)
    .replace(/≥/g, ">=").replace(/≤/g, "<=")
    // O travessão e o sinal de menos tipográfico, que a tela usa e o
    // WinAnsi não codifica. O menos vira hífen aqui e não some: num
    // número, apagá-lo transformaria queda em alta.
    .replace(/[–—]/g, "-")
    .replace(/[−]/g, "-")
    .replace(/…/g, "...")
    .replace(/[🌟✔⚠🔻⭐️★]/gu, "").replace(/\s{2,}/g, " ").trim();
}

// ── Desenho da tabela de frequência ──────────────────────────
// Extraído de dentro do relatório geral para escopo de módulo: o
// relatório POR MODALIDADE precisa da mesma tabela, e duplicá-la faria
// os dois documentos divergirem visualmente na primeira alteração.
// Recebe as fontes porque elas só existem depois de o documento abrir.
// `rotuloDaPrimeiraColuna` existe porque a mesma tabela serve ao relatório
// dos atletas e ao da equipe técnica. Chamar de "Atleta" a linha de um
// técnico é dizer a coisa errada num documento que sai impresso.
function criarDesenhoTabela({ fontR, fontB, rotuloDaPrimeiraColuna = "Atleta" }) {
  function colunas() {
    const total = A4W - 2 * MX;
    // Colunas iguais às do Excel (gestão de presença), por extenso, SEM a
    // coluna "Dias de treino" (já consta no topo do relatório).
    // Cabeçalhos com "\n" quebram em duas linhas.
    const defs = [
      { h: rotuloDaPrimeiraColuna, p: 0.225, a: "left" },
      { h: "Presenças", p: 0.075, a: "center" },
      { h: "Faltas não\njustificadas", p: 0.088, a: "center" },
      { h: "Justif.\nColetiva", p: 0.082, a: "center" },
      { h: "Justif.\nModalidade", p: 0.088, a: "center" },
      { h: "Justif.\nIndividual", p: 0.085, a: "center" },
      { h: "%\nAbsoluto", p: 0.075, a: "center" },
      { h: "%\nJustif.", p: 0.065, a: "center" },
      { h: "%\nTotal", p: 0.062, a: "center" },
      { h: "Classificação", p: 0.155, a: "center" },
    ];
    return defs.map(d => ({ h: d.h, w: total * d.p, align: d.a }));
  }
  function cabecalhoTabela(pg, yy) {
    const cols = colunas();
    let cx = MX;
    const hH = 24; // cabeçalho mais alto (2 linhas)
    pg.drawRectangle({ x: MX, y: yy - hH, width: A4W - 2 * MX, height: hH, color: AZUL });
    cols.forEach(c => {
      // Cabeçalho pode ter 1 ou 2 linhas (separadas por "\n").
      const linhas = c.h.split("\n");
      const size = 6.8;
      linhas.forEach((ln, li) => {
        const tw = fontB.widthOfTextAtSize(ln, size);
        let tx;
        if (c.align === "left") tx = cx + 4;
        else tx = cx + (c.w - tw) / 2;
        // centraliza verticalmente: 1 linha no meio, 2 linhas empilhadas
        const yBase = linhas.length === 1 ? yy - 15 : yy - 10 - li * 8;
        pg.drawText(ln, { x: tx, y: yBase, size, font: fontB, color: BRANCO });
      });
      cx += c.w;
    });
    return yy - hH;
  }
  function linhaAtleta(pg, yy, m, zebra) {
    const cols = colunas();
    const cl = classifPct(m.pctTotal * 100);
    const rowH = 15;
    if (zebra) pg.drawRectangle({ x: MX, y: yy - rowH, width: A4W - 2 * MX, height: rowH, color: CINZA_CL });
    let cx = MX;
    // Ordem: Atleta, Presenças, Faltas, J.Coletiva, J.Modalidade, J.Individual,
    // % Absoluto, % Justif., % Total, Classificação.
    const vals = [
      m.inativo ? `${m.nome} (inativo)` : m.nome, m.presEfetiva, m.faltas, m.jc, m.jm, m.ji,
      `${Math.round(m.pctAbs * 100)}%`, `${Math.round(m.pctJust * 100)}%`, `${Math.round(m.pctTotal * 100)}%`, cl.l,
    ];
    const IDX_TOTAL = 8, IDX_CLS = 9, IDX_NOME = 0;
    cols.forEach((c, i) => {
      if (i === IDX_CLS) {
        pg.drawRectangle({ x: cx, y: yy - rowH, width: c.w, height: rowH, color: cl.bg });
      }
      const destaque = (i === IDX_TOTAL || i === IDX_CLS);
      const font = (destaque || i === IDX_NOME) ? fontB : fontR;
      const color = destaque ? cl.c : TEXTO;
      const txt = txtSeguro(vals[i]);
      const size = 7.5;
      const tw = font.widthOfTextAtSize(txt, size);
      let tx;
      if (c.align === "left") tx = cx + 4;
      else tx = cx + (c.w - tw) / 2;
      pg.drawText(txt.length > 30 && i === IDX_NOME ? txt.slice(0, 29) + "..." : txt, { x: tx, y: yy - 10, size, font, color });
      cx += c.w;
    });
    pg.drawLine({ start: { x: MX, y: yy - rowH }, end: { x: A4W - MX, y: yy - rowH }, thickness: 0.4, color: BORDA });
    return yy - rowH;
  }
  function barraTitulo(pg, yy, titulo, subtitulo, cor) {
    titulo = txtSeguro(titulo); subtitulo = txtSeguro(subtitulo);
    pg.drawRectangle({ x: MX, y: yy - 20, width: A4W - 2 * MX, height: 20, color: cor });
    pg.drawText(titulo, { x: MX + 8, y: yy - 14, size: 12, font: fontB, color: BRANCO });
    const sw = fontR.widthOfTextAtSize(subtitulo, 9);
    pg.drawText(subtitulo, { x: A4W - MX - sw - 8, y: yy - 13, size: 9, font: fontR, color: hexRgb("DBEAFE") });
    return yy - 20;
  }

  return { colunas, cabecalhoTabela, linhaAtleta, barraTitulo };
}

// `nucleo` é o identificador (chave dos registros) e continua sendo o que
// filtra e agrupa. `nucleoNome` é o nome de cadastro, e é ele que aparece
// DENTRO do documento e também no NOME DO ARQUIVO, limpo de acento e de
// espaço (ver nomeDeArquivo.js): quem recebe o anexo conhece o núcleo pelo
// nome, não pela chave.
export async function gerarPDFRelatorio({ totalTreinos, membros, nucleo, nucleoNome, mes, ano }) {
  const rotuloNucleo = nucleoNome || nucleo;
  const doc = await PDFDocument.create();
  const fontR = await doc.embedFont(StandardFonts.Helvetica);
  const fontB = await doc.embedFont(StandardFonts.HelveticaBold);

  // Carrega as imagens da pasta public (colocadas em /relatorio/).
  let logoImg = null, reguaImg = null;
  try { logoImg = await doc.embedPng(await fetchBytes(`${process.env.PUBLIC_URL || ""}/relatorio/logo_revelar.png`)); } catch (e) { /* segue sem logo */ }
  try { reguaImg = await doc.embedPng(await fetchBytes(`${process.env.PUBLIC_URL || ""}/relatorio/regua_parceiros.png`)); } catch (e) { /* segue sem régua */ }

  const periodo = `${MESES[mes]} / ${ano}`;
  const hoje = new Date();
  const emitidoEm = `${String(hoje.getDate()).padStart(2, "0")}/${String(hoje.getMonth() + 1).padStart(2, "0")}/${hoje.getFullYear()}`;

  // Só os atletas entram neste relatório. A equipe técnica continua vindo
  // em `membros` e sendo usada no painel e no Excel — aqui ela é
  // descartada de propósito (ver nota no fim da função).
  const atletas = membros.filter(m => m.grupo === "ATLETA");
  const totalAtletas = atletas.length;

  // Agrupa atletas por modalidade.
  const porMod = {};
  atletas.forEach(m => {
    const mod = normalizarModalidade(m.modalidade || "Sem modalidade");
    (porMod[mod] = porMod[mod] || []).push(m);
  });
  const modalidades = Object.keys(porMod).sort();

  // Média geral do núcleo (atletas).
  const mediaGeral = Math.round((mediaPctTotal(atletas) ?? 0) * 100);

  // Distribuição por classificação (atletas).
  const dist = { mb: 0, b: 0, r: 0, ru: 0, mr: 0 };
  atletas.forEach(m => {
    const p = m.pctTotal * 100;
    if (p >= 90) dist.mb++; else if (p >= 80) dist.b++; else if (p >= 70) dist.r++; else if (p >= 50) dist.ru++; else dist.mr++;
  });

  // ---- Estado de paginação ----
  const paginas = [];
  const { cabecalhoTabela, linhaAtleta, barraTitulo } = criarDesenhoTabela({ fontR, fontB });

  let page = null, y = 0;
  const sumario = []; // { titulo, pageIndex }

  function novaPagina() {
    page = doc.addPage([A4W, A4H]);
    paginas.push(page);
    desenharCabecalho(page);
    desenharRodape(page);
    y = TOP_CONTENT;
    return paginas.length - 1;
  }
  function garantirEspaco(altura) {
    if (y - altura < BOTTOM_LIMIT) novaPagina();
  }

  function desenharCabecalho(pg) {
    // Faixa lateral azul
    pg.drawRectangle({ x: 0, y: 0, width: 6, height: A4H, color: AZUL });
    // Logo topo direito (menor, para caber acima da linha sem sobreposição)
    let logoLeftX = A4W - MX; // onde a logo começa (default: sem logo)
    if (logoImg) {
      const w = 34, h = w * logoImg.height / logoImg.width;
      logoLeftX = A4W - MX - w;
      pg.drawImage(logoImg, { x: logoLeftX, y: A4H - h - 14, width: w, height: h });
    }
    // Título de miolo
    pg.drawText("REVELAR TALENTOS", { x: MX, y: A4H - 34, size: 10, font: fontB, color: AZUL });
    pg.drawText(txtSeguro(`Núcleo ${rotuloNucleo} · ${periodo}`), { x: MX, y: A4H - 46, size: 8, font: fontR, color: CINZA });
    // Linha divisória: vai só até um pouco ANTES da logo, para não passar por baixo dela.
    pg.drawLine({ start: { x: MX, y: A4H - 56 }, end: { x: logoLeftX - 12, y: A4H - 56 }, thickness: 0.5, color: BORDA });
  }
  function desenharRodape(pg) {
    if (reguaImg) {
      const w = A4W - 2 * MX - 40, h = w * reguaImg.height / reguaImg.width;
      pg.drawImage(reguaImg, { x: (A4W - w) / 2, y: 40, width: w, height: h });
    }
    pg.drawLine({ start: { x: MX, y: 34 }, end: { x: A4W - MX, y: 34 }, thickness: 0.5, color: BORDA });
    pg.drawText(txtSeguro(IDENTIFICACAO.join(" · ")), { x: MX, y: 22, size: 7, font: fontR, color: CINZA });
    const num = `Página ${paginas.indexOf(pg) + 1}`;
    pg.drawText(num, { x: A4W - MX - fontR.widthOfTextAtSize(num, 7), y: 22, size: 7, font: fontR, color: CINZA });
  }

  function textoCentrado(pg, txt, yy, size, font, color) {
    txt = txtSeguro(txt);
    const w = font.widthOfTextAtSize(txt, size);
    pg.drawText(txt, { x: (A4W - w) / 2, y: yy, size, font, color });
  }

  // ══════════ CAPA ══════════
  {
    const pg = doc.addPage([A4W, A4H]);
    paginas.push(pg);
    // Bloco azul superior (52% da altura) com faixas verde/amarelo
    const topo = A4H * 0.52;
    pg.drawRectangle({ x: 0, y: topo, width: A4W, height: A4H - topo, color: AZUL });
    pg.drawRectangle({ x: 0, y: topo, width: A4W, height: 8, color: VERDE });
    pg.drawRectangle({ x: 0, y: topo - 8, width: A4W, height: 8, color: hexRgb("F5C518") });
    // Título e frase (sobre o azul)
    textoCentrado(pg, "RELATÓRIO DE FREQUÊNCIA", topo + 150, 26, fontB, BRANCO);
    // frase com "jovens atletas" destacado
    const f1 = "Impulsionando ", f2 = "jovens atletas", f3 = " rumo ao alto desempenho";
    const s = 14;
    const wTotal = fontB.widthOfTextAtSize(f1, s) + fontB.widthOfTextAtSize(f2, s) + fontB.widthOfTextAtSize(f3, s);
    let fx = (A4W - wTotal) / 2;
    const fy = topo + 118;
    pg.drawText(f1, { x: fx, y: fy, size: s, font: fontB, color: BRANCO }); fx += fontB.widthOfTextAtSize(f1, s);
    pg.drawText(f2, { x: fx, y: fy, size: s, font: fontB, color: hexRgb("F5C518") }); fx += fontB.widthOfTextAtSize(f2, s);
    pg.drawText(f3, { x: fx, y: fy, size: s, font: fontB, color: BRANCO });
    // Identificação do núcleo, sobre o azul.
    let ny = topo + 100;
    IDENTIFICACAO.forEach((linha, i) => {
      textoCentrado(pg, linha, ny, i === 0 ? 12 : 10, i === 0 ? fontB : fontR, hexRgb("DBEAFE"));
      ny -= i === 0 ? 18 : 14;
    });

    // Régua de parceiros no rodapé da capa
    if (reguaImg) {
      const w = A4W - 2 * MX - 20, h = w * reguaImg.height / reguaImg.width;
      pg.drawImage(reguaImg, { x: (A4W - w) / 2, y: 44, width: w, height: h });
    }

    // Logomarca na metade branca, e não sobre o azul: o logo tem o nome
    // em azul-escuro, que desapareceria no bloco azul da capa.
    if (logoImg) {
      const w = 78, h = w * logoImg.height / logoImg.width;
      pg.drawImage(logoImg, { x: (A4W - w) / 2, y: topo - 26 - h, width: w, height: h });
    }

    // Caixa de identificação (na metade de baixo)
    const bx = MX + 30, bw = A4W - 2 * (MX + 30), bh = 132, byTop = topo - 150;
    pg.drawRectangle({ x: bx, y: byTop - bh, width: bw, height: bh, borderColor: BORDA, borderWidth: 1, color: BRANCO });
    const linhas = [["NÚCLEO", rotuloNucleo], ["PERÍODO", periodo], ["EMITIDO EM", emitidoEm], ["ATLETAS", String(totalAtletas)]];
    let ly = byTop - 26;
    linhas.forEach(([rot, val], i) => {
      pg.drawText(txtSeguro(rot), { x: bx + 20, y: ly, size: 9, font: fontB, color: CINZA });
      pg.drawText(txtSeguro(val), { x: bx + 160, y: ly - 2, size: 13, font: fontB, color: TEXTO });
      if (i < linhas.length - 1) pg.drawLine({ start: { x: bx + 16, y: ly - 12 }, end: { x: bx + bw - 16, y: ly - 12 }, thickness: 0.5, color: BORDA });
      ly -= 30;
    });
  }

  // ══════════ RESUMO DO NÚCLEO ══════════
  novaPagina();
  page.drawText("Resumo do núcleo", { x: MX, y, size: 16, font: fontB, color: AZUL });
  y -= 30;
  // 4 cartões
  const cardW = (A4W - 2 * MX - 3 * 10) / 4, cardH = 60;
  const cards = [["Atletas ativos", String(totalAtletas), AZUL], ["Frequência média", `${mediaGeral}%`, VERDE], ["Dias de treino", String(totalTreinos), AZUL], ["Modalidades", String(modalidades.length), AZUL]];
  cards.forEach(([rot, val, cor], i) => {
    const cx = MX + i * (cardW + 10);
    page.drawRectangle({ x: cx, y: y - cardH, width: cardW, height: cardH, color: CINZA_CL, borderColor: BORDA, borderWidth: 1 });
    const vw = fontB.widthOfTextAtSize(val, 22);
    page.drawText(val, { x: cx + (cardW - vw) / 2, y: y - 30, size: 22, font: fontB, color: cor });
    const rw = fontR.widthOfTextAtSize(rot, 8);
    page.drawText(txtSeguro(rot), { x: cx + (cardW - rw) / 2, y: y - 48, size: 8, font: fontR, color: CINZA });
  });
  y -= cardH + 28;

  // ── Quadro "Distribuição de Frequência por modalidade" ──
  // Vem ANTES da distribuição por classificação: a leitura natural do
  // relatório é do coletivo para o individual — primeiro como cada
  // modalidade está, depois como os atletas se distribuem nas faixas.
  garantirEspaco(30 + modalidades.length * 22);
  page.drawText("Distribuição de Frequência por modalidade", { x: MX, y, size: 11, font: fontB, color: TEXTO });
  y -= 20;
  const modBarX = MX + 150, modBarMaxW = 200;
  modalidades.forEach(mod => {
    const lista = porMod[mod];
    const media = Math.round((mediaPctTotal(lista) ?? 0) * 100);
    const cor = classifPct(media).c;
    // nome da modalidade
    page.drawText(txtSeguro(mod), { x: MX, y: y - 8, size: 9, font: fontB, color: TEXTO });
    // nº de atletas
    page.drawText(`${somenteAtivos(lista).length} atl.`, { x: MX + 100, y: y - 8, size: 8, font: fontR, color: CINZA });
    // barra proporcional ao % médio
    page.drawRectangle({ x: modBarX, y: y - 11, width: modBarMaxW, height: 8, color: BARRA_BG });
    page.drawRectangle({ x: modBarX, y: y - 11, width: Math.max(2, modBarMaxW * media / 100), height: 8, color: cor });
    // % médio
    page.drawText(`${media}%`, { x: modBarX + modBarMaxW + 12, y: y - 8, size: 9, font: fontB, color: cor });
    y -= 22;
  });

  // ── Quadro "Distribuição dos atletas por classificação" ──
  y -= 14;
  garantirEspaco(30 + 5 * 22);
  page.drawText("Distribuição dos atletas por classificação", { x: MX, y, size: 11, font: fontB, color: TEXTO });
  y -= 20;
  const distRows = [["Muito bom (>=90%)", dist.mb, CLS.mb.c], ["Bom (80-89%)", dist.b, CLS.b.c], ["Regular (70-79%)", dist.r, CLS.r.c], ["Ruim (50-69%)", dist.ru, CLS.ru.c], ["Muito ruim (<50%)", dist.mr, CLS.mr.c]];
  const barX = MX + 150, barMaxW = 200;
  distRows.forEach(([label, n, cor]) => {
    page.drawText(txtSeguro(label), { x: MX, y: y - 8, size: 9, font: fontR, color: TEXTO });
    page.drawRectangle({ x: barX, y: y - 11, width: barMaxW, height: 8, color: BARRA_BG });
    const w = totalAtletas ? Math.max(2, barMaxW * n / totalAtletas) : 2;
    page.drawRectangle({ x: barX, y: y - 11, width: w, height: 8, color: cor });
    page.drawText(`${n} atletas`, { x: barX + barMaxW + 12, y: y - 8, size: 9, font: fontB, color: CINZA });
    y -= 22;
  });

  // ══════════ SEÇÕES POR MODALIDADE ══════════

  modalidades.forEach(mod => {
    const lista = porMod[mod].slice().sort((a, b) => a.pctTotal - b.pctTotal);
    // espaço mínimo para barra+cabeçalho+1 linha
    garantirEspaco(20 + 24 + 15 + 10);
    y -= 14;
    const idx = paginas.length - 1;
    sumario.push({ titulo: mod, pageIndex: idx });
    y = barraTitulo(page, y, mod.toUpperCase(), `${lista.length} atleta(s)`, VERDE);
    y -= 6;
    y = cabecalhoTabela(page, y);
    lista.forEach((m, i) => {
      garantirEspaco(15);
      if (y === TOP_CONTENT) y = cabecalhoTabela(page, y); // recolocar cabeçalho se quebrou página
      y = linhaAtleta(page, y, m, i % 2 === 1);
    });
  });

  // O quadro de presença da EQUIPE TÉCNICA saiu deste relatório a pedido
  // da coordenação: o documento é sobre a frequência dos atletas, e a da
  // equipe é acompanhamento interno. Ela continua no Excel e no painel,
  // que recebem os mesmos `membros` sem esse filtro.

  const bytes = await doc.save();
  const nomeArquivo = `Relatorio_${nucleoParaArquivo(nucleoNome, nucleo)}_${MESES[mes]}_${ano}.pdf`;
  baixar(bytes, nomeArquivo);
  return nomeArquivo;
}

// ============================================================
// RELATÓRIO POR MODALIDADE
//
// Mesmo padrão visual do relatório geral — logomarca, cabeçalho, tabela
// e rodapé idênticos — mas sem capa: é um documento de trabalho do
// técnico, não uma peça institucional. O conteúdo espelha o Excel por
// modalidade: os números do topo e a tabela dos atletas.
//
// A equipe técnica saiu daqui e ganhou documento próprio, escolhido no
// mesmo seletor: na mesma tabela, a frequência do técnico acabava lida
// como se fosse a de um atleta.
//
// A tabela vem de criarDesenhoTabela, a mesma do relatório geral, para
// os dois documentos não divergirem quando um deles for alterado.
// ============================================================
// Valor especial no lugar do nome de uma modalidade: pede o relatório da
// equipe técnica inteira, todas as modalidades juntas. Fica no mesmo
// seletor porque, para quem imprime, é mais um recorte da mesma lista, e
// não uma segunda ferramenta.
export const RELATORIO_EQUIPE = "__equipe_tecnica__";

export async function gerarPDFModalidade({ totalTreinos, membros, nucleo, nucleoNome, mes, ano, modalidade }) {
  const rotuloNucleo = nucleoNome || nucleo;
  const doc = await PDFDocument.create();
  const fontR = await doc.embedFont(StandardFonts.Helvetica);
  const fontB = await doc.embedFont(StandardFonts.HelveticaBold);

  let logoImg = null;
  try {
    logoImg = await doc.embedPng(await fetchBytes(`${process.env.PUBLIC_URL || ""}/relatorio/logo_revelar.png`));
  } catch (e) { /* segue sem logo */ }

  const ehEquipe = modalidade === RELATORIO_EQUIPE;
  const modalidadeNorm = ehEquipe ? "Equipe técnica" : normalizarModalidade(modalidade);
  const periodo = `${MESES[mes]} / ${ano}`;
  const hoje = new Date();
  const emitidoEm = `${String(hoje.getDate()).padStart(2, "0")}/${String(hoje.getMonth() + 1).padStart(2, "0")}/${hoje.getFullYear()}`;

  // Mesma seleção do Excel por modalidade: atletas da modalidade, pior
  // percentual primeiro — a ordem em que o técnico precisa olhar.
  //
  // No relatório da equipe a lista é a equipe inteira, de todas as
  // modalidades, ordenada por modalidade e depois pelo percentual. A
  // coordenação não entra: ela não pertence a uma modalidade e a
  // frequência dela não é medida do mesmo jeito.
  const atletas = ehEquipe
    ? membros
        .filter(m => m.grupo === "TECNICO")
        .sort((a, b) => {
          const cmp = (a.modalidade || "").localeCompare(b.modalidade || "", "pt-BR");
          return cmp !== 0 ? cmp : a.pctTotal - b.pctTotal;
        })
    : membros
        .filter(m => m.grupo === "ATLETA" && normalizarModalidade(m.modalidade) === modalidadeNorm)
        .sort((a, b) => a.pctTotal - b.pctTotal);

  const mediaTotal = Math.round((mediaPctTotal(atletas) ?? 0) * 100);

  const { cabecalhoTabela, linhaAtleta, barraTitulo } = criarDesenhoTabela({
    fontR, fontB, rotuloDaPrimeiraColuna: ehEquipe ? "Membro" : "Atleta",
  });

  let page = null, y = 0;

  function desenharCabecalho(pg) {
    pg.drawRectangle({ x: 0, y: 0, width: 6, height: A4H, color: AZUL });
    let logoLeftX = A4W - MX;
    if (logoImg) {
      const w = 34, h = w * logoImg.height / logoImg.width;
      logoLeftX = A4W - MX - w;
      pg.drawImage(logoImg, { x: logoLeftX, y: A4H - h - 14, width: w, height: h });
    }
    pg.drawText("REVELAR TALENTOS", { x: MX, y: A4H - 34, size: 10, font: fontB, color: AZUL });
    pg.drawText(
      txtSeguro(`Núcleo ${rotuloNucleo} · ${periodo}`),
      { x: MX, y: A4H - 46, size: 8, font: fontR, color: CINZA }
    );
    pg.drawLine({
      start: { x: MX, y: A4H - 56 },
      end: { x: logoLeftX - 12, y: A4H - 56 },
      thickness: 0.5, color: BORDA,
    });
  }

  function desenharRodape(pg) {
    pg.drawText(txtSeguro(IDENTIFICACAO.join(" · ")), { x: MX, y: 22, size: 7, font: fontR, color: CINZA });
    pg.drawText(txtSeguro(`Emitido em ${emitidoEm}`), {
      x: A4W - MX - fontR.widthOfTextAtSize(`Emitido em ${emitidoEm}`, 7),
      y: 22, size: 7, font: fontR, color: CINZA,
    });
  }

  function novaPagina() {
    page = doc.addPage([A4W, A4H]);
    desenharCabecalho(page);
    desenharRodape(page);
    y = TOP_CONTENT;
  }

  function garantirEspaco(altura) {
    if (y - altura < BOTTOM_LIMIT) novaPagina();
  }

  // ── Página única de abertura (sem capa) ──
  novaPagina();

  page.drawText(txtSeguro(modalidadeNorm.toUpperCase()), { x: MX, y, size: 20, font: fontB, color: AZUL });
  y -= 18;
  page.drawText(txtSeguro(ehEquipe
    ? "Relatório de frequência · técnicos, auxiliares e preparação física"
    : "Relatório de frequência por modalidade"), {
    x: MX, y, size: 10, font: fontR, color: CINZA,
  });
  y -= 26;

  // ── KPIs (mesmos três do Excel por modalidade) ──
  const cardW = (A4W - 2 * MX - 2 * 10) / 3, cardH = 58;
  const cards = [
    ["Dias de treino", String(totalTreinos), AZUL],
    [ehEquipe ? "Membros da equipe" : "Atletas", String(atletas.length), AZUL],
    ["Frequência média", `${mediaTotal}%`, classifPct(mediaTotal).c],
  ];
  cards.forEach(([rot, val, cor], i) => {
    const cx = MX + i * (cardW + 10);
    page.drawRectangle({ x: cx, y: y - cardH, width: cardW, height: cardH, color: CINZA_CL, borderColor: BORDA, borderWidth: 1 });
    const vw = fontB.widthOfTextAtSize(val, 20);
    page.drawText(val, { x: cx + (cardW - vw) / 2, y: y - 29, size: 20, font: fontB, color: cor });
    const rw = fontR.widthOfTextAtSize(txtSeguro(rot), 8);
    page.drawText(txtSeguro(rot), { x: cx + (cardW - rw) / 2, y: y - 46, size: 8, font: fontR, color: CINZA });
  });
  y -= cardH + 26;

  // ── Tabela de atletas ──
  if (atletas.length === 0) {
    page.drawText(txtSeguro(ehEquipe
      ? `Nenhum membro da equipe com registros neste mês.`
      : `Nenhum atleta de ${modalidadeNorm} com registros neste mês.`), {
      x: MX, y, size: 10, font: fontR, color: CINZA,
    });
    y -= 20;
  } else if (ehEquipe) {
    // Uma faixa por modalidade. A equipe inteira numa lista corrida
    // obrigaria a procurar de quem é cada nome, que é a informação que
    // a coordenação usa para cobrar.
    const porModalidade = [];
    atletas.forEach((m) => {
      const mod = m.modalidade || "Sem modalidade";
      const grupo = porModalidade.find((g) => g.mod === mod);
      if (grupo) grupo.lista.push(m);
      else porModalidade.push({ mod, lista: [m] });
    });

    porModalidade.forEach((g, gi) => {
      garantirEspaco(20 + 24 + 15);
      if (gi > 0) y -= 12;
      y = barraTitulo(page, y, g.mod.toUpperCase(), `${g.lista.length} membro(s)`, VERDE);
      y -= 6;
      y = cabecalhoTabela(page, y);
      g.lista.forEach((m, i) => {
        garantirEspaco(15);
        if (y === TOP_CONTENT) y = cabecalhoTabela(page, y);
        y = linhaAtleta(page, y, m, i % 2 === 1);
      });
    });
  } else {
    garantirEspaco(20 + 24 + 15);
    y = barraTitulo(page, y, "ATLETAS", `${atletas.length} atleta(s)`, VERDE);
    y -= 6;
    y = cabecalhoTabela(page, y);
    atletas.forEach((m, i) => {
      garantirEspaco(15);
      if (y === TOP_CONTENT) y = cabecalhoTabela(page, y); // recoloca o cabeçalho se quebrou página
      y = linhaAtleta(page, y, m, i % 2 === 1);
    });
  }

  // A equipe técnica não entra mais no relatório da modalidade: ela tem
  // documento próprio, escolhido no mesmo seletor. Estavam na mesma
  // tabela, e a frequência do técnico acabava lida como se fosse a de um
  // atleta, inclusive na média.

  const bytes = await doc.save();
  const doNucleo = nucleoParaArquivo(nucleoNome, nucleo);
  const nomeArquivo = ehEquipe
    ? `Relatorio_Equipe_Tecnica_${doNucleo}_${MESES[mes]}_${ano}.pdf`
    : `Relatorio_${modalidadeNorm}_${doNucleo}_${MESES[mes]}_${ano}.pdf`;
  baixar(bytes, nomeArquivo);
  return nomeArquivo;
}

function baixar(bytes, nome) {
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = nome;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ============================================================
// RELATÓRIO DE COMPETIÇÕES E MEDALHAS
//
// Mesmo padrão dos outros dois: barra azul na lateral, logomarca, cabeçalho
// e rodapé idênticos. Sem capa, como o relatório por modalidade.
//
// Todo número aqui é derivado do que foi lançado. Não existe campo de total
// digitado à mão, que é justamente o que faz a planilha atual divergir
// entre as abas COMPETIÇÕES e MEDALHAS.
// ============================================================
// O aproveitamento impresso.
//
// Traço, e não "0%", quando não houve prova: quem não competiu não teve
// aproveitamento ruim, não teve aproveitamento. Num anexo de prestação de
// contas a diferença entre as duas coisas importa.
function emPorcento(v) {
  return v === null || v === undefined || !Number.isFinite(v) ? "—" : `${v}%`;
}

export async function gerarPDFCompeticoes({ ranking, nucleo, nucleoNome, de, ate }) {
  const rotuloNucleo = nucleoNome || nucleo;
  const r = ranking || {};
  const linhas = r.linhas || [];
  const competicoes = r.competicoes || [];
  const totais = r.totais || { ouro: 0, prata: 0, bronze: 0, outras: 0, total: 0 };

  const doc = await PDFDocument.create();
  const fontR = await doc.embedFont(StandardFonts.Helvetica);
  const fontB = await doc.embedFont(StandardFonts.HelveticaBold);

  let logoImg = null;
  try {
    logoImg = await doc.embedPng(await fetchBytes(`${process.env.PUBLIC_URL || ""}/relatorio/logo_revelar.png`));
  } catch (e) { /* segue sem logo */ }

  const modalidade = normalizarModalidade(r.modalidade || "");
  const dia = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "");
  const periodo = de || ate
    ? `${de ? dia(de) : "início"} a ${ate ? dia(ate) : "hoje"}`
    : "todo o período registrado";

  const hoje = new Date();
  const emitidoEm = `${String(hoje.getDate()).padStart(2, "0")}/${String(hoje.getMonth() + 1).padStart(2, "0")}/${hoje.getFullYear()}`;

  let page = null, y = 0;

  function desenharCabecalho(pg) {
    pg.drawRectangle({ x: 0, y: 0, width: 6, height: A4H, color: AZUL });
    let logoLeftX = A4W - MX;
    if (logoImg) {
      const w = 34, h = w * logoImg.height / logoImg.width;
      logoLeftX = A4W - MX - w;
      pg.drawImage(logoImg, { x: logoLeftX, y: A4H - h - 14, width: w, height: h });
    }
    pg.drawText("REVELAR TALENTOS", { x: MX, y: A4H - 34, size: 10, font: fontB, color: AZUL });
    pg.drawText(
      txtSeguro(`Núcleo ${rotuloNucleo} · ${periodo}`),
      { x: MX, y: A4H - 46, size: 8, font: fontR, color: CINZA }
    );
    pg.drawLine({
      start: { x: MX, y: A4H - 56 },
      end: { x: logoLeftX - 12, y: A4H - 56 },
      thickness: 0.5, color: BORDA,
    });
  }

  function desenharRodape(pg) {
    pg.drawText(txtSeguro(IDENTIFICACAO.join(" · ")), { x: MX, y: 22, size: 7, font: fontR, color: CINZA });
    pg.drawText(txtSeguro(`Emitido em ${emitidoEm}`), {
      x: A4W - MX - fontR.widthOfTextAtSize(`Emitido em ${emitidoEm}`, 7),
      y: 22, size: 7, font: fontR, color: CINZA,
    });
  }

  function novaPagina() {
    page = doc.addPage([A4W, A4H]);
    desenharCabecalho(page);
    desenharRodape(page);
    y = TOP_CONTENT;
  }

  function garantirEspaco(altura) {
    if (y - altura < BOTTOM_LIMIT) novaPagina();
  }

  // Corta o texto na largura da coluna em vez de deixá-lo invadir a
  // seguinte. Nome comprido acontece, e sem isso a tabela vira sopa.
  function cortar(texto, fonte, tamanho, largura) {
    let t = txtSeguro(texto || "");
    while (t.length > 1 && fonte.widthOfTextAtSize(t, tamanho) > largura) t = t.slice(0, -1);
    return t;
  }

  novaPagina();

  page.drawText(txtSeguro(modalidade.toUpperCase()), { x: MX, y, size: 20, font: fontB, color: AZUL });
  y -= 18;
  page.drawText(txtSeguro("Quadro de medalhas e competições disputadas"), { x: MX, y, size: 10, font: fontR, color: CINZA });
  y -= 26;

  // ── Números do período ──
  const cardW = (A4W - 2 * MX - 3 * 10) / 4, cardH = 58;
  const cards = [
    ["Competições", String(competicoes.length), AZUL],
    ["Medalhas", String(totais.total), AMARELO],
    ["Atletas premiados", String(linhas.filter((l) => l.totalMedalhas > 0).length), VERDE],
    ["Atletas que competiram", String(linhas.filter((l) => l.competicoes > 0).length), AZUL],
  ];
  cards.forEach(([rot, val, cor], i) => {
    const cx = MX + i * (cardW + 10);
    page.drawRectangle({ x: cx, y: y - cardH, width: cardW, height: cardH, color: CINZA_CL, borderColor: BORDA, borderWidth: 1 });
    const vw = fontB.widthOfTextAtSize(val, 20);
    page.drawText(val, { x: cx + (cardW - vw) / 2, y: y - 29, size: 20, font: fontB, color: cor });
    const rw = fontR.widthOfTextAtSize(txtSeguro(rot), 7.5);
    page.drawText(txtSeguro(rot), { x: cx + (cardW - rw) / 2, y: y - 46, size: 7.5, font: fontR, color: CINZA });
  });
  y -= cardH + 24;

  // Uma coluna por COLOCAÇÃO premiada, como na tela.
  //
  // Pódio e medalha são a mesma coisa, e o pódio é cadastro da
  // modalidade: três no tênis de mesa e no skate, cinco no BMX. Até
  // 15/10/2026 o anexo imprimia ouro, prata, bronze e uma coluna
  // "Outras" que juntava o 4º e o 5º do BMX, duas posições que ali são
  // pódio com a mesma dignidade do 3º.
  //
  // Lançamento antigo não tem a contagem posição a posição. Nesse caso o
  // anexo volta ao desenho antigo, que é o que aqueles dados sabem
  // dizer, em vez de imprimir zero onde houve medalha.
  const temPorPosicao = linhas.some((l) => l.porColocacao);
  const podio = Math.max(1, Number(r.tamanhoDoPodio) || 3);
  const METAIS = ["Ouro", "Prata", "Bronze"];
  const temOutras = !temPorPosicao && (totais.outras > 0 || podio > 3);

  const COLS_DE_PODIO = temPorPosicao
    ? Array.from({ length: podio }, (_, i) => ({
      rot: METAIS[i] || `${i + 1}º`, larg: 34, posicao: i + 1,
    }))
    : [
      { rot: "Ouro", larg: 34, chave: "ouro" },
      { rot: "Prata", larg: 34, chave: "prata" },
      { rot: "Bronze", larg: 36, chave: "bronze" },
      ...(temOutras ? [{ rot: "Outras", larg: 36, chave: "outras" }] : []),
    ];

  // O nome recebe o que sobra da largura útil, com um piso: com cinco
  // colunas de pódio o quadro fica apertado, e nome longo é cortado por
  // `cortar` em vez de invadir a coluna seguinte.
  const LARG_DAS_CONTAGENS = 40 + 40 + 48 + 46 + 40;
  const LARG_DO_PODIO = COLS_DE_PODIO.reduce((soma, c) => soma + c.larg, 0);
  const LARG_NOME = Math.max(
    118, (A4W - 2 * MX) - 6 - LARG_DO_PODIO - LARG_DAS_CONTAGENS,
  );
  const COLS = [
    { rot: "Atleta", x: MX + 6, larg: LARG_NOME, al: "esq" },
    ...COLS_DE_PODIO,
    // Pódio e medalha são a mesma coisa, então a coluna de pódios É o total
    // de medalhas do atleta.
    { rot: "Pódios", larg: 40 },
    // Provas e Competições são contagens diferentes, e as duas precisam
    // aparecer. Uma prova é uma CATEGORIA disputada; uma competição é um
    // evento. No tênis de mesa o mesmo atleta disputa a simples da idade,
    // o absoluto e as duplas no mesmo fim de semana: são cinco provas e
    // uma competição. Imprimir só uma das duas deixa o leitor do anexo
    // sem saber se "5" quer dizer cinco viagens ou cinco inscrições.
    { rot: "Provas", larg: 40 }, { rot: "Competições", larg: 48 },
    // O aproveitamento é pódios sobre provas, porque é em cada prova que
    // existe a chance de pódio.
    { rot: "Aprov.", larg: 46 }, { rot: "Melhor", larg: 40 },
  ];
  let acumulado = MX + 6 + LARG_NOME;
  COLS.forEach((c, i) => {
    if (i === 0) return;
    c.x = acumulado;
    acumulado += c.larg;
  });

  function cabecalhoQuadro() {
    page.drawRectangle({ x: MX, y: y - 17, width: A4W - 2 * MX, height: 17, color: BARRA_BG });
    COLS.forEach((c, i) => {
      const t = txtSeguro(c.rot);
      const x = i === 0 ? c.x : c.x + (c.larg - fontB.widthOfTextAtSize(t, 7.5)) / 2;
      page.drawText(t, { x, y: y - 12, size: 7.5, font: fontB, color: TEXTO });
    });
    return y - 17;
  }

  if (!linhas.length) {
    page.drawText(txtSeguro("Nenhum resultado lançado para esta modalidade no período."), {
      x: MX, y, size: 10, font: fontR, color: CINZA,
    });
    y -= 20;
  } else {
    garantirEspaco(17 + 15);
    y = cabecalhoQuadro();
    linhas.forEach((l, i) => {
      garantirEspaco(15);
      if (y === TOP_CONTENT) y = cabecalhoQuadro();
      if (i % 2 === 1) {
        page.drawRectangle({ x: MX, y: y - 15, width: A4W - 2 * MX, height: 15, color: CINZA_CL });
      }
      const m = l.medalhas || {};
      // Cada valor diz se vai em negrito, em vez de o código contar
      // posições a partir do fim da lista. A conta por índice quebrava
      // calada toda vez que uma coluna entrava ou saía do quadro.
      const valores = [
        ...COLS_DE_PODIO.map((c) => ({
          v: String(c.posicao ? (l.porColocacao || {})[c.posicao] || 0 : m[c.chave] || 0),
        })),
        { v: String(l.totalMedalhas || 0), forte: true },
        { v: String(l.participacoes || 0) },
        { v: String(l.competicoes || 0) },
        { v: emPorcento(l.aproveitamento) },
        { v: l.melhorColocacao ? `${l.melhorColocacao}º` : "—" },
      ];
      page.drawText(cortar(l.nome, fontR, 8, LARG_NOME - 8), {
        x: COLS[0].x, y: y - 11, size: 8, font: fontR, color: TEXTO,
      });
      valores.forEach(({ v, forte }, j) => {
        const c = COLS[j + 1];
        const fonte = forte ? fontB : fontR;
        page.drawText(v, {
          x: c.x + (c.larg - fonte.widthOfTextAtSize(v, 8)) / 2,
          y: y - 11, size: 8, font: fonte, color: TEXTO,
        });
      });
      y -= 15;
    });

    // Linha de totais. É a que a coordenação copia para o relatório do
    // programa, então sai do mesmo cálculo das linhas acima.
    page.drawRectangle({ x: MX, y: y - 17, width: A4W - 2 * MX, height: 17, color: BARRA_BG });
    page.drawText(txtSeguro("TOTAL DA MODALIDADE"), { x: COLS[0].x, y: y - 12, size: 8, font: fontB, color: AZUL });
    [
      ...COLS_DE_PODIO.map((c) => String(
        c.posicao ? (totais.porColocacao || {})[c.posicao] || 0 : totais[c.chave] || 0,
      )),
      String(totais.total),
      String(totais.participacoes || 0),
      // Competições da modalidade é o número de eventos, e não a soma dos
      // eventos de cada atleta: dez atletas na mesma prova disputaram uma
      // competição, não dez.
      String(competicoes.length),
      emPorcento(totais.aproveitamento), "",
    ].forEach((v, j) => {
      if (!v) return;
      const c = COLS[j + 1];
      page.drawText(v, {
        x: c.x + (c.larg - fontB.widthOfTextAtSize(v, 8)) / 2,
        y: y - 12, size: 8, font: fontB, color: AZUL,
      });
    });
    y -= 17;
  }

  // ── Competições disputadas ──
  if (competicoes.length) {
    garantirEspaco(16 + 17 + 15);
    y -= 20;
    page.drawRectangle({ x: MX, y: y - 17, width: A4W - 2 * MX, height: 17, color: BARRA_BG });
    page.drawText(txtSeguro("COMPETIÇÕES DISPUTADAS"), { x: MX + 6, y: y - 12, size: 8, font: fontB, color: AZUL });
    const contagem = `${competicoes.length} competição(ões)`;
    page.drawText(txtSeguro(contagem), {
      x: A4W - MX - 6 - fontR.widthOfTextAtSize(txtSeguro(contagem), 7.5),
      y: y - 12, size: 7.5, font: fontR, color: CINZA,
    });
    y -= 23;

    competicoes.forEach((c) => {
      garantirEspaco(15);
      const quando = c.dataFim && c.dataFim !== c.dataInicio
        ? `${dia(c.dataInicio)} a ${dia(c.dataFim)}`
        : dia(c.dataInicio);
      page.drawText(txtSeguro(quando), { x: MX + 6, y: y - 10, size: 8, font: fontR, color: CINZA });
      page.drawText(cortar(c.nome, fontR, 8, 250), { x: MX + 118, y: y - 10, size: 8, font: fontR, color: TEXTO });
      page.drawText(cortar(c.local || "", fontR, 7.5, 110), { x: MX + 376, y: y - 10, size: 7.5, font: fontR, color: CINZA });
      const lanc = `${c.lancados} result.`;
      page.drawText(lanc, {
        x: A4W - MX - 6 - fontR.widthOfTextAtSize(lanc, 7.5),
        y: y - 10, size: 7.5, font: fontR, color: CINZA,
      });
      y -= 15;
    });
  }

  // ── Legenda ──
  garantirEspaco(46);
  y -= 14;
  page.drawText(txtSeguro("Legenda"), { x: MX, y, size: 8, font: fontB, color: CINZA });
  y -= 11;
  [
    `Pódio desta modalidade: até o ${podio}º colocado. Quem sobe ao pódio recebe medalha, e por `
      + "isso há uma coluna para cada colocação premiada.",
    ...(temOutras
      ? ["Outras: medalhas da quarta posição em diante, dentro do pódio da modalidade."]
      : []),
    "Pódios: quantas vezes o atleta subiu ao pódio, que é o total de medalhas dele.",
    // Em tênis de mesa o mesmo evento rende várias provas ao mesmo atleta;
    // em BMX e skate, uma só. Dizer as duas coisas no mesmo anexo fazia o
    // leitor procurar no quadro uma diferença que aquela modalidade não tem.
    disputaVariasCategorias(modalidade)
      ? "Provas: categorias disputadas. O mesmo atleta pode disputar mais de uma categoria no "
        + "mesmo evento."
      : "Provas: categorias disputadas. Nesta modalidade o atleta disputa a categoria dele em "
        + "cada competição, então provas e competições andam juntas.",
    "Competições: eventos distintos de que o atleta participou. Quem não classificou conta, "
      + "porque esteve na prova; quem não disputou, não.",
    "Aproveitamento: pódios divididos por provas disputadas.",
  ].forEach((t) => {
    page.drawText(txtSeguro(t), { x: MX, y, size: 7, font: fontR, color: CINZA });
    y -= 10;
  });

  const bytes = await doc.save();
  const nomeArquivo = `Competicoes_${modalidade.replace(/\s+/g, "_")}_${nucleoParaArquivo(nucleoNome, nucleo)}.pdf`;
  baixar(bytes, nomeArquivo);
  return nomeArquivo;
}

// ============================================================
// RELATÓRIO COMPARATIVO
//
// Os dois gráficos da tela num arquivo só, no mesmo padrão dos outros:
// barra azul na lateral, logomarca, cabeçalho e rodapé idênticos.
//
// Os gráficos são desenhados aqui, e não capturados da tela. Imagem de
// gráfico da tela sai na resolução do monitor e borra na impressão, que é
// justamente para onde este documento vai.
// ============================================================

// Uma cor por modalidade, na mesma ordem da tela, para quem olha os dois
// não precisar reaprender a legenda.
const CORES_SERIE = [
  hexRgb("DC2626"), hexRgb("2563EB"), hexRgb("16A34A"), hexRgb("D97706"),
  hexRgb("7C3AED"), hexRgb("DB2777"), hexRgb("0891B2"), hexRgb("64748B"),
];

// Desenha um gráfico de linhas com eixo de 0 a 100%.
//
// `series` é [{ nome, cor, valores: [n|null] }]. Valor nulo é mês sem
// atleta na modalidade, e a linha se interrompe ali em vez de cair a zero:
// zero significaria "ninguém frequentou", que é coisa bem diferente.
function desenharGraficoDeLinhas(pg, { x, y, largura, altura, rotulos, series, fontR, fontB, mostrarValores = false }) {
  const padEsq = 26, padBaixo = 16, padTopo = 8;
  const gx = x + padEsq;
  const gy = y - altura + padBaixo;
  const gw = largura - padEsq;
  const gh = altura - padBaixo - padTopo;

  // Grade e eixo de percentual.
  [0, 25, 50, 75, 100].forEach((v) => {
    const ly = gy + (v / 100) * gh;
    pg.drawLine({
      start: { x: gx, y: ly }, end: { x: gx + gw, y: ly },
      thickness: v === 0 ? 0.8 : 0.4, color: v === 0 ? BORDA : hexRgb("F1F5F9"),
    });
    const t = `${v}%`;
    pg.drawText(t, {
      x: gx - 4 - fontR.widthOfTextAtSize(t, 6.5), y: ly - 2,
      size: 6.5, font: fontR, color: CINZA,
    });
  });

  // Posição horizontal de cada mês. Com um mês só, fica no meio.
  const n = rotulos.length;
  const passo = n > 1 ? gw / (n - 1) : 0;
  const px = (i) => (n > 1 ? gx + i * passo : gx + gw / 2);

  rotulos.forEach((r, i) => {
    const t = txtSeguro(r);
    const tw = fontR.widthOfTextAtSize(t, 6.5);
    pg.drawText(t, { x: px(i) - tw / 2, y: gy - 11, size: 6.5, font: fontR, color: CINZA });
  });

  series.forEach((s) => {
    const pontos = s.valores.map((v, i) =>
      v === null || v === undefined ? null : { x: px(i), y: gy + (Math.max(0, Math.min(100, v)) / 100) * gh, v }
    );
    pontos.forEach((p, i) => {
      const ant = i > 0 ? pontos[i - 1] : null;
      if (p && ant) {
        pg.drawLine({ start: { x: ant.x, y: ant.y }, end: { x: p.x, y: p.y }, thickness: 1.4, color: s.cor });
      }
      if (p) {
        pg.drawCircle({ x: p.x, y: p.y, size: 2.2, color: s.cor });
        if (mostrarValores) {
          const t = `${p.v}%`;
          const tw = fontB.widthOfTextAtSize(t, 6.5);
          pg.drawText(t, { x: p.x - tw / 2, y: p.y + 6, size: 6.5, font: fontB, color: s.cor });
        }
      }
    });
  });

  return gy - padBaixo;
}

export async function gerarPDFComparativo({
  porNucleo, modalidades, porModalidade, nucleo, nucleoNome, periodo,
}) {
  const rotuloNucleo = nucleoNome || nucleo;
  const meses = (porNucleo || []).map((p) => p.mes);

  const doc = await PDFDocument.create();
  const fontR = await doc.embedFont(StandardFonts.Helvetica);
  const fontB = await doc.embedFont(StandardFonts.HelveticaBold);

  let logoImg = null;
  try {
    logoImg = await doc.embedPng(await fetchBytes(`${process.env.PUBLIC_URL || ""}/relatorio/logo_revelar.png`));
  } catch (e) { /* segue sem logo */ }

  const hoje = new Date();
  const emitidoEm = `${String(hoje.getDate()).padStart(2, "0")}/${String(hoje.getMonth() + 1).padStart(2, "0")}/${hoje.getFullYear()}`;
  const intervalo = periodo || (meses.length ? `${meses[0]} a ${meses[meses.length - 1]}` : "");

  let page = null, y = 0;

  function desenharCabecalho(pg) {
    pg.drawRectangle({ x: 0, y: 0, width: 6, height: A4H, color: AZUL });
    let logoLeftX = A4W - MX;
    if (logoImg) {
      const w = 34, h = w * logoImg.height / logoImg.width;
      logoLeftX = A4W - MX - w;
      pg.drawImage(logoImg, { x: logoLeftX, y: A4H - h - 14, width: w, height: h });
    }
    pg.drawText("REVELAR TALENTOS", { x: MX, y: A4H - 34, size: 10, font: fontB, color: AZUL });
    pg.drawText(txtSeguro(`Núcleo ${rotuloNucleo} · ${intervalo}`),
      { x: MX, y: A4H - 46, size: 8, font: fontR, color: CINZA });
    pg.drawLine({
      start: { x: MX, y: A4H - 64 }, end: { x: logoLeftX - 12, y: A4H - 64 },
      thickness: 0.5, color: BORDA,
    });
  }

  function desenharRodape(pg) {
    pg.drawText(txtSeguro(IDENTIFICACAO.join(" · ")), { x: MX, y: 22, size: 7, font: fontR, color: CINZA });
    pg.drawText(txtSeguro(`Emitido em ${emitidoEm}`), {
      x: A4W - MX - fontR.widthOfTextAtSize(`Emitido em ${emitidoEm}`, 7),
      y: 22, size: 7, font: fontR, color: CINZA,
    });
  }

  function novaPagina() {
    page = doc.addPage([A4W, A4H]);
    desenharCabecalho(page);
    desenharRodape(page);
    y = TOP_CONTENT;
  }

  function garantirEspaco(altura) {
    if (y - altura < BOTTOM_LIMIT) novaPagina();
  }

  const { barraTitulo } = criarDesenhoTabela({ fontR, fontB });

  novaPagina();

  page.drawText(txtSeguro("COMPARATIVO DE FREQUÊNCIA"), { x: MX, y, size: 20, font: fontB, color: AZUL });
  y -= 24;

  if (!meses.length) {
    page.drawText(txtSeguro("Nenhum mês no intervalo escolhido."), { x: MX, y, size: 10, font: fontR, color: CINZA });
  } else {
    // ── Gráfico 1: a média do núcleo ──
    y = barraTitulo(page, y, "MÉDIA DO NÚCLEO", `${meses.length} mês(es)`, AZUL);
    y -= 10;
    const alturaG = 170;
    garantirEspaco(alturaG + 10);
    y = desenharGraficoDeLinhas(page, {
      x: MX, y, largura: A4W - 2 * MX, altura: alturaG, rotulos: meses,
      series: [{ nome: "Núcleo", cor: AZUL, valores: porNucleo.map((p) => p.media) }],
      fontR, fontB, mostrarValores: true,
    });
    y -= 16;
    page.drawText(
      txtSeguro("Média do percentual total dos atletas, mês a mês."),
      { x: MX, y, size: 7.5, font: fontR, color: CINZA }
    );
    y -= 22;

    // ── Gráfico 2: as modalidades ──
    const mods = modalidades || [];
    garantirEspaco(20 + alturaG + 40);
    y = barraTitulo(page, y, "POR MODALIDADE", `${mods.length} modalidade(s)`, VERDE);
    y -= 10;

    if (!mods.length) {
      page.drawText(txtSeguro("Nenhuma modalidade com atleta no intervalo."), { x: MX, y, size: 9, font: fontR, color: CINZA });
      y -= 16;
    } else {
      y = desenharGraficoDeLinhas(page, {
        x: MX, y, largura: A4W - 2 * MX, altura: alturaG, rotulos: meses,
        series: mods.map((m, i) => ({
          nome: m, cor: CORES_SERIE[i % CORES_SERIE.length],
          valores: (porModalidade || []).map((linha) => {
            const v = linha[m];
            return v === null || v === undefined ? null : v;
          }),
        })),
        fontR, fontB,
      });
      y -= 16;

      // Legenda.
      let lx = MX;
      mods.forEach((m, i) => {
        const cor = CORES_SERIE[i % CORES_SERIE.length];
        const t = txtSeguro(m);
        const tw = fontR.widthOfTextAtSize(t, 7.5);
        if (lx + 10 + tw > A4W - MX) { lx = MX; y -= 12; }
        page.drawCircle({ x: lx + 3, y: y + 2.5, size: 2.6, color: cor });
        page.drawText(t, { x: lx + 9, y, size: 7.5, font: fontR, color: TEXTO });
        lx += 9 + tw + 14;
      });
      y -= 20;

      // ── Os mesmos números em tabela ──
      // O gráfico mostra a tendência; a tabela permite conferir e copiar o
      // valor exato, que é o que a coordenação leva para a reunião.
      garantirEspaco(20 + 15 * (mods.length + 2));
      y = barraTitulo(page, y, "NÚMEROS DO PERÍODO", "", hexRgb("7C3AED"));
      y -= 4;

      const larguraNome = 150;
      const larguraMes = (A4W - 2 * MX - larguraNome) / meses.length;

      page.drawRectangle({ x: MX, y: y - 16, width: A4W - 2 * MX, height: 16, color: BARRA_BG });
      page.drawText(txtSeguro("Modalidade"), { x: MX + 6, y: y - 11, size: 7.5, font: fontB, color: TEXTO });
      meses.forEach((m, i) => {
        const t = txtSeguro(m);
        const tw = fontB.widthOfTextAtSize(t, 7.5);
        page.drawText(t, {
          x: MX + larguraNome + i * larguraMes + (larguraMes - tw) / 2,
          y: y - 11, size: 7.5, font: fontB, color: TEXTO,
        });
      });
      y -= 16;

      const linhas = [
        { nome: "Núcleo (média)", valores: porNucleo.map((p) => p.media), destaque: true },
        ...mods.map((m) => ({
          nome: m,
          valores: (porModalidade || []).map((linha) => linha[m]),
          destaque: false,
        })),
      ];

      linhas.forEach((l, i) => {
        garantirEspaco(15);
        if (i % 2 === 1) {
          page.drawRectangle({ x: MX, y: y - 15, width: A4W - 2 * MX, height: 15, color: CINZA_CL });
        }
        const fonte = l.destaque ? fontB : fontR;
        const cor = l.destaque ? AZUL : TEXTO;
        page.drawText(txtSeguro(l.nome), { x: MX + 6, y: y - 10.5, size: 8, font: fonte, color: cor });
        l.valores.forEach((v, j) => {
          const t = v === null || v === undefined ? "—" : `${v}%`;
          const tw = fonte.widthOfTextAtSize(t, 8);
          page.drawText(t, {
            x: MX + larguraNome + j * larguraMes + (larguraMes - tw) / 2,
            y: y - 10.5, size: 8, font: fonte, color: cor,
          });
        });
        y -= 15;
      });

      y -= 12;
    }
  }

  const bytes = await doc.save();
  const nomeArquivo = `Comparativo_${nucleoParaArquivo(nucleoNome, nucleo)}_${(meses[0] || "").replace("/", "-")}_a_${(meses[meses.length - 1] || "").replace("/", "-")}.pdf`;
  baixar(bytes, nomeArquivo);
  return nomeArquivo;
}

// ============================================================
// TERMO DE CONSENTIMENTO — VERIFICAÇÃO FACIAL
//
// Documento que o responsável assina fora do sistema e a coordenação anexa
// depois na aba Cadastro facial. Sai daqui, junto dos relatórios, porque
// tudo que se imprime no programa passou a ter a mesma barra azul, a mesma
// logomarca e o mesmo rodapé.
//
// Dois papéis, e eles não se misturam: a INSTITUIÇÃO do núcleo é a
// controladora, quem decide a finalidade e responde pelo tratamento; a
// Dynamon é a operadora, que executa a instrução institucional e não usa os
// dados para finalidade própria. É por isso que o canal de exercício de
// direitos é o da instituição, e não o nosso.
//
// Duas afirmações do texto não são estilo e não devem ser suavizadas: a
// ferramenta não invalida a presença sozinha, e a revogação do consentimento
// apaga a foto de referência. As duas sustentam o consentimento como livre
// na Lei Geral de Proteção de Dados, e o sistema foi construído para que
// sejam verdade. A caixa de autorização também sai SEMPRE em branco: termo
// impresso com a autorização já marcada é consentimento presumido.
// ============================================================

// Quebra o parágrafo na largura disponível. O pdf-lib escreve a linha que
// mandarem, inclusive para fora da página.
function quebrarTexto(texto, fonte, tamanho, largura) {
  const palavras = txtSeguro(texto).split(/\s+/).filter(Boolean);
  const linhas = [];
  let atual = "";
  palavras.forEach((p) => {
    const tentativa = atual ? `${atual} ${p}` : p;
    if (fonte.widthOfTextAtSize(tentativa, tamanho) > largura && atual) {
      linhas.push(atual);
      atual = p;
    } else {
      atual = tentativa;
    }
  });
  if (atual) linhas.push(atual);
  return linhas;
}

// Controladora: a instituição que decide a finalidade e responde pelo
// tratamento. É a ela que o responsável cobra, e é o canal dela que sai
// no termo.
//
// Era constante fixa com o IFCE aqui dentro. Com um núcleo só, ninguém
// via; com dois, o termo de um núcleo da Bahia nomearia a instituição do
// Ceará como responsável pelos dados do filho de outra pessoa, e o
// responsável cobraria no canal errado. Num documento de LGPD isso não é
// um rótulo errado, é o controlador errado.
//
// Agora vem do cadastro do núcleo. E quando não está cadastrada, o termo
// NÃO sai: ver a recusa em gerarPDFTermo.

// Operadora: executa o tratamento sob instrução da controladora, e não usa
// os dados para finalidade própria.
const OPERADORA = {
  nome: "Dynamon, Gestão e Desenvolvimento Esportivo",
  cnpj: "51.835.989/0001-94",
  contato: "contato@dynamon.com.br",
};

// Encarregado pela proteção de dados: o canal que recebe pedido e
// reclamação sobre o tratamento.
const ENCARREGADO = "contato@dynamon.com.br";

// As seções fixas do termo. É função, e não constante, porque a seção 6
// cita o canal de direitos, que é de cada núcleo.
const termoSecoes = (canalDeDireitos) => [
  {
    titulo: "2. Finalidade e funcionamento da verificação facial",
    paragrafos: [
      "O Programa utiliza verificação facial para confirmar se a pessoa que registra a presença é o atleta cadastrado. No momento do registro, a fotografia capturada pelo aplicativo é comparada automaticamente com a foto de referência previamente cadastrada.",
      "O sistema realiza uma comparação individual, exclusivamente para autenticar o registro de presença e prevenir registros realizados por terceiros. Quando o resultado for divergente ou inconclusivo, será gerado um alerta para análise humana pela coordenação.",
      "A ferramenta não invalida automaticamente a presença nem toma, sozinha, decisões sobre frequência, permanência ou benefícios do atleta. Eventual alteração do registro somente poderá ocorrer após análise da coordenação, com possibilidade de esclarecimento ou contestação pelo atleta ou responsável.",
      "As imagens e os dados biométricos não serão utilizados para publicidade, redes sociais, reconhecimento em vídeo, identificação de pessoas em imagens de terceiros ou qualquer finalidade incompatível com o controle de frequência.",
    ],
  },
  {
    titulo: "3. Quais dados são tratados",
    itens: [
      "Foto de referência: uma fotografia do rosto do atleta, feita pela coordenação.",
      "Foto do registro: a fotografia que o atleta tira ao registrar presença, já coletada hoje pelo programa.",
      "Medidas do rosto extraídas dessas imagens durante a comparação, usadas apenas naquele instante e não armazenadas.",
    ],
  },
  {
    titulo: "4. Como os dados são guardados e quem tem acesso",
    paragrafos: [
      "As imagens ficam em servidor com acesso restrito, protegidas por controle de acesso técnico. O acesso é limitado à coordenação do núcleo do atleta, para a finalidade descrita acima. O processamento da comparação ocorre em servidores localizados no Brasil. Todo acesso e alteração ficam registrados para auditoria.",
    ],
  },
  {
    titulo: "5. Por quanto tempo",
    itens: [
      "A foto de referência é guardada enquanto o atleta participar do programa e é apagada quando ele é desligado.",
      "As fotos dos registros diários são apagadas em lote, uma vez por mês, depois de completados três meses do mês do registro. Nenhuma imagem fica guardada por mais de quatro meses. A presença registrada permanece, sem a imagem.",
      "Este termo assinado é conservado como comprovação da autorização concedida.",
    ],
  },
  {
    titulo: "6. Direitos do atleta e do responsável",
    tituloMaior: "6. Direitos do atleta",
    paragrafos: [
      "O atleta ou responsável poderá solicitar confirmação e acesso aos dados, correção, informação sobre o compartilhamento, revisão de eventual decisão baseada na verificação facial e, quando aplicável, bloqueio, eliminação ou revogação do consentimento.",
      `Os pedidos poderão ser apresentados gratuitamente pelo canal: ${canalDeDireitos}`,
      "O consentimento poderá ser revogado a qualquer momento. A revogação não invalida os tratamentos realizados anteriormente, mas interrompe novas comparações faciais e provoca a eliminação da foto de referência, salvo quando sua conservação estiver autorizada pela legislação.",
      "Sem autorização, a presença continua sendo registrada normalmente, pela fotografia do registro e pela análise da coordenação, sem qualquer prejuízo ao atleta.",
    ],
  },
];

// `instituicao` e `canalDeDireitos` vêm do cadastro do núcleo, por
// SecaoCadastroFacial. Sem os dois não há termo: ver abaixo.
export async function gerarPDFTermo({ atleta, nucleo, nucleoNome, instituicao, canalDeDireitos }) {
  // Sem controladora cadastrada o termo não sai.
  //
  // A alternativa seria imprimir um recuo, e era o que havia: saía o
  // IFCE. Um termo que nomeia o controlador errado é pior que termo
  // nenhum, porque é assinado, arquivado e aparenta estar correto. Deixar
  // a linha em branco também não serve: o responsável assina sem saber a
  // quem está consentindo.
  //
  // Recusar manda a coordenação preencher uma vez, no cadastro do núcleo,
  // e o termo passa a sair certo para todo mundo daquele núcleo.
  const controladora = String(instituicao || "").trim();
  const canal = String(canalDeDireitos || "").trim();
  if (!controladora || !canal) {
    throw new Error(
      "Antes de emitir o termo, cadastre a instituição responsável e o canal para " +
      "exercício de direitos em Núcleos, na edição deste núcleo. Esses dois dados " +
      "saem impressos no termo como controlador dos dados, e não podem sair errados."
    );
  }

  const rotuloNucleo = nucleoNome || nucleo || "";
  const a = atleta || {};

  // Quem consente.
  //
  // Maior de 18 anos responde por si: não há representante legal a
  // identificar nem a assinar, e pedir CPF de responsável a um adulto é
  // coletar dado de terceiro sem finalidade.
  //
  // Sem data de nascimento cadastrada — e o termo em branco é sempre esse
  // caso —, sai a versão do menor. O programa é de criança e adolescente,
  // e presumir maioridade pularia o consentimento do responsável; o
  // contrário só pede uma linha a mais de quem podia assinar sozinho.
  const maiorDeIdade = ehMaiorDeIdade(a.dataNascimento);

  const doc = await PDFDocument.create();
  const fontR = await doc.embedFont(StandardFonts.Helvetica);
  const fontB = await doc.embedFont(StandardFonts.HelveticaBold);

  let logoImg = null;
  try {
    logoImg = await doc.embedPng(await fetchBytes(`${process.env.PUBLIC_URL || ""}/relatorio/logo_revelar.png`));
  } catch (e) { /* segue sem logo */ }

  const hoje = new Date();
  const emitidoEm = `${String(hoje.getDate()).padStart(2, "0")}/${String(hoje.getMonth() + 1).padStart(2, "0")}/${hoje.getFullYear()}`;
  const LARGURA = A4W - 2 * MX;

  let page = null, y = 0;

  function desenharCabecalho(pg) {
    pg.drawRectangle({ x: 0, y: 0, width: 6, height: A4H, color: AZUL });
    let logoLeftX = A4W - MX;
    if (logoImg) {
      const w = 34, h = w * logoImg.height / logoImg.width;
      logoLeftX = A4W - MX - w;
      pg.drawImage(logoImg, { x: logoLeftX, y: A4H - h - 14, width: w, height: h });
    }
    pg.drawText("REVELAR TALENTOS", { x: MX, y: A4H - 34, size: 10, font: fontB, color: AZUL });
    // O núcleo aparece em destaque: o termo circula em papel, entre
    // famílias e entre núcleos, e é ele que diz de onde o documento veio.
    if (rotuloNucleo) {
      const rotulo = "Núcleo ";
      pg.drawText(rotulo, { x: MX, y: A4H - 47, size: 8, font: fontR, color: CINZA });
      pg.drawText(txtSeguro(rotuloNucleo), {
        x: MX + fontR.widthOfTextAtSize(rotulo, 8),
        y: A4H - 47, size: 9.5, font: fontB, color: AZUL,
      });
    } else {
      pg.drawText("Programa Revelar Talentos", { x: MX, y: A4H - 46, size: 8, font: fontR, color: CINZA });
    }
    pg.drawLine({
      start: { x: MX, y: A4H - 64 }, end: { x: logoLeftX - 12, y: A4H - 64 },
      thickness: 0.5, color: BORDA,
    });
  }

  function desenharRodape(pg) {
    pg.drawText(txtSeguro(IDENTIFICACAO.join(" · ")), { x: MX, y: 22, size: 7, font: fontR, color: CINZA });
    pg.drawText(txtSeguro(`Emitido em ${emitidoEm}`), {
      x: A4W - MX - fontR.widthOfTextAtSize(`Emitido em ${emitidoEm}`, 7),
      y: 22, size: 7, font: fontR, color: CINZA,
    });
  }

  function novaPagina() {
    page = doc.addPage([A4W, A4H]);
    desenharCabecalho(page);
    desenharRodape(page);
    y = TOP_CONTENT;
  }

  function garantirEspaco(altura) {
    if (y - altura < BOTTOM_LIMIT) novaPagina();
  }

  function paragrafo(texto, { tamanho = 8.5, fonte = fontR, cor = TEXTO, recuo = 0, espaco = 5 } = {}) {
    const linhas = quebrarTexto(texto, fonte, tamanho, LARGURA - recuo);
    linhas.forEach((ln) => {
      garantirEspaco(tamanho + 4);
      page.drawText(ln, { x: MX + recuo, y, size: tamanho, font: fonte, color: cor });
      y -= tamanho + 3.5;
    });
    y -= espaco;
  }

  // Campo de preenchimento, com altura e espaçamento iguais em todos.
  //
  // O rótulo fica ACIMA da linha, não na frente dela. Assim a linha inteira
  // sobra para o conteúdo, e um nome comprido não se espreme contra o
  // rótulo nem invade o campo do lado. Para quem preenche à mão, a altura
  // constante é o que faz o bloco parecer um formulário e não uma lista.
  const ALTURA_DO_CAMPO = 30;

  function campo(rotulo, valor, { largura = LARGURA, x = MX } = {}) {
    page.drawText(txtSeguro(rotulo), { x, y, size: 7, font: fontR, color: CINZA });

    if (valor) {
      // O valor tem que caber na largura do campo. Diminui até 6,5 pt e,
      // se ainda assim não couber, corta: melhor um nome abreviado do que
      // um texto atravessando a página por cima do campo vizinho.
      const disponivel = largura - 4;
      let tamanho = 8.5;
      let texto = txtSeguro(valor);
      while (tamanho > 6.5 && fontB.widthOfTextAtSize(texto, tamanho) > disponivel) {
        tamanho -= 0.5;
      }
      while (texto.length > 4 && fontB.widthOfTextAtSize(texto, tamanho) > disponivel) {
        texto = texto.slice(0, -2) + "…";
      }
      page.drawText(texto, { x: x + 2, y: y - 13, size: tamanho, font: fontB, color: TEXTO });
    }

    page.drawLine({
      start: { x, y: y - 17 }, end: { x: x + largura, y: y - 17 },
      thickness: 0.6, color: BORDA_FORTE_PDF,
    });
    y -= ALTURA_DO_CAMPO;
  }

  function faixa(texto) {
    garantirEspaco(30);
    page.drawRectangle({ x: MX, y: y - 16, width: LARGURA, height: 16, color: BARRA_BG });
    page.drawText(txtSeguro(texto), { x: MX + 6, y: y - 11, size: 8, font: fontB, color: AZUL });
    y -= 26;
  }

  novaPagina();

  page.drawText(txtSeguro("TERMO DE CONSENTIMENTO"), { x: MX, y, size: 17, font: fontB, color: AZUL });
  y -= 15;
  page.drawText(txtSeguro(maiorDeIdade
    ? "Verificação facial para registro de presença"
    : "Verificação facial para registro de presença, de criança ou adolescente"),
    { x: MX, y, size: 9.5, font: fontR, color: TEXTO });
  y -= 12;
  page.drawText(txtSeguro("Lei nº 13.709/2018, Lei Geral de Proteção de Dados Pessoais"),
    { x: MX, y, size: 8, font: fontR, color: CINZA });
  y -= 22;

  // ── Quem é quem no tratamento ──
  // A distinção entre controlador e operador não é formalidade: ela diz a
  // quem o responsável cobra, e a resposta é a instituição, não a empresa
  // que mantém o sistema.
  faixa("Responsáveis pelo tratamento dos dados");

  const meiaLargura = (LARGURA - 14) / 2;

  campo("Controlador, instituição responsável pelo programa", controladora);

  const yTratamento = y;
  campo("Canal para o exercício de direitos", canal, { largura: meiaLargura });
  y = yTratamento;
  campo("Encarregado pela proteção de dados", ENCARREGADO, { largura: meiaLargura, x: MX + meiaLargura + 14 });

  const yOperador = y;
  campo("Operador, que executa sob instrução do controlador", OPERADORA.nome, { largura: meiaLargura });
  y = yOperador;
  campo("CNPJ do operador", OPERADORA.cnpj, { largura: meiaLargura, x: MX + meiaLargura + 14 });

  y -= 4;
  faixa("1. Identificação");

  const meia = meiaLargura;

  campo("Nome do atleta", a.nome || "");

  const yLinha2 = y;
  campo("Data de nascimento", a.dataNascimento ? fmtDataBR(a.dataNascimento) : "", { largura: meia });
  y = yLinha2;
  campo("Modalidade e núcleo", [a.modalidade, rotuloNucleo].filter(Boolean).join(" · "), { largura: meia, x: MX + meia + 14 });

  // Os dados do responsável saem do cadastro do atleta. Antes eram quatro
  // linhas copiadas à mão, atleta por atleta, e o que fosse escrito ali
  // nunca voltava para o sistema.
  //
  // No termo do maior de idade eles não existem: quem consente é o próprio
  // atleta, e o que fica é o telefone dele.
  if (maiorDeIdade) {
    campo("Telefone de contato", a.responsavelTelefone || "");
  } else {
    campo("Nome do responsável legal", a.responsavelNome || "");

    const yLinha3 = y;
    campo("CPF do responsável", a.responsavelCpf || "", { largura: meia });
    y = yLinha3;
    campo("Parentesco ou vínculo", a.responsavelParentesco || "", { largura: meia, x: MX + meia + 14 });

    campo("Telefone de contato", a.responsavelTelefone || "");
  }

  // ── Seções de texto ──
  termoSecoes(canal).forEach((sec) => {
    garantirEspaco(34);
    y -= 2;
    page.drawText(txtSeguro((maiorDeIdade && sec.tituloMaior) || sec.titulo), { x: MX, y, size: 9, font: fontB, color: AZUL });
    y -= 13;
    (sec.paragrafos || []).forEach((p) => paragrafo(p));
    (sec.itens || []).forEach((it) => {
      garantirEspaco(18);
      page.drawCircle({ x: MX + 3, y: y + 3, size: 1.6, color: CINZA });
      paragrafo(it, { recuo: 12, espaco: 2 });
    });
    y -= 4;
  });

  // ── Manifestação ──
  // As caixas saem vazias, sempre. Autorização impressa já marcada é
  // consentimento presumido, que é justamente o que a lei não admite.
  //
  // Não existe caixa de recusa: a autorização é que precisa ser dada, e a
  // ausência da marca já é a ausência dela. Uma segunda caixa criaria a
  // dúvida de um termo devolvido em branco valer como recusa declarada,
  // quando pode ser apenas um papel que ninguém leu.
  garantirEspaco(170);
  y -= 4;
  page.drawText(txtSeguro("7. Manifestação"), { x: MX, y, size: 9, font: fontB, color: AZUL });
  y -= 6;

  (maiorDeIdade
    ? [
      "Declaro que li e estou ciente do Aviso de Privacidade sobre a verificação facial.",
      "AUTORIZO, de forma específica e destacada, o tratamento dos meus dados biométricos para verificação dos meus registros de presença, nas condições descritas neste termo.",
    ]
    : [
      "Declaro que li e estou ciente do Aviso de Privacidade sobre a verificação facial.",
      "AUTORIZO, de forma específica e destacada, o tratamento dos dados biométricos do atleta para verificação dos registros de presença, nas condições descritas neste termo.",
    ]
  ).forEach((opcao) => {
    garantirEspaco(34);
    const topo = y;
    page.drawRectangle({ x: MX, y: topo - 9, width: 11, height: 11, borderColor: BORDA_FORTE_PDF, borderWidth: 1 });
    const linhas = quebrarTexto(opcao, fontR, 8.5, LARGURA - 20);
    linhas.forEach((ln, i) => {
      page.drawText(ln, { x: MX + 20, y: topo - i * 12, size: 8.5, font: fontR, color: TEXTO });
    });
    y = topo - linhas.length * 12 - 10;
  });

  garantirEspaco(130);
  y -= 6;
  const yAss = y;
  campo("Local", "", { largura: meia });
  y = yAss;
  campo("Data", "", { largura: meia, x: MX + meia + 14 });

  y -= 18;
  (maiorDeIdade
    ? [
      "Assinatura do atleta",
      "Assinatura e cargo de quem recebeu, pelo Programa Revelar Talentos",
    ]
    : [
      "Assinatura do responsável legal",
      "Assinatura do atleta (a partir de 12 anos, quando possível)",
      "Assinatura e cargo de quem recebeu, pelo Programa Revelar Talentos",
    ]
  ).forEach((rot) => {
    garantirEspaco(36);
    page.drawLine({ start: { x: MX, y }, end: { x: MX + LARGURA * 0.72, y }, thickness: 0.6, color: BORDA_FORTE_PDF });
    y -= 10;
    page.drawText(txtSeguro(rot), { x: MX, y, size: 7.5, font: fontR, color: CINZA });
    y -= 26;
  });

  garantirEspaco(20);
  page.drawText(txtSeguro(maiorDeIdade
    ? "Documento emitido em duas vias: uma para o atleta, outra para o arquivo do programa."
    : "Documento emitido em duas vias: uma para o responsável, outra para o arquivo do programa."),
    { x: MX, y, size: 7, font: fontR, color: CINZA });

  const bytes = await doc.save();
  const nomeLimpo = (a.nome || "em branco").normalize("NFD").replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "_");
  const nomeArquivo = `Termo_Facial_${nomeLimpo}.pdf`;
  baixar(bytes, nomeArquivo);
  return nomeArquivo;
}

// ============================================================
// PLANO TRIMESTRAL
//
// O plano é discutido em reunião e arquivado, e até aqui só existia dentro
// da tela. Sai no mesmo padrão dos outros documentos: barra azul na
// lateral, logomarca, cabeçalho com o escopo e rodapé idênticos.
//
// Sai em qualquer estado, inclusive rascunho, com o estado impresso no
// alto. Documento sem essa marca circula como se fosse definitivo, e o
// rascunho de um técnico viraria compromisso do programa numa reunião.
// ============================================================
export async function gerarPDFPlano({ plano, trimestre, nucleo, nucleoNome }) {
  const p = plano || {};
  const c = p.conteudo || {};
  const t = trimestre || {};
  const rotuloNucleo = nucleoNome || nucleo || "";

  const doc = await PDFDocument.create();
  const fontR = await doc.embedFont(StandardFonts.Helvetica);
  const fontB = await doc.embedFont(StandardFonts.HelveticaBold);

  let logoImg = null;
  try {
    logoImg = await doc.embedPng(await fetchBytes(`${process.env.PUBLIC_URL || ""}/relatorio/logo_revelar.png`));
  } catch (e) { /* segue sem logo */ }

  const hoje = new Date();
  const emitidoEm = `${String(hoje.getDate()).padStart(2, "0")}/${String(hoje.getMonth() + 1).padStart(2, "0")}/${hoje.getFullYear()}`;
  const LARGURA = A4W - 2 * MX;

  // Declarado aqui, antes da primeira página: o cabeçalho usa, e o rótulo
  // em caixa alta do título só existe depois.
  const tipoDoPlano = p.tipo === "fisico" ? "Plano de preparação física" : "Plano técnico";

  const ESTADOS = {
    rascunho: "RASCUNHO, ainda não enviado",
    enviado: "ENVIADO, em análise pela coordenação",
    aprovado: "APROVADO pela coordenação",
    devolvido: "DEVOLVIDO para ajuste",
  };

  let page = null, y = 0;

  function desenharCabecalho(pg) {
    pg.drawRectangle({ x: 0, y: 0, width: 6, height: A4H, color: AZUL });
    let logoLeftX = A4W - MX;
    if (logoImg) {
      const w = 34, h = w * logoImg.height / logoImg.width;
      logoLeftX = A4W - MX - w;
      pg.drawImage(logoImg, { x: logoLeftX, y: A4H - h - 14, width: w, height: h });
    }
    pg.drawText("REVELAR TALENTOS", { x: MX, y: A4H - 34, size: 10, font: fontB, color: AZUL });
    pg.drawText(
      txtSeguro(`${tipoDoPlano}${p.modalidade ? ` · ${p.modalidade}` : ""}`),
      { x: MX, y: A4H - 46, size: 8, font: fontB, color: AZUL }
    );
    pg.drawText(
      txtSeguro(`Núcleo ${rotuloNucleo}${t.nome ? ` · ${t.nome}` : ""}`),
      { x: MX, y: A4H - 56, size: 8, font: fontR, color: CINZA }
    );
    pg.drawLine({
      start: { x: MX, y: A4H - 64 }, end: { x: logoLeftX - 12, y: A4H - 64 },
      thickness: 0.5, color: BORDA,
    });
  }

  function desenharRodape(pg) {
    pg.drawText(txtSeguro(IDENTIFICACAO.join(" · ")), { x: MX, y: 22, size: 7, font: fontR, color: CINZA });
    pg.drawText(txtSeguro(`Emitido em ${emitidoEm}`), {
      x: A4W - MX - fontR.widthOfTextAtSize(`Emitido em ${emitidoEm}`, 7),
      y: 22, size: 7, font: fontR, color: CINZA,
    });
  }

  function novaPagina() {
    page = doc.addPage([A4W, A4H]);
    desenharCabecalho(page);
    desenharRodape(page);
    // Mais baixo que os outros: o cabeçalho ganhou a linha do tipo do plano.
    y = A4H - 82;
  }

  function garantirEspaco(altura) {
    if (y - altura < BOTTOM_LIMIT) novaPagina();
  }

  function paragrafo(texto, { tamanho = 9, fonte = fontR, cor = TEXTO, recuo = 0, espaco = 6 } = {}) {
    const limpo = String(texto === undefined || texto === null ? "" : texto).trim();
    if (!limpo) return;
    // Quebra de linha digitada pela pessoa é intenção, e vira parágrafo.
    limpo.split(/\r?\n/).forEach((trecho) => {
      if (!trecho.trim()) { y -= 4; return; }
      quebrarTexto(trecho, fonte, tamanho, LARGURA - recuo).forEach((ln) => {
        garantirEspaco(tamanho + 5);
        page.drawText(ln, { x: MX + recuo, y, size: tamanho, font: fonte, color: cor });
        y -= tamanho + 4;
      });
    });
    y -= espaco;
  }

  function secao(titulo) {
    garantirEspaco(34);
    y -= 4;
    page.drawRectangle({ x: MX, y: y - 17, width: LARGURA, height: 17, color: BARRA_BG });
    page.drawText(txtSeguro(titulo), { x: MX + 7, y: y - 12, size: 8.5, font: fontB, color: AZUL });
    y -= 27;
  }

  function vazio(texto) {
    garantirEspaco(16);
    page.drawText(txtSeguro(texto), { x: MX, y, size: 8.5, font: fontR, color: CINZA });
    y -= 16;
  }

  novaPagina();

  const rotuloTipo = p.tipo === "fisico" ? "PLANO DE PREPARAÇÃO FÍSICA" : "PLANO TÉCNICO";
  page.drawText(txtSeguro(rotuloTipo), { x: MX, y, size: 18, font: fontB, color: AZUL });
  y -= 16;
  page.drawText(
    txtSeguro([p.modalidade, t.nome].filter(Boolean).join(" · ")),
    { x: MX, y, size: 10, font: fontR, color: TEXTO }
  );
  y -= 13;
  page.drawText(
    txtSeguro(`${fmtDataBR(t.dataInicio)} a ${fmtDataBR(t.dataFim)}${t.prazoEnvio ? ` · entrega até ${fmtDataBR(t.prazoEnvio)}` : ""}`),
    { x: MX, y, size: 8, font: fontR, color: CINZA }
  );
  y -= 18;

  // O estado, em destaque. Rascunho impresso sem marca vira compromisso.
  const estadoTexto = ESTADOS[p.status] || ESTADOS.rascunho;
  page.drawRectangle({ x: MX, y: y - 16, width: LARGURA, height: 16, color: CINZA_CL });
  page.drawText(txtSeguro(estadoTexto), { x: MX + 7, y: y - 11, size: 8, font: fontB, color: TEXTO });
  y -= 26;

  if (p.status === "devolvido" && p.observacoesCoordenacao) {
    secao("OBSERVAÇÕES DA COORDENAÇÃO");
    paragrafo(p.observacoesCoordenacao);
  }

  secao("OBJETIVO GERAL DO TRIMESTRE");
  if (String(c.objetivoGeral || "").trim()) paragrafo(c.objetivoGeral);
  else vazio("Não preenchido.");

  secao("PLANEJAMENTO MÊS A MÊS");
  const meses = Array.isArray(c.meses) ? c.meses : [];
  const nomesPadrao = ["Mês 1", "Mês 2", "Mês 3"];
  const DIAS = [
    ["seg", "Segunda"], ["ter", "Terça"], ["qua", "Quarta"],
    ["qui", "Quinta"], ["sex", "Sexta"],
  ];
  let algumMes = false;
  meses.forEach((m, i) => {
    const foco = String((m && m.foco) || "").trim();
    const conteudos = String((m && m.conteudos) || "").trim();
    // Um mês pode ter até cinco semanas. Plano antigo tem `microciclo`,
    // um objeto só, e ele é a primeira semana: converter na leitura evita
    // migrar o banco para imprimir um PDF.
    const semanas = (Array.isArray(m && m.microciclos) && m.microciclos.length
      ? m.microciclos
      : [(m && m.microciclo) || {}])
      .map((semana) => DIAS.filter(([ch]) => String((semana || {})[ch] || "").trim())
        .map(([ch, rotulo]) => [rotulo, String(semana[ch])]))
      .filter((dias) => dias.length);

    if (!foco && !conteudos && !semanas.length) return;
    algumMes = true;
    garantirEspaco(30);
    page.drawText(txtSeguro((m && m.rotulo) || nomesPadrao[i]), { x: MX, y, size: 9, font: fontB, color: AZUL });
    y -= 13;
    if (foco) paragrafo(foco, { fonte: fontB, espaco: 3 });
    if (conteudos) paragrafo(conteudos, { recuo: 0 });

    // Microciclo: o dia à esquerda, o que se faz nele à direita. Em lista
    // corrida, a semana some no meio do texto do plano.
    semanas.forEach((dias, sem) => {
      garantirEspaco(20);
      const titulo = semanas.length > 1 ? `Microciclo · semana ${sem + 1}` : "Microciclo da semana";
      page.drawText(txtSeguro(titulo), { x: MX, y, size: 8, font: fontB, color: CINZA });
      y -= 12;
      dias.forEach(([rotulo, texto]) => {
        garantirEspaco(16);
        page.drawText(txtSeguro(rotulo), { x: MX + 6, y, size: 8.5, font: fontB, color: TEXTO });
        paragrafo(texto, { tamanho: 8.5, recuo: 62, espaco: 2 });
      });
      y -= 4;
    });
  });
  if (!algumMes) vazio("Nenhum mês preenchido.");

  if (String(c.volumeSemanal || "").trim()) {
    secao("VOLUME SEMANAL PREVISTO");
    paragrafo(c.volumeSemanal);
  }

  secao("COMPETIÇÕES PREVISTAS");
  const competicoes = Array.isArray(c.competicoes) ? c.competicoes : [];
  if (!competicoes.length) {
    vazio("Nenhuma competição prevista no período.");
  } else {
    competicoes.forEach((comp) => {
      garantirEspaco(30);
      const quando = comp.dataFim && comp.dataFim !== comp.dataInicio
        ? `${fmtDataBR(comp.dataInicio)} a ${fmtDataBR(comp.dataFim)}`
        : fmtDataBR(comp.dataInicio);
      page.drawText(txtSeguro(quando), { x: MX, y, size: 8.5, font: fontR, color: CINZA });
      page.drawText(txtSeguro(comp.nome || "(sem nome)"), { x: MX + 110, y, size: 9, font: fontB, color: TEXTO });
      y -= 12;
      const detalhe = [comp.local, (comp.categorias || []).join(", ")].filter(Boolean).join(" · ");
      if (detalhe) {
        page.drawText(txtSeguro(detalhe), { x: MX + 110, y, size: 8, font: fontR, color: CINZA });
        y -= 12;
      }
      y -= 4;
    });
  }

  secao(p.tipo === "fisico" ? "AVALIAÇÃO E TESTES FÍSICOS" : "AVALIAÇÃO TÉCNICA");
  if (t.avaliacaoInicio) {
    paragrafo(`Janela definida pela coordenação: ${fmtDataBR(t.avaliacaoInicio)} a ${fmtDataBR(t.avaliacaoFim)}.`, { fonte: fontB, espaco: 3 });
  }
  if (String(c.avaliacaoConteudo || "").trim()) paragrafo(c.avaliacaoConteudo);
  else vazio("O que será avaliado ainda não foi preenchido.");

  const objetivos = (Array.isArray(c.objetivosPorAtleta) ? c.objetivosPorAtleta : [])
    .filter((o) => String((o && o.objetivo) || "").trim());
  if (objetivos.length) {
    secao("OBJETIVOS DE ATLETAS ESPECÍFICOS");
    objetivos.forEach((o) => {
      garantirEspaco(26);
      page.drawText(txtSeguro(o.atletaNome || "(atleta sem nome)"), { x: MX, y, size: 9, font: fontB, color: TEXTO });
      y -= 12;
      paragrafo(o.objetivo, { recuo: 10, espaco: 4 });
    });
  }

  if (String(c.observacoes || "").trim()) {
    secao("OBSERVAÇÕES");
    paragrafo(c.observacoes);
  }

  // Assinaturas. O plano é combinado entre quem executa e quem coordena, e
  // o papel guarda isso mesmo quando o sistema já registrou a aprovação.
  garantirEspaco(90);
  y -= 16;
  const meia = (LARGURA - 24) / 2;
  [
    [`Responsável pelo plano${p.atualizadoPorNome ? `: ${p.atualizadoPorNome}` : ""}`, MX],
    ["Coordenação do núcleo", MX + meia + 24],
  ].forEach(([rotulo, x]) => {
    page.drawLine({ start: { x, y }, end: { x: x + meia, y }, thickness: 0.6, color: BORDA_FORTE_PDF });
    page.drawText(txtSeguro(rotulo), { x, y: y - 11, size: 7.5, font: fontR, color: CINZA });
  });

  const bytes = await doc.save();
  const limpo = (s) => String(s || "").normalize("NFD").replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "_");
  const nomeArquivo = `Plano_${p.tipo === "fisico" ? "Fisico" : "Tecnico"}_${limpo(p.modalidade)}_${limpo(t.nome)}.pdf`;
  baixar(bytes, nomeArquivo);
  return nomeArquivo;
}

// ── Planilha de treino da preparação física ──────────────────
//
// Uma folha por dia, em paisagem. O preparador pediu arquivo único e
// impressão da página que interessa, e é isso que a paisagem viabiliza: as
// quatro semanas de carga só cabem lado a lado na folha deitada, e o dia
// inteiro cabe numa página só.
//
// Dia sem exercício não vira página em branco.
export async function gerarPDFPlanilhaTreino({
  planilha, modalidade, anoMes, alvoNome, contexto, nucleo, nucleoNome, preparador,
}) {
  const p = planilha || {};
  const dias = p.dias || {};
  const rotuloNucleo = nucleoNome || nucleo || "";

  const doc = await PDFDocument.create();
  const fontR = await doc.embedFont(StandardFonts.Helvetica);
  const fontB = await doc.embedFont(StandardFonts.HelveticaBold);

  let logoImg = null;
  try {
    logoImg = await doc.embedPng(await fetchBytes(`${process.env.PUBLIC_URL || ""}/relatorio/logo_revelar.png`));
  } catch (e) { /* segue sem logo */ }

  // Folha deitada: a mesma A4, com largura e altura trocadas.
  const LW = A4H, LH = A4W;
  const LARG = LW - 2 * MX;

  const hoje = new Date();
  const emitidoEm = `${String(hoje.getDate()).padStart(2, "0")}/${String(hoje.getMonth() + 1).padStart(2, "0")}/${hoje.getFullYear()}`;

  const DIAS_DA_PLANILHA = [
    ["seg", "Segunda-feira"], ["ter", "Terça-feira"], ["qua", "Quarta-feira"],
    ["qui", "Quinta-feira"], ["sex", "Sexta-feira"],
  ];
  const NOME_DO_BLOCO = { aquecimento: "Aquecimento", principal: "Parte principal", final: "Volta à calma" };
  const ORDEM_DOS_BLOCOS = ["aquecimento", "principal", "final"];

  // Larguras somando a faixa útil da folha. Objetivo e exercício levam o
  // espaço porque são texto; o resto é número curto.
  const COLS = [
    { chave: "n", rotulo: "", w: 22 },
    { chave: "objetivo", rotulo: "Objetivo", w: 122 },
    { chave: "exercicio", rotulo: "Exercício / método", w: 178 },
    { chave: "series", rotulo: "Séries", w: 42, centro: true },
    { chave: "repeticoes", rotulo: "Repetições", w: 66, centro: true },
    { chave: "intervalo", rotulo: "Intervalo", w: 62, centro: true },
    { chave: "carga", rotulo: "Carga base", w: 66, centro: true },
    { chave: "s1", rotulo: "Sem 1", w: 50, centro: true, carga: true },
    { chave: "s2", rotulo: "Sem 2", w: 50, centro: true, carga: true },
    { chave: "s3", rotulo: "Sem 3", w: 50, centro: true, carga: true },
    { chave: "s4", rotulo: "Sem 4", w: 50, centro: true, carga: true },
  ];

  const mesPorExtenso = (() => {
    const partes = String(anoMes || "").split("-");
    if (partes.length !== 2) return String(anoMes || "");
    const i = Number(partes[1]) - 1;
    return `${MESES_DO_ANO[i] || partes[1]} de ${partes[0]}`;
  })();

  // A carga de cada semana, calculada aqui como na tela: guardada pronta,
  // continuaria dizendo o valor antigo depois de alguém corrigir a base.

  function cortar(texto, fonte, tamanho, largura) {
    const t = txtSeguro(texto);
    if (!t) return "";
    if (fonte.widthOfTextAtSize(t, tamanho) <= largura) return t;
    let corte = t;
    while (corte.length > 1 && fonte.widthOfTextAtSize(corte + "...", tamanho) > largura) {
      corte = corte.slice(0, -1);
    }
    return corte + "...";
  }

  let page = null, y = 0;
  const paginas = [];

  function cabecalho(pg, diaRotulo, continuacao) {
    pg.drawRectangle({ x: 0, y: 0, width: 6, height: LH, color: AZUL });
    let logoX = LW - MX;
    if (logoImg) {
      const w = 30, h = w * logoImg.height / logoImg.width;
      logoX = LW - MX - w;
      pg.drawImage(logoImg, { x: logoX, y: LH - h - 12, width: w, height: h });
    }
    pg.drawText("REVELAR TALENTOS", { x: MX, y: LH - 30, size: 9.5, font: fontB, color: AZUL });
    // O que é este papel, em toda página. A folha é impressa avulsa e vai
    // parar na mesa junto com o plano técnico e com a folha de outra
    // modalidade; sem isto, quem pega não sabe de qual das duas é.
    pg.drawText(
      txtSeguro(`PLANILHA DE TREINO FÍSICO · ${modalidade || ""} · ${mesPorExtenso}`),
      { x: MX, y: LH - 41, size: 8, font: fontB, color: AZUL }
    );
    pg.drawText(
      txtSeguro(`Núcleo ${rotuloNucleo}`),
      { x: MX, y: LH - 51, size: 8, font: fontR, color: CINZA }
    );
    pg.drawLine({ start: { x: MX, y: LH - 59 }, end: { x: logoX - 12, y: LH - 59 }, thickness: 0.5, color: BORDA });

    // O nome do dia em tamanho grande: a folha é impressa avulsa e vai
    // parar na mão de quem não viu as outras páginas.
    pg.drawText(
      txtSeguro(diaRotulo + (continuacao ? " (continuação)" : "")),
      { x: MX, y: LH - 80, size: 15, font: fontB, color: TEXTO }
    );
    const quem = txtSeguro(alvoNome ? `Vale para: ${alvoNome}` : "Vale para: turma toda");
    pg.drawText(quem, { x: MX + 4 + fontB.widthOfTextAtSize(txtSeguro(diaRotulo), 15) + 14, y: LH - 77, size: 8.5, font: fontR, color: CINZA });
  }

  function rodape(pg) {
    pg.drawLine({ start: { x: MX, y: 34 }, end: { x: LW - MX, y: 34 }, thickness: 0.5, color: BORDA });
    pg.drawText(txtSeguro(IDENTIFICACAO.join(" · ")), { x: MX, y: 22, size: 7, font: fontR, color: CINZA });
    const dir = txtSeguro(`${preparador ? preparador + " · " : ""}Emitido em ${emitidoEm}`);
    pg.drawText(dir, { x: LW - MX - fontR.widthOfTextAtSize(dir, 7), y: 22, size: 7, font: fontR, color: CINZA });
  }

  function novaPagina(diaRotulo, continuacao) {
    page = doc.addPage([LW, LH]);
    cabecalho(page, diaRotulo, continuacao);
    rodape(page);
    paginas.push(page);
    y = LH - 100;
  }

  function cabecalhoDaTabela() {
    page.drawRectangle({ x: MX, y: y - 16, width: LARG, height: 16, color: BARRA_BG });
    let x = MX;
    COLS.forEach((c) => {
      if (c.rotulo) {
        const t = txtSeguro(c.rotulo);
        const px = c.centro ? x + (c.w - fontB.widthOfTextAtSize(t, 7)) / 2 : x + 5;
        page.drawText(t, { x: px, y: y - 11, size: 7, font: fontB, color: AZUL });
      }
      x += c.w;
    });
    y -= 16;
  }

  function linhaDoBloco(nome) {
    page.drawRectangle({ x: MX, y: y - 14, width: LARG, height: 14, color: CINZA_CL });
    page.drawText(txtSeguro(nome.toUpperCase()), { x: MX + 6, y: y - 10, size: 7, font: fontB, color: CINZA });
    y -= 14;
  }

  function linhaDoExercicio(l, n) {
    const alt = 17;
    const s = semanasDaLinha(l);
    const valores = {
      n: String(n),
      objetivo: l.objetivo || "",
      exercicio: l.exercicio || "",
      series: l.series || "",
      repeticoes: l.repeticoes || "",
      intervalo: l.intervalo || "",
      carga: l.carga || "",
      s1: s[0], s2: s[1], s3: s[2], s4: s[3],
    };
    let x = MX;
    COLS.forEach((c) => {
      if (c.carga) {
        page.drawRectangle({ x, y: y - alt, width: c.w, height: alt, color: CINZA_CL });
      }
      const bruto = valores[c.chave];
      const texto = bruto ? cortar(bruto, fontR, 8, c.w - 8) : (c.carga ? "-" : "");
      if (texto) {
        const fonte = c.carga ? fontB : fontR;
        const cor = c.carga ? (bruto ? AZUL : CINZA) : TEXTO;
        const px = c.centro
          ? x + (c.w - fonte.widthOfTextAtSize(texto, 8)) / 2
          : x + 5;
        page.drawText(texto, { x: px, y: y - 11.5, size: 8, font: fonte, color: cor });
      }
      x += c.w;
    });
    page.drawLine({
      start: { x: MX, y: y - alt }, end: { x: MX + LARG, y: y - alt },
      thickness: 0.4, color: BORDA,
    });
    y -= alt;
  }

  // ── Uma página por dia ──
  let saiuAlgumDia = false;

  DIAS_DA_PLANILHA.forEach(([chave, rotulo]) => {
    const dia = dias[chave] || {};
    const linhas = Array.isArray(dia.linhas) ? dia.linhas : [];
    const observacao = String(dia.observacao || "").trim();
    if (!linhas.length && !observacao) return;
    saiuAlgumDia = true;

    novaPagina(rotulo, false);

    // O que o plano combinou para o dia, quando existe: a planilha detalha
    // o microciclo, e lado a lado fica claro se o detalhe bateu com ele.
    const doPlano = contexto || {};
    const linhaDoPlano = [
      doPlano.foco ? `Objetivo do mês: ${doPlano.foco}` : "",
      (doPlano.microciclo && doPlano.microciclo[chave]) ? `Microciclo: ${doPlano.microciclo[chave]}` : "",
    ].filter(Boolean).join("  ·  ");
    if (linhaDoPlano) {
      page.drawText(cortar(linhaDoPlano, fontR, 8, LARG), { x: MX, y, size: 8, font: fontR, color: CINZA });
      y -= 14;
    }

    cabecalhoDaTabela();

    let n = 0;
    ORDEM_DOS_BLOCOS.forEach((bloco) => {
      const doBloco = linhas.filter((l) => (l.bloco || "principal") === bloco);
      if (!doBloco.length) return;
      // Nome do bloco sozinho no pé da página seria uma faixa anunciando
      // exercícios que estão na folha seguinte.
      if (y - 14 - 17 < 60) { novaPagina(rotulo, true); cabecalhoDaTabela(); }
      linhaDoBloco(NOME_DO_BLOCO[bloco] || bloco);
      doBloco.forEach((l) => {
        n += 1;
        if (y - 17 < 60) { novaPagina(rotulo, true); cabecalhoDaTabela(); }
        linhaDoExercicio(l, n);
      });
    });

    const totalDeSeries = linhas.reduce((soma, l) => {
      const v = Number(String(l.series || "").replace(",", "."));
      return soma + (Number.isFinite(v) && v > 0 ? v : 0);
    }, 0);
    y -= 10;
    if (y < 70) novaPagina(rotulo, true);
    page.drawText(
      txtSeguro(`${linhas.length} exercício(s) · ${totalDeSeries} série(s) no dia`),
      { x: MX, y, size: 8, font: fontB, color: CINZA }
    );
    y -= 14;

    if (observacao) {
      page.drawText("Observações", { x: MX, y, size: 7.5, font: fontB, color: CINZA });
      y -= 11;
      quebrarTexto(txtSeguro(observacao), fontR, 8.5, LARG).forEach((ln) => {
        if (y < 56) return;
        page.drawText(ln, { x: MX, y, size: 8.5, font: fontR, color: TEXTO });
        y -= 12;
      });
    }
  });

  if (!saiuAlgumDia) {
    novaPagina("Planilha sem treino lançado", false);
    page.drawText(
      txtSeguro("Nenhum exercício foi lançado neste mês."),
      { x: MX, y, size: 10, font: fontR, color: CINZA }
    );
  }

  // Numeração no rodapé, só agora: antes do fim não se sabe o total, e
  // "página 1 de 3" é o que permite imprimir a folha certa.
  paginas.forEach((pg, i) => {
    const t = txtSeguro(`Página ${i + 1} de ${paginas.length}`);
    pg.drawText(t, { x: (LW - fontR.widthOfTextAtSize(t, 7)) / 2, y: 22, size: 7, font: fontR, color: CINZA });
  });

  const bytes = await doc.save();
  const limpo = (s) => String(s || "").normalize("NFD").replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "_");
  const nomeArquivo = `Planilha_Treino_${limpo(modalidade)}_${String(anoMes || "").replace("-", "_")}.pdf`;
  baixar(bytes, nomeArquivo);
  return nomeArquivo;
}

// ============================================================
// RELATÓRIO DA AVALIAÇÃO DE ENCERRAMENTO DO CICLO
//
// Mesmo padrão dos demais: barra azul na lateral, logomarca, cabeçalho e
// rodapé idênticos, sem capa (como o relatório por modalidade).
//
// Todo número vem de apurar(), a mesma função que alimenta a tela e a
// planilha. Não há contagem refeita aqui: quando cada saída contava do seu
// jeito, bastava um arredondamento diferente para o PDF impresso e o painel
// discordarem na frente de quem decide.
// ============================================================

// O título da seção pelo id, da mesma lista que a tela usa. Seção sem
// título cadastrado sai pelo id, que é feio mas é melhor do que uma barra
// de seção em branco no documento.
function tituloDeSecao(id) {
  const s = SECOES_DA_AVALIACAO.find((x) => x.id === id);
  return s ? s.titulo : id;
}

export async function gerarPDFAvaliacaoCiclo({
  apuracao, totalRespostas, atletasAtivos, noPrazo,
  nucleo, nucleoNome, ciclo,
}) {
  const ap = apuracao || { fechadas: [], abertas: [] };
  const rotuloNucleo = nucleoNome || nucleo || "";

  const doc = await PDFDocument.create();
  const fontR = await doc.embedFont(StandardFonts.Helvetica);
  const fontB = await doc.embedFont(StandardFonts.HelveticaBold);

  let logoImg = null;
  try {
    logoImg = await doc.embedPng(await fetchBytes(`${process.env.PUBLIC_URL || ""}/relatorio/logo_revelar.png`));
  } catch (e) { /* segue sem logo */ }

  const hoje = new Date();
  const emitidoEm = `${String(hoje.getDate()).padStart(2, "0")}/${String(hoje.getMonth() + 1).padStart(2, "0")}/${hoje.getFullYear()}`;
  const LARGURA = A4W - 2 * MX;

  let page = null, y = 0;

  function desenharCabecalho(pg) {
    pg.drawRectangle({ x: 0, y: 0, width: 6, height: A4H, color: AZUL });
    let logoLeftX = A4W - MX;
    if (logoImg) {
      const w = 34, h = w * logoImg.height / logoImg.width;
      logoLeftX = A4W - MX - w;
      pg.drawImage(logoImg, { x: logoLeftX, y: A4H - h - 14, width: w, height: h });
    }
    pg.drawText("REVELAR TALENTOS", { x: MX, y: A4H - 34, size: 10, font: fontB, color: AZUL });
    pg.drawText(txtSeguro(`Avaliação de encerramento do ciclo ${ciclo}`),
      { x: MX, y: A4H - 46, size: 8, font: fontB, color: AZUL });
    pg.drawText(txtSeguro(`Núcleo ${rotuloNucleo}`),
      { x: MX, y: A4H - 56, size: 8, font: fontR, color: CINZA });
    pg.drawLine({
      start: { x: MX, y: A4H - 64 }, end: { x: logoLeftX - 12, y: A4H - 64 },
      thickness: 0.5, color: BORDA,
    });
  }

  function desenharRodape(pg) {
    pg.drawText(txtSeguro(IDENTIFICACAO.join(" · ")), { x: MX, y: 22, size: 7, font: fontR, color: CINZA });
    const dir = `Emitido em ${emitidoEm}`;
    pg.drawText(txtSeguro(dir), {
      x: A4W - MX - fontR.widthOfTextAtSize(dir, 7), y: 22, size: 7, font: fontR, color: CINZA,
    });
  }

  function novaPagina() {
    page = doc.addPage([A4W, A4H]);
    desenharCabecalho(page);
    desenharRodape(page);
    y = A4H - 82;
  }

  function garantirEspaco(altura) {
    if (y - altura < BOTTOM_LIMIT) novaPagina();
  }

  function secao(titulo) {
    garantirEspaco(34);
    y -= 4;
    page.drawRectangle({ x: MX, y: y - 17, width: LARGURA, height: 17, color: BARRA_BG });
    page.drawText(txtSeguro(titulo), { x: MX + 7, y: y - 12, size: 8.5, font: fontB, color: AZUL });
    y -= 27;
  }

  // A cor da barra sai da POSIÇÃO da opção na escala, e não do texto dela.
  // As escalas do questionário são todas ordenadas da melhor para a pior
  // ("Excelente, Boa, Regular, Ruim"), então a primeira sai verde e a
  // última vermelha em qualquer pergunta, sem uma tabela de palavras que
  // precisaria crescer a cada pergunta nova.
  function corPorPosicao(f, o) {
    const julgadas = f.opcoes.filter((x) => !x.neutra);
    const i = julgadas.findIndex((x) => x.opcao === o.opcao);
    if (i < 0) return BORDA_FORTE_PDF;
    const n = julgadas.length;
    if (n <= 1) return CLS.b.c;
    const posicao = i / (n - 1);
    if (posicao <= 0.01) return CLS.mb.c;
    if (posicao < 0.5) return CLS.b.c;
    if (posicao < 0.99) return CLS.r.c;
    return CLS.mr.c;
  }

  // A pergunta e, embaixo, uma linha por opção: rótulo, barra proporcional,
  // quantidade e percentual. A barra existe porque o olho compara
  // comprimento muito mais rápido do que lê quatro números em coluna.
  const LARGURA_ROTULO = 150;
  const LARGURA_BARRA = LARGURA - LARGURA_ROTULO - 86;

  function distribuicao(f) {
    const linhasDaPergunta = quebrarTexto(f.texto, fontB, 9, LARGURA);
    garantirEspaco(linhasDaPergunta.length * 12 + f.opcoes.length * 13 + 24);
    linhasDaPergunta.forEach((ln) => {
      page.drawText(ln, { x: MX, y, size: 9, font: fontB, color: TEXTO });
      y -= 12;
    });
    page.drawText(txtSeguro(`${f.responderam} resposta(s)`),
      { x: MX, y, size: 7.5, font: fontR, color: CINZA });
    y -= 12;

    f.opcoes.forEach((o) => {
      garantirEspaco(14);
      const corDaBarra = o.neutra ? BORDA_FORTE_PDF : corPorPosicao(f, o);
      page.drawText(txtSeguro(o.opcao), {
        x: MX + 6, y: y - 1, size: 8, font: fontR, color: o.neutra ? CINZA : TEXTO,
      });
      page.drawRectangle({
        x: MX + LARGURA_ROTULO, y: y - 4, width: LARGURA_BARRA, height: 8, color: CINZA_CL,
      });
      if (o.pct > 0) {
        page.drawRectangle({
          x: MX + LARGURA_ROTULO, y: y - 4,
          width: Math.max(2, LARGURA_BARRA * (o.pct / 100)), height: 8, color: corDaBarra,
        });
      }
      const direita = `${o.qtd}  ·  ${o.pct}%`;
      page.drawText(txtSeguro(direita), {
        x: A4W - MX - fontR.widthOfTextAtSize(txtSeguro(direita), 8), y: y - 1,
        size: 8, font: o.qtd > 0 ? fontB : fontR, color: o.qtd > 0 ? TEXTO : CINZA,
      });
      y -= 13;
    });
    y -= 8;
  }

  novaPagina();

  page.drawText(txtSeguro("AVALIAÇÃO DE ENCERRAMENTO DO CICLO"),
    { x: MX, y, size: 18, font: fontB, color: AZUL });
  y -= 16;
  page.drawText(txtSeguro(`Ciclo ${ciclo} · Núcleo ${rotuloNucleo}`),
    { x: MX, y, size: 10, font: fontR, color: TEXTO });
  y -= 20;

  // Os números do ciclo, em caixa, antes de qualquer barra: é a primeira
  // pergunta de quem recebe o documento, "quantos responderam".
  const participacao = atletasAtivos > 0
    ? `${Math.round((totalRespostas / atletasAtivos) * 100)}% dos ${atletasAtivos} atletas ativos`
    : "participação não calculada";
  page.drawRectangle({ x: MX, y: y - 30, width: LARGURA, height: 30, color: CINZA_CL });
  page.drawText(txtSeguro(`${totalRespostas} avaliação(ões) respondida(s)  ·  ${participacao}`),
    { x: MX + 8, y: y - 13, size: 9, font: fontB, color: TEXTO });
  page.drawText(txtSeguro(`${noPrazo} no prazo  ·  ${Math.max(0, totalRespostas - noPrazo)} fora do prazo`),
    { x: MX + 8, y: y - 24, size: 8, font: fontR, color: CINZA });
  y -= 42;

  const nomeDoArquivo =
    `Avaliacao_Ciclo_${String(ciclo).replace(/\//g, "-")}_${nucleoParaArquivo(nucleoNome, nucleo)}.pdf`;

  if (totalRespostas === 0) {
    page.drawText(txtSeguro("Nenhuma avaliação respondida neste ciclo até agora."),
      { x: MX, y, size: 9, font: fontR, color: CINZA });
    baixar(await doc.save(), nomeDoArquivo);
    return nomeDoArquivo;
  }

  // Uma seção por bloco do questionário, na ordem em que o atleta respondeu.
  const secoesNaOrdem = [];
  ap.fechadas.forEach((f) => { if (!secoesNaOrdem.includes(f.secao)) secoesNaOrdem.push(f.secao); });
  secoesNaOrdem.forEach((idDaSecao) => {
    secao(String(tituloDeSecao(idDaSecao)).toUpperCase());
    ap.fechadas.filter((f) => f.secao === idDaSecao).forEach(distribuicao);
  });

  // As respostas escritas, por extenso. Vão inteiras, sem corte: resposta
  // de atleta resumida dentro do documento de avaliação é justamente o que
  // o documento existe para não fazer.
  const comTexto = ap.abertas.filter((a) => a.respostas.length > 0);
  if (comTexto.length > 0) {
    secao("RESPOSTAS ESCRITAS");
    comTexto.forEach((ab) => {
      garantirEspaco(26);
      quebrarTexto(ab.texto, fontB, 9, LARGURA).forEach((ln) => {
        page.drawText(ln, { x: MX, y, size: 9, font: fontB, color: TEXTO });
        y -= 12;
      });
      y -= 2;
      ab.respostas.forEach((r) => {
        garantirEspaco(24);
        const quem = [r.nome, r.modalidade].filter(Boolean).join(" · ");
        page.drawText(txtSeguro(quem), { x: MX + 6, y, size: 7.5, font: fontB, color: CINZA });
        y -= 11;
        quebrarTexto(r.texto, fontR, 8.5, LARGURA - 12).forEach((ln) => {
          garantirEspaco(13);
          page.drawText(ln, { x: MX + 6, y, size: 8.5, font: fontR, color: TEXTO });
          y -= 12;
        });
        y -= 5;
      });
      y -= 6;
    });
  }

  baixar(await doc.save(), nomeDoArquivo);
  return nomeDoArquivo;
}

// ============================================================
// Avaliação física de um atleta, para impressão
//
// É o documento que vai para a pasta do atleta e para a mão da comissão
// técnica na sala, onde não há tela. Por isso leva a série inteira e não
// só a última coleta: o valor de hoje sozinho não diz nada, e quem lê no
// papel não tem como clicar para ver o anterior.
//
// Não leva gráfico. Os desenhos da tela existem para o olho comparar
// rápido; no papel, a mesma informação cabe melhor numa tabela, que ainda
// permite conferir número por número, que é o que se faz com a folha
// impressa na frente.
//
// Recebe a série JÁ COMPILADA, do mesmo jeito que a tela a monta. Refazer
// a conta aqui criaria um segundo lugar onde o percentual de gordura é
// calculado, e no dia em que um protocolo mudasse, tela e papel passariam
// a discordar sem ninguém perceber.
// ============================================================
// ── A folha A4 do padrão da casa ─────────────────────────────
//
// Cabeçalho, rodapé, seção, parágrafo e grade, com a página e a altura
// corrente dentro do próprio objeto.
//
// Isto era interno ao gerador da avaliação física e subiu para o módulo
// em 15/10/2026, quando a ficha completa passou a precisar das mesmas
// seções. O que importa não é a economia de linhas: é não existirem dois
// lugares desenhando a mesma seção, porque no dia em que o fracionamento
// mudar, só um dos dois seria corrigido.
//
// ── A armadilha da paginação ────────────────────────────────
//
// `page` e `y` ficam EM `f`, e quem desenha tem de ler `f.y`, nunca uma
// cópia local. O alerta é concreto: a tabela da série decidia a quebra
// com `if (y - 13 < BOTTOM_LIMIT)`, e uma cópia local de `y` faria a
// tabela parar de paginar e seguir desenhando abaixo do rodapé. O pdf-lib
// aceita y negativo sem reclamar, o texto continua no fluxo da página, e
// um teste que só procura texto passa verde com a folha quebrada.
//
// Por isso `garantirEspaco` recebe `aoQuebrar`: quem desenha tabela pede
// a quebra à folha e manda repetir o cabeçalho dela, em vez de decidir
// sozinho quando quebrar.
function criarFolhaA4({ doc, fontR, fontB, logoImg, subtitulo, rotuloNucleo, emitidoEm }) {
  const LARGURA = A4W - 2 * MX;
  const f = { doc, fontR, fontB, LARGURA, pagina: null, y: 0 };

  function desenharCabecalho(pg) {
    pg.drawRectangle({ x: 0, y: 0, width: 6, height: A4H, color: AZUL });
    let logoLeftX = A4W - MX;
    if (logoImg) {
      const w = 34, h = w * logoImg.height / logoImg.width;
      logoLeftX = A4W - MX - w;
      pg.drawImage(logoImg, { x: logoLeftX, y: A4H - h - 14, width: w, height: h });
    }
    pg.drawText("REVELAR TALENTOS", { x: MX, y: A4H - 34, size: 10, font: fontB, color: AZUL });
    pg.drawText(txtSeguro(subtitulo), { x: MX, y: A4H - 46, size: 8, font: fontB, color: AZUL });
    pg.drawText(txtSeguro(`Núcleo ${rotuloNucleo}`), {
      x: MX, y: A4H - 56, size: 8, font: fontR, color: CINZA,
    });
    pg.drawLine({
      start: { x: MX, y: A4H - 64 }, end: { x: logoLeftX - 12, y: A4H - 64 },
      thickness: 0.5, color: BORDA,
    });
  }

  function desenharRodape(pg) {
    pg.drawText(txtSeguro(IDENTIFICACAO.join(" · ")), {
      x: MX, y: 22, size: 7, font: fontR, color: CINZA,
    });
    const dir = `Emitido em ${emitidoEm}`;
    pg.drawText(txtSeguro(dir), {
      x: A4W - MX - fontR.widthOfTextAtSize(dir, 7), y: 22, size: 7, font: fontR, color: CINZA,
    });
  }

  f.novaPagina = function novaPagina() {
    f.pagina = doc.addPage([A4W, A4H]);
    desenharCabecalho(f.pagina);
    desenharRodape(f.pagina);
    f.y = A4H - 82;
  };

  // Devolve true quando quebrou a página, e chama `aoQuebrar` depois de
  // abrir a nova: é assim que a tabela repete o cabeçalho dela.
  f.garantirEspaco = function garantirEspaco(altura, aoQuebrar) {
    if (f.pagina && f.y - altura >= BOTTOM_LIMIT) return false;
    f.novaPagina();
    if (aoQuebrar) aoQuebrar();
    return true;
  };

  f.secao = function secao(titulo) {
    f.garantirEspaco(34);
    f.y -= 4;
    f.pagina.drawRectangle({ x: MX, y: f.y - 17, width: LARGURA, height: 17, color: BARRA_BG });
    f.pagina.drawText(txtSeguro(titulo), {
      x: MX + 7, y: f.y - 12, size: 8.5, font: fontB, color: AZUL,
    });
    f.y -= 27;
  };

  f.paragrafo = function paragrafo(texto, { size = 8, cor = CINZA } = {}) {
    // A limpeza acontece ANTES da quebra, e não no desenho.
    //
    // `quebrarTexto` mede cada palavra com a fonte, e medir um caractere
    // que o WinAnsi não codifica derruba a geração ali mesmo, antes de
    // qualquer drawText. Foi o que aconteceu quando o laudo dos testes
    // passou a escrever a queda com o sinal de menos tipográfico.
    quebrarTexto(txtSeguro(texto), fontR, size, LARGURA).forEach((ln) => {
      f.garantirEspaco(12);
      f.pagina.drawText(ln, { x: MX, y: f.y, size, font: fontR, color: cor });
      f.y -= 11;
    });
    f.y -= 4;
  };

  // Um valor por caixa, em grade. É o formato da tela, e aqui ele serve ao
  // mesmo objetivo: quem procura "quanto deu a gordura" acha sem ler o
  // resto da página.
  f.grade = function grade(itens, colunas = 4) {
    const larguraCaixa = (LARGURA - (colunas - 1) * 8) / colunas;
    // Laços simples, sem função dentro. A caixa é desenhada na página e na
    // altura do momento, e `garantirEspaco` pode trocar as duas no meio do
    // percurso: uma função declarada aqui dentro leria o valor de depois.
    for (let i = 0; i < itens.length; i += colunas) {
      f.garantirEspaco(40);
      const base = f.y;
      for (let k = 0; k < colunas && i + k < itens.length; k += 1) {
        const item = itens[i + k];
        const x = MX + k * (larguraCaixa + 8);
        f.pagina.drawRectangle({ x, y: base - 34, width: larguraCaixa, height: 34, color: CINZA_CL });
        f.pagina.drawText(txtSeguro(item.rotulo), {
          x: x + 6, y: base - 11, size: 6.5, font: fontR, color: CINZA,
        });
        f.pagina.drawText(txtSeguro(item.valor), {
          x: x + 6, y: base - 24, size: 10, font: fontB, color: TEXTO,
        });
        if (item.apoio) {
          f.pagina.drawText(txtSeguro(item.apoio), {
            x: x + 6, y: base - 31, size: 6, font: fontR, color: CINZA,
          });
        }
      }
      f.y = base - 40;
    }
  };

  // O cadastro com o retrato ao lado, igual ao cartão da Gestão e à ficha
  // na tela. Retrato nulo não deixa buraco: as linhas sobem para a margem,
  // porque atleta sem foto é o caso comum e uma moldura vazia no papel
  // parece erro de impressão.
  f.cadastroComRetrato = function cadastroComRetrato({ retrato, linhas }) {
    const LADO = 92;
    const ALTURA_DO_RETRATO = Math.round(LADO * 1.25);
    const ALTURA_DA_LINHA = 13;
    const alturaDoTexto = linhas.length * ALTURA_DA_LINHA;
    const altura = Math.max(retrato ? ALTURA_DO_RETRATO : 0, alturaDoTexto);

    // O bloco inteiro cabe ou passa junto para a página seguinte. Partir
    // o retrato do atleta das linhas dele seria o pior corte possível.
    f.garantirEspaco(altura + 10);
    const topo = f.y;

    if (retrato) {
      f.pagina.drawImage(retrato, {
        x: MX, y: topo - ALTURA_DO_RETRATO, width: LADO, height: ALTURA_DO_RETRATO,
      });
    }

    const xTexto = MX + (retrato ? LADO + 14 : 0);
    for (let i = 0; i < linhas.length; i += 1) {
      const linha = linhas[i];
      const y = topo - 9 - i * ALTURA_DA_LINHA;
      const rotulo = txtSeguro(`${linha.rotulo}:`);
      f.pagina.drawText(rotulo, { x: xTexto, y, size: 7.5, font: fontB, color: CINZA });
      f.pagina.drawText(txtSeguro(String(linha.valor)), {
        x: xTexto + fontB.widthOfTextAtSize(rotulo, 7.5) + 5, y,
        size: 9, font: linha.destaque ? fontB : fontR, color: TEXTO,
      });
    }

    f.y = topo - altura - 10;
  };

  f.assinaturas = function assinaturas(rotulos) {
    f.secao("Assinaturas");
    f.y -= 6;
    rotulos.forEach((rot) => {
      f.garantirEspaco(34);
      f.pagina.drawLine({
        start: { x: MX, y: f.y }, end: { x: MX + 230, y: f.y },
        thickness: 0.6, color: BORDA_FORTE_PDF,
      });
      f.pagina.drawText(txtSeguro(rot), { x: MX, y: f.y - 10, size: 7, font: fontR, color: CINZA });
      f.y -= 32;
    });
  };

  return f;
}

// Data no formato de quem lê, com travessão quando não é data.
//
// Diferente de `fmtDataBR`, que devolve o que recebeu: na folha
// impressa, campo vazio tem de aparecer como travessão, e não como
// espaço em branco que parece falha de impressão.
function dataOuTraco(iso) {
  return DATA_ISO.test(String(iso || "")) ? fmtDataBR(iso) : "—";
}

// Número no formato de quem lê, com o travessão do valor ausente.
function numPt(v, casas = 1) {
  return v === null || v === undefined || !Number.isFinite(Number(v))
    ? "—"
    : Number(v).toFixed(casas).replace(".", ",");
}

// O cabeçalho de identificação do atleta, igual nas duas folhas.
// `comDetalhe` existe por causa da ficha completa: logo abaixo dela vem a
// seção Cadastro, com modalidade, categoria e nascimento em linhas
// rotuladas. Impressos duas vezes na mesma página, os mesmos campos fazem
// quem lê procurar a diferença entre eles, e não há nenhuma.
// `comNome` existe pela ficha completa: logo abaixo do título vem a seção
// Cadastro, com o retrato e o nome em linha rotulada ao lado. Escrito duas
// vezes na mesma dobra da página, o nome não acrescenta nada.
function desenharIdentificacao(f, { atleta, titulo, comDetalhe = true, comNome = true }) {
  const a = atleta || {};
  f.pagina.drawText(txtSeguro(titulo), { x: MX, y: f.y, size: 18, font: f.fontB, color: AZUL });
  f.y -= 18;
  if (comNome) {
    f.pagina.drawText(txtSeguro(a.nome || a.name || "Atleta"), {
      x: MX, y: f.y, size: 12, font: f.fontB, color: TEXTO,
    });
  }
  f.y -= 13;
  if (comDetalhe) {
    f.pagina.drawText(txtSeguro([
      normalizarModalidade(a.modalidade) || "sem modalidade",
      a.categoria || null,
      a.dataNascimento ? `nascimento ${fmtDataBR(a.dataNascimento)}` : null,
    ].filter(Boolean).join("  ·  ")), { x: MX, y: f.y, size: 9, font: f.fontR, color: CINZA });
  }
  f.y -= 22;
}

// ── A avaliação física, desenhada ────────────────────────────
//
// A mesma seção serve à folha só de avaliação física e à ficha completa.
// Recebe a série JÁ COMPILADA, do mesmo jeito que a tela a monta: refazer
// a conta aqui criaria um segundo lugar onde o percentual de gordura é
// calculado, e no dia em que um protocolo mudasse, tela e papel passariam
// a discordar sem ninguém perceber.
function desenharAvaliacaoFisica(f, { pontos, sintese }) {
  const n = numPt;
  const emBR = dataOuTraco;

  // ── A última coleta ────────────────────────────────────────
  const ultimo = pontos[pontos.length - 1];
  const comp = ultimo.deurenberg || null;
  const frac = ultimo.fracionamento || null;
  const som = ultimo.somatotipo || null;
  const mat = ultimo.maturacao || null;
  const idx = ultimo.indices || null;

  f.secao(`Coleta de ${emBR(ultimo.data)}`);
  f.grade([
    { rotulo: "IDADE NA COLETA", valor: ultimo.idade != null ? `${n(ultimo.idade, 1)} anos` : "—" },
    { rotulo: "PESO", valor: ultimo.peso != null ? `${n(ultimo.peso, 1)} kg` : "—" },
    { rotulo: "ESTATURA", valor: ultimo.estatura != null ? `${n(ultimo.estatura, 1)} cm` : "—" },
    { rotulo: "IMC", valor: idx && idx.imc != null ? n(idx.imc, 1) : "—" },
  ]);
  // O PROTOCOLO fica na grade, e é obrigatório que ele apareça em algum
  // lugar da folha: é ele que explica por que o número desta folha pode
  // não bater com o de uma folha antiga do mesmo atleta.
  f.grade([
    {
      rotulo: "GORDURA",
      valor: comp && comp.completo ? `${n(comp.percentualGordura, 1)}%` : "—",
      apoio: comp && comp.completo ? comp.classificacao : null,
    },
    { rotulo: "MASSA GORDA", valor: comp && comp.completo ? `${n(comp.massaGorda, 1)} kg` : "—" },
    { rotulo: "MASSA MAGRA", valor: comp && comp.completo ? `${n(comp.massaMagra, 1)} kg` : "—" },
    {
      rotulo: "PROTOCOLO",
      valor: comp && comp.protocolo ? (rotuloDoProtocolo(comp.protocolo) || "—") : "—",
    },
  ]);

  if (comp && !comp.completo && (comp.faltam || []).length) {
    f.paragrafo(`Composição corporal não calculada: faltam ${comp.faltam.length} medida(s) do protocolo.`);
  }

  if (frac && frac.completo) {
    f.secao("Fracionamento da massa corporal");
    f.grade([
      { rotulo: "MASSA ADIPOSA", valor: `${n(frac.massaGorda, 2)} kg` },
      { rotulo: "MASSA MUSCULAR", valor: `${n(frac.massaMuscular, 2)} kg` },
      { rotulo: "MASSA ÓSSEA", valor: `${n(frac.massaOssea, 2)} kg` },
      { rotulo: "MASSA RESIDUAL", valor: `${n(frac.massaResidual, 2)} kg` },
    ]);
  }

  if (som && som.completo) {
    f.secao("Somatotipo");
    f.grade([
      { rotulo: "ENDOMORFIA", valor: n(som.endomorfia, 1), apoio: "adiposidade" },
      { rotulo: "MESOMORFIA", valor: n(som.mesomorfia, 1), apoio: "músculo e osso" },
      { rotulo: "ECTOMORFIA", valor: n(som.ectomorfia, 1), apoio: "linearidade" },
      { rotulo: "PERFIL", valor: som.classificacao || "—" },
    ]);
    // Os três números não dizem nada a quem não é da área, e a folha
    // impressa não tem onde explicar além dela mesma.
    const laudo = laudoDoSomatotipo(som);
    if (laudo) {
      f.paragrafo(laudo.texto, { size: 8, cor: TEXTO });
      f.paragrafo(laudo.ressalva, { size: 7 });
    }
  }

  // ── Maturação ──
  //
  // Esta seção saía vazia até 15/10/2026: lia `mat.idadePVC`, que não
  // existe no resultado, e passava `mat.estagio` ("Pós-PVC") por
  // `rotuloDoEstagio`, que espera a chave do estágio de Deurenberg
  // ("pre", "pubere", "pos"). Imprimia "não informado" e "— anos" numa
  // folha que na tela mostrava os dois valores certos.
  const laudoMat = laudoDaMaturacao(mat);
  if (laudoMat) {
    f.secao("Maturação");
    f.grade([
      { rotulo: "ESTÁGIO", valor: laudoMat.estagio },
      { rotulo: "PICO ESTIMADO", valor: `${n(mat.idadeNoPico, 1)} anos` },
      { rotulo: "MÉTODO", valor: mat.metodo === "mirwald" ? "Mirwald" : "Moore" },
      {
        rotulo: "ESTÁGIO DE DEURENBERG",
        valor: rotuloDoEstagio(ultimo.estagioMaturacional) || "não informado",
      },
    ]);
    f.paragrafo(laudoMat.texto, { size: 8, cor: TEXTO });
    f.paragrafo(laudoMat.ressalva, { size: 7 });
  }

  // ── Índices e proporções ───────────────────────────────────
  //
  // A tela mostra estes seis desde sempre e a folha não mostrava nenhum,
  // então quem lia o PDF na reunião não tinha a razão cintura/estatura
  // que a tela usava para falar do atleta. São razões entre medidas do
  // próprio atleta, sem equação de população no meio.
  const temIndices = idx && [
    idx.somaDasDobras, idx.razaoCinturaQuadril, idx.razaoCinturaEstatura,
    idx.indiceDeConicidade, idx.indiceCormico, idx.razaoEnvergaduraEstatura,
  ].some((v) => v != null && Number.isFinite(Number(v)));

  if (temIndices) {
    f.secao("Índices e proporções");
    f.grade([
      {
        rotulo: "SOMATÓRIO DAS DOBRAS",
        valor: idx.somaDasDobras != null ? `${n(idx.somaDasDobras, 1)} mm` : "—",
        apoio: "medida direta",
      },
      { rotulo: "CINTURA / QUADRIL", valor: n(idx.razaoCinturaQuadril, 2) },
      { rotulo: "CINTURA / ESTATURA", valor: n(idx.razaoCinturaEstatura, 2) },
      { rotulo: "ÍNDICE DE CONICIDADE", valor: n(idx.indiceDeConicidade, 2) },
    ]);
    if (idx.indiceCormico != null || idx.razaoEnvergaduraEstatura != null) {
      f.grade([
        {
          rotulo: "ÍNDICE CÓRMICO",
          valor: idx.indiceCormico != null ? `${n(idx.indiceCormico, 1)}%` : "—",
          apoio: "quanto da estatura é tronco",
        },
        {
          rotulo: "ENVERGADURA / ESTATURA",
          valor: n(idx.razaoEnvergaduraEstatura, 3),
          apoio: "1,00 é braço do tamanho da altura",
        },
      ], 2);
    }
    f.paragrafo(
      "O somatório das dobras é medida direta; o percentual de gordura é estimativa "
      + "calculada a partir dele. As duas últimas razões mudam no estirão, porque a perna e "
      + "o braço crescem antes do tronco, e servem para acompanhar a mudança de proporção "
      + "do próprio atleta.",
      { size: 7 },
    );
  }

  // ── A série ────────────────────────────────────────────────
  f.secao("Série do atleta");

  const COLS = [
    { t: "Data", w: 58, get: (p) => emBR(p.data) },
    { t: "Idade", w: 38, get: (p) => (p.idade != null ? n(p.idade, 1) : "—") },
    { t: "Peso", w: 44, get: (p) => n(p.peso, 1) },
    { t: "Estatura", w: 50, get: (p) => n(p.estatura, 1) },
    { t: "IMC", w: 38, get: (p) => n(p.indices && p.indices.imc, 1) },
    { t: "Gordura", w: 48, get: (p) => (p.deurenberg && p.deurenberg.completo ? `${n(p.deurenberg.percentualGordura, 1)}%` : "—") },
    { t: "M. adiposa", w: 56, get: (p) => n((p.fracionamento && p.fracionamento.massaGorda) || (p.deurenberg && p.deurenberg.massaGorda), 2) },
    { t: "M. muscular", w: 58, get: (p) => n(p.fracionamento && p.fracionamento.massaMuscular, 2) },
    { t: "Protocolo", w: null, get: (p) => (p.deurenberg && p.deurenberg.protocolo ? (rotuloDoProtocolo(p.deurenberg.protocolo) || "—") : "—") },
  ];
  const fixas = COLS.reduce((soma, c) => soma + (c.w || 0), 0);
  const larguras = COLS.map((c) => (c.w === null ? f.LARGURA - fixas : c.w));

  function cabecalhoDaTabela() {
    f.garantirEspaco(18);
    f.pagina.drawRectangle({ x: MX, y: f.y - 13, width: f.LARGURA, height: 13, color: BARRA_BG });
    let x = MX + 4;
    COLS.forEach((c, i) => {
      f.pagina.drawText(txtSeguro(c.t), { x, y: f.y - 9.5, size: 6.5, font: f.fontB, color: AZUL });
      x += larguras[i];
    });
    f.y -= 15;
  }

  cabecalhoDaTabela();
  pontos.forEach((pt, linha) => {
    // A quebra é pedida à folha, que repete o cabeçalho da tabela na
    // página nova. Decidir a quebra aqui, lendo um `y` local, é o erro
    // que faz a tabela seguir desenhando abaixo do rodapé.
    f.garantirEspaco(13, cabecalhoDaTabela);
    if (linha % 2 === 1) {
      f.pagina.drawRectangle({ x: MX, y: f.y - 11, width: f.LARGURA, height: 12, color: CINZA_CL });
    }
    let x = MX + 4;
    COLS.forEach((c, i) => {
      f.pagina.drawText(txtSeguro(String(c.get(pt))), {
        x, y: f.y - 8, size: 7, font: linha === pontos.length - 1 ? f.fontB : f.fontR, color: TEXTO,
      });
      x += larguras[i];
    });
    f.y -= 12;
  });
  f.y -= 6;

  // ── A troca de equação, dita também no papel ───────────────
  //
  // Mesma informação do aviso da tela. No papel ela pesa mais: quem compara
  // duas folhas impressas do mesmo atleta não tem como passar o cursor em
  // cima da coluna para descobrir que a régua mudou.
  const protocolosUsados = [];
  pontos.forEach((pt) => {
    const prot = pt.deurenberg && pt.deurenberg.completo && pt.deurenberg.protocolo;
    if (!prot) return;
    const ultimoProt = protocolosUsados[protocolosUsados.length - 1];
    if (!ultimoProt || ultimoProt.protocolo !== prot) protocolosUsados.push({ protocolo: prot, data: pt.data });
  });
  if (protocolosUsados.length > 1) {
    const virada = protocolosUsados[protocolosUsados.length - 1];
    const antes = protocolosUsados[protocolosUsados.length - 2];
    f.garantirEspaco(46);
    const topo = f.y;
    f.pagina.drawRectangle({ x: MX, y: topo - 40, width: f.LARGURA, height: 40, color: BARRA_BG });
    f.y = topo - 12;
    quebrarTexto(
      `A coluna de gordura muda de equação no meio da série. Até ${emBR(antes.data)} o cálculo `
      + `saiu por ${rotuloDoProtocolo(antes.protocolo)}; de ${emBR(virada.data)} em diante, por `
      + `${rotuloDoProtocolo(virada.protocolo)}, porque o atleta passou dos 18 anos. Parte da `
      + "diferença entre esses dois pontos é troca de equação, e não mudança do atleta.",
      f.fontR, 7, f.LARGURA - 16
    ).slice(0, 3).forEach((ln) => {
      f.pagina.drawText(ln, { x: MX + 8, y: f.y, size: 7, font: f.fontR, color: TEXTO });
      f.y -= 9;
    });
    f.y = topo - 48;
  }

  // ── A síntese da equipe ────────────────────────────────────
  // A síntese é o que a equipe concluiu da coleta, em quatro campos. Vai
  // inteira: é ela que transforma a medida em conduta, e a folha que chega
  // ao treino sem ela volta a ser uma tabela de números.
  const CAMPOS_DA_SINTESE = [
    { chave: "avancos", rotulo: "Avanços" },
    { chave: "atencao", rotulo: "Pontos de atenção" },
    { chave: "prioridades", rotulo: "Prioridades até a próxima avaliação" },
  ];
  const temSintese = sintese && CAMPOS_DA_SINTESE.some((c) => String(sintese[c.chave] || "").trim());
  if (temSintese) {
    f.secao("Síntese da equipe");
    CAMPOS_DA_SINTESE.forEach((c) => {
      const texto = String(sintese[c.chave] || "").trim();
      if (!texto) return;
      f.garantirEspaco(14);
      f.pagina.drawText(txtSeguro(c.rotulo), { x: MX, y: f.y, size: 7.5, font: f.fontB, color: AZUL });
      f.y -= 11;
      f.paragrafo(texto, { size: 8.5, cor: TEXTO });
    });
    const assinada = [
      sintese.responsavel ? `Escrita por ${sintese.responsavel}` : null,
      sintese.escritaEm ? `em ${emBR(sintese.escritaEm)}` : null,
      sintese.proximaAvaliacao ? `Próxima avaliação prevista para ${emBR(sintese.proximaAvaliacao)}` : null,
    ].filter(Boolean).join("  ·  ");
    if (assinada) f.paragrafo(assinada, { size: 7 });
  }
}

// ── A frequência, desenhada ─────────────────────────
//
// Recebe o que a tela já somou. Duas partes: o período inteiro, com as
// duas frequências, e uma linha por avaliação, com o treino daquela
// janela ao lado do escore que saiu dela.
//
// Não há fórmula ligando os dois, e a folha diz isso por escrito: pôr
// número ao lado de número num relatório que vai à reunião convida a
// leitura de causa, e frequência alta não produz escore alto por si.
function desenharFrequencia(f, { periodo, total, porOnda = [] }) {
  const n = numPt;
  const emBR = dataOuTraco;
  const pct = v => (Number.isFinite(v) ? `${n(v, 1)}%` : "—");

  f.secao(periodo && periodo.rotulo ? `Frequência · ${periodo.rotulo}` : "Frequência");
  f.grade([
    { rotulo: "TREINOS", valor: String(total.treinos) },
    { rotulo: "PRESENÇAS", valor: String(total.presencas) },
    { rotulo: "FREQUÊNCIA ABSOLUTA", valor: pct(total.pctAbs) },
    { rotulo: "FREQUÊNCIA TOTAL", valor: pct(total.pctTotal) },
  ]);
  f.paragrafo(
    `${total.abonos} dia(s) abonado(s) e ${total.faltas} falta(s). A frequência absoluta `
    + "considera apenas as presenças registradas em relação aos dias de treino do "
    + "período. A frequência total acrescenta os dias abonados: competição, dispensa de "
    + "modalidade, viagem e justificativa aceita pela coordenação.",
    { size: 7.5 },
  );

  if (!porOnda.length) return;
  f.secao("Frequência por período de avaliação");
  desenharTabela(f, [
    { t: "Avaliação", w: 62, get: l => emBR(l.data) },
    { t: "Período", w: 92, get: l => (l.desdeAInicio ? "até esta data" : "desde a anterior") },
    { t: "Treinos", w: 50, get: l => String(l.treinos) },
    { t: "Presenças", w: 56, get: l => String(l.presencas) },
    { t: "Absoluta", w: 56, get: l => pct(l.pctAbs) },
    { t: "Total", w: 50, get: l => pct(l.pctTotal) },
    { t: "Escore", w: 50, get: l => (Number.isFinite(l.global) ? n(l.global, 1) : "—") },
    {
      t: "Variação",
      w: null,
      get: l => (Number.isFinite(l.variacao)
        ? `${l.variacao > 0 ? "+" : ""}${n(l.variacao, 1)}`
        : "—"),
    },
  ], porOnda);
  f.paragrafo(
    "Cada linha corresponde ao período decorrido desde a avaliação anterior, apurado por "
    + "mês inteiro. Os dois indicadores são apresentados lado a lado, sem relação de "
    + "cálculo entre si: a frequência não compõe o escore, e a interpretação conjunta "
    + "cabe à comissão técnica.",
    { size: 7 },
  );
}

// ── O desempenho em competições, desenhado ───────────────────
//
// Recebe a linha do atleta no quadro da modalidade, como o servidor a
// devolve, e o tamanho do pódio que o cadastro da modalidade fixa. Uma
// linha por colocação premiada, igual à tela e ao anexo de competições.
function desenharCompeticoes(f, { linha, tamanhoDoPodio, modalidade }) {
  const METAL = { 1: "OURO", 2: "PRATA", 3: "BRONZE" };
  const l = linha || null;

  f.secao("Desempenho em competições");
  if (!l || !l.participacoes) {
    f.paragrafo("Nenhum resultado lançado para este atleta.", { size: 8.5, cor: TEXTO });
    return;
  }

  const ate = Math.max(1, Number(tamanhoDoPodio) || 3);
  const porPosicao = [];
  for (let i = 1; i <= ate; i += 1) {
    porPosicao.push({
      rotulo: METAL[i] || `${i}º LUGAR`,
      valor: String((l.porColocacao || {})[i] ?? (i === 1 ? (l.medalhas || {}).ouro
        : i === 2 ? (l.medalhas || {}).prata
          : i === 3 ? (l.medalhas || {}).bronze : 0) ?? 0),
    });
  }
  f.grade(porPosicao, Math.min(5, porPosicao.length));
  f.grade([
    { rotulo: "PÓDIOS", valor: String(l.totalMedalhas || 0) },
    { rotulo: "PROVAS", valor: String(l.participacoes || 0) },
    { rotulo: "COMPETIÇÕES", valor: String(l.competicoes || 0) },
    { rotulo: "APROVEITAMENTO", valor: emPorcento(l.aproveitamento) },
    { rotulo: "MELHOR", valor: l.melhorColocacao ? `${l.melhorColocacao}º` : "—" },
  ], 5);
  f.paragrafo(legendaDoQuadroDeMedalhas({ modalidade, tamanhoDoPodio: ate }).join(" "));

  const lancamentos = Array.isArray(l.lancamentos) ? l.lancamentos : [];
  if (!lancamentos.length) return;

  const COLS = [
    { t: "Data", w: 58, get: (r) => dataOuTraco(r.dataInicio) },
    { t: "Competição", w: 190, get: (r) => r.competicao || "—" },
    { t: "Categoria", w: 140, get: (r) => r.categoria || "—" },
    { t: "Tipo", w: null, get: (r) => (r.tipo === "principal" ? "principal" : "secundária") },
    { t: "Coloc.", w: 42, get: (r) => (r.colocacao ? `${r.colocacao}º` : "—") },
  ];
  const fixas = COLS.reduce((soma, c) => soma + (c.w || 0), 0);
  const larguras = COLS.map((c) => (c.w === null ? f.LARGURA - fixas : c.w));

  function cabecalhoDaTabela() {
    f.garantirEspaco(18);
    f.pagina.drawRectangle({ x: MX, y: f.y - 13, width: f.LARGURA, height: 13, color: BARRA_BG });
    let x = MX + 4;
    COLS.forEach((c, i) => {
      f.pagina.drawText(txtSeguro(c.t), { x, y: f.y - 9.5, size: 6.5, font: f.fontB, color: AZUL });
      x += larguras[i];
    });
    f.y -= 15;
  }

  cabecalhoDaTabela();
  [...lancamentos]
    .sort((x, z) => String(z.dataInicio || "").localeCompare(String(x.dataInicio || "")))
    .forEach((r, i) => {
      f.garantirEspaco(13, cabecalhoDaTabela);
      if (i % 2 === 1) {
        f.pagina.drawRectangle({ x: MX, y: f.y - 11, width: f.LARGURA, height: 12, color: CINZA_CL });
      }
      let x = MX + 4;
      COLS.forEach((c, j) => {
        f.pagina.drawText(txtSeguro(String(c.get(r))), {
          x, y: f.y - 8, size: 7, font: f.fontR, color: TEXTO,
        });
        x += larguras[j];
      });
      f.y -= 12;
    });
  f.y -= 6;
}

// Seção de módulo que ainda não tem lançamento.
//
// A ficha completa imprime as quatro áreas sempre, mesmo vazias. Uma
// folha que simplesmente omite a avaliação técnica deixa quem lê sem
// saber se o atleta não foi avaliado ou se a folha é que não a traz.
function desenharModuloVazio(f, { titulo, texto }) {
  f.secao(titulo);
  f.paragrafo(texto, { size: 8.5, cor: TEXTO });
}

export async function gerarPDFAvaliacaoFisica({
  atleta, serie, nucleo, nucleoNome, sintese = null,
}) {
  const a = atleta || {};
  const pontos = (Array.isArray(serie) ? serie : []).filter((p) => p && p.data);

  const doc = await PDFDocument.create();
  const fontR = await doc.embedFont(StandardFonts.Helvetica);
  const fontB = await doc.embedFont(StandardFonts.HelveticaBold);

  let logoImg = null;
  try {
    logoImg = await doc.embedPng(await fetchBytes(`${process.env.PUBLIC_URL || ""}/relatorio/logo_revelar.png`));
  } catch (e) { /* segue sem logo */ }

  const hoje = new Date();
  const emitidoEm = `${String(hoje.getDate()).padStart(2, "0")}/${String(hoje.getMonth() + 1).padStart(2, "0")}/${hoje.getFullYear()}`;

  const f = criarFolhaA4({
    doc, fontR, fontB, logoImg,
    subtitulo: "Avaliação física",
    rotuloNucleo: nucleoNome || nucleo || "",
    emitidoEm,
  });
  f.novaPagina();
  desenharIdentificacao(f, { atleta: a, titulo: "AVALIAÇÃO FÍSICA" });

  if (!pontos.length) {
    f.paragrafo("Nenhuma avaliação registrada para este atleta.", { size: 9, cor: TEXTO });
    const soCabecalho = `Avaliacao_Fisica_${nucleoParaArquivo(nucleoNome, nucleo)}.pdf`;
    baixar(await doc.save(), soCabecalho);
    return soCabecalho;
  }

  desenharAvaliacaoFisica(f, { pontos, sintese });
  f.assinaturas([
    "Avaliador responsável pela coleta",
    "Preparação física",
    "Atleta ou responsável legal",
  ]);

  const nomeArquivo = `Avaliacao_Fisica_${arquivoDoAtleta(a)}_${nucleoParaArquivo(nucleoNome, nucleo)}.pdf`;
  baixar(await doc.save(), nomeArquivo);
  return nomeArquivo;
}

// O nome do atleta reduzido ao que serve de nome de arquivo.
function arquivoDoAtleta(a) {
  return String((a && (a.nome || a.name)) || "Atleta")
    .normalize("NFD")
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

// ============================================================
// Ficha completa do atleta
//
// O dossiê: cadastro, avaliação física, avaliação técnica, testes
// físicos e desempenho em competições num documento só. É o que vai para
// a reunião de comissão técnica, para a prestação de contas e para a mão
// de quem acompanha o atleta sem abrir o sistema.
//
// As quatro áreas aparecem SEMPRE, mesmo vazias. Técnica e testes ainda
// não têm lançamento no sistema, e omiti-las deixaria quem lê sem saber
// se o atleta não foi avaliado ou se a folha é que não as traz. Quando os
// dados entrarem, o texto de ausência vira a seção de verdade e o resto
// da folha não muda.
//
// A impressão isolada de cada área continua existindo na aba dela: a
// avaliação física tem a sua, e esta aqui é a soma.
// ============================================================
export async function gerarPDFFichaCompleta({
  atleta, serie, sintese = null, competicoes = null, nucleo, nucleoNome,
  // As séries da avaliação técnica e dos testes físicos, já compiladas
  // pela tela, e o escore global que junta as duas. Vêm de fora pelo
  // mesmo motivo da série física: a conta mora no módulo de domínio, e
  // refazê-la aqui criaria um segundo lugar onde a nota é calculada.
  serieTecnica = null, serieDeTestes = null, escores = null,
  // A frequência, já somada pela tela: `{ periodo, total, porOnda }`. Pelo
  // mesmo motivo das séries, e por um a mais: ela sai de `calculo.js`, mês
  // a mês, e refazê-la aqui põe um segundo lugar calculando presença.
  frequencia = null,
  // O retrato, como a tela o carregou: { base64, tipo }. Vem de fora
  // porque buscá-lo é chamada autenticada, e quem tem sessão é a tela;
  // este arquivo não tem como pedir nada ao servidor.
  foto = null,
}) {
  const a = atleta || {};
  const pontos = (Array.isArray(serie) ? serie : []).filter((p) => p && p.data);

  const doc = await PDFDocument.create();
  const fontR = await doc.embedFont(StandardFonts.Helvetica);
  const fontB = await doc.embedFont(StandardFonts.HelveticaBold);

  let logoImg = null;
  try {
    logoImg = await doc.embedPng(await fetchBytes(`${process.env.PUBLIC_URL || ""}/relatorio/logo_revelar.png`));
  } catch (e) { /* segue sem logo */ }

  const hoje = new Date();
  const emitidoEm = `${String(hoje.getDate()).padStart(2, "0")}/${String(hoje.getMonth() + 1).padStart(2, "0")}/${hoje.getFullYear()}`;

  const f = criarFolhaA4({
    doc, fontR, fontB, logoImg,
    subtitulo: "Ficha de Acompanhamento e Desempenho do Atleta",
    rotuloNucleo: nucleoNome || nucleo || "",
    emitidoEm,
  });
  f.novaPagina();
  desenharIdentificacao(f, {
    atleta: a, titulo: "FICHA DE ACOMPANHAMENTO E DESEMPENHO DO ATLETA",
    comDetalhe: false, comNome: false,
  });

  // ── Cadastro ──
  //
  // Retrato à esquerda, informação em linhas rotuladas ao lado: o mesmo
  // desenho do cartão de cadastro em Gestão e da ficha na tela. Os três
  // lugares mostram o mesmo cadastro, e mostrá-lo de três jeitos fazia
  // quem conferia um contra o outro procurar onde estava cada campo.
  const anos = idadeEm(a.dataNascimento);
  f.secao("Cadastro");

  // A foto é opcional e nunca derruba a geração: atleta sem retrato é o
  // caso comum, e um tipo de imagem que o pdf-lib não aceita não pode
  // custar a ficha inteira.
  let retrato = null;
  if (foto && foto.base64) {
    try {
      const bytes = Uint8Array.from(atob(foto.base64), (c) => c.charCodeAt(0));
      retrato = String(foto.tipo || "").includes("png")
        ? await doc.embedPng(bytes)
        : await doc.embedJpg(bytes);
    } catch (e) { retrato = null; }
  }

  // Responsável e contato ficam DE FORA, de propósito.
  //
  // A tela mostra os dois para menor de idade, e aqui não. A diferença é
  // o papel: esta folha é impressa e circula, e telefone de responsável
  // numa folha que anda pela sala é dado de terceiro exposto sem
  // necessidade. Quem precisa do contato abre a ficha na tela, onde ele
  // está. Decisão da coordenação em 17/10/2026, não esquecimento.
  f.cadastroComRetrato({
    retrato,
    linhas: [
      { rotulo: "Nome", valor: a.nome || a.name || "—", destaque: true },
      { rotulo: "Núcleo", valor: nucleoNome || nucleo || "—" },
      { rotulo: "Gênero", valor: ROTULO_DO_GENERO[a.sexo] || "—" },
      { rotulo: "Modalidade", valor: normalizarModalidade(a.modalidade) || "—" },
      { rotulo: "Categoria principal", valor: a.categoria || "—" },
      {
        rotulo: "Data de Nascimento",
        valor: a.dataNascimento
          ? `${fmtDataBR(a.dataNascimento)}${anos !== null ? ` (${anos} anos)` : ""}`
          : "—",
      },
      { rotulo: "E-mail", valor: a.email || "—" },
      { rotulo: "Situação", valor: a.ativo === false ? "Desligado" : "Ativo no programa" },
    ],
  });
  if (a.rankingEstadual || a.rankingNacional) {
    f.grade([
      { rotulo: "RANKING ESTADUAL", valor: a.rankingEstadual || "—" },
      { rotulo: "RANKING NACIONAL", valor: a.rankingNacional || "—" },
      {
        rotulo: "CONSULTADO EM",
        valor: a.rankingAtualizadoEm ? fmtDataBR(a.rankingAtualizadoEm) : "—",
      },
    ], 3);
  }

  // ── Avaliação física ──
  if (pontos.length) {
    desenharAvaliacaoFisica(f, { pontos, sintese });
  } else {
    desenharModuloVazio(f, {
      titulo: "Avaliação física",
      texto: "Nenhuma avaliação física registrada para este atleta.",
    });
  }

  // ── Avaliação técnica ──
  //
  // Sai vazia quando o atleta não tem lançamento, e não some: uma folha
  // que omite a seção deixa quem lê sem saber se o atleta não foi
  // avaliado ou se a folha é que não a traz.
  const tecnicos = (Array.isArray(serieTecnica) ? serieTecnica : []).filter((p) => p && p.data);
  if (tecnicos.length) {
    desenharAvaliacaoTecnica(f, { pontos: tecnicos, escores: [] });
  } else {
    desenharModuloVazio(f, {
      titulo: "Avaliação técnica",
      texto: "Ainda não há lançamento de avaliação técnica no sistema para este atleta.",
    });
  }

  // ── Testes físicos ──
  const dosTestes = (Array.isArray(serieDeTestes) ? serieDeTestes : []).filter((p) => p && p.data);
  if (dosTestes.length) {
    desenharTestesFisicos(f, { pontos: dosTestes, escores: [] });
  } else {
    desenharModuloVazio(f, {
      titulo: "Testes físicos",
      texto: "Ainda não há lançamento de testes físicos no sistema para este atleta.",
    });
  }

  // ── O escore global ──
  //
  // Depois das duas seções, porque é a soma delas, e uma vez só: as duas
  // seções acima recebem `escores` vazio de propósito, para o quadro não
  // sair repetido no meio da folha.
  const comEscore = (Array.isArray(escores) ? escores : []).filter((e) => Number.isFinite(e.global));
  if (comEscore.length) {
    desenharEscoreGlobal(f, comEscore);
  } else if (tecnicos.length || dosTestes.length) {
    desenharModuloVazio(f, {
      titulo: "Escore global de desempenho",
      texto: "O escore global precisa da avaliação técnica e das capacidades físicas na mesma "
        + "onda de avaliação. Este atleta tem apenas uma das duas, então ele não é calculado.",
    });
  }

  // ── Frequência ──
  if (frequencia && frequencia.total && frequencia.total.treinos) {
    desenharFrequencia(f, frequencia);
  }

  // ── Competições ──
  desenharCompeticoes(f, {
    linha: competicoes && competicoes.linha,
    tamanhoDoPodio: competicoes && competicoes.tamanhoDoPodio,
    modalidade: atleta && atleta.modalidade,
  });

  f.assinaturas([
    "Técnico responsável pela modalidade",
    "Preparação física",
    "Coordenação do núcleo",
  ]);

  const nomeArquivo = `Ficha_${arquivoDoAtleta(a)}_${nucleoParaArquivo(nucleoNome, nucleo)}.pdf`;
  baixar(await doc.save(), nomeArquivo);
  return nomeArquivo;
}

// ============================================================
// Ficha de coleta em papel
//
// A folha que o avaliador leva para a pista quando não dá para lançar na
// hora: sem internet, sem aparelho à mão, ou com as duas coisas e o
// atleta esperando. Uma página por atleta, para preencher a lápis e
// transcrever depois.
//
// ── Por que ela sai do mesmo lugar que a tela ───────────────
//
// Os campos vêm de DOBRAS, PERIMETROS_POR_CAMPO, DIAMETROS e ALTURAS, as
// mesmas listas que montam o formulário. Uma folha impressa com campos
// próprios ficaria desatualizada no dia em que um sítio entrasse na
// bateria, e o avaliador mediria o que o papel pede em vez do que o
// sistema guarda. Aqui, acrescentar um sítio muda os dois de uma vez.
//
// O protocolo que a idade manda vai impresso, com o asterisco nos sítios
// que ele exige, porque quem está medindo precisa saber o que não pode
// faltar antes de o atleta ir embora. Os três protocolos aparecem com
// caixa para marcar: a escolha continua sendo de quem mede.
// ============================================================
export async function gerarPDFFichaDeColeta({ atletas, nucleo, nucleoNome }) {
  const lista = (Array.isArray(atletas) ? atletas : []).filter(Boolean);
  const f = await abrirFolhaEmBranco({
    subtitulo: "Ficha de coleta · avaliação física",
    rotuloNucleo: nucleoNome || nucleo || "",
    rodapeDireita: "Transcrever no sistema após a coleta",
  });

  function umaFicha(atleta) {
    f.novaPagina();
    f.identificacao(atleta, [
      { rotulo: "Data da coleta", largura: 150 },
      { rotulo: "Avaliador", largura: 200 },
      { rotulo: "Hora", largura: 110 },
    ]);

    // ── Protocolo e estágio ──
    f.pagina.drawText(txtSeguro("PROTOCOLO DE GORDURA"), {
      x: MX, y: f.y, size: 7.5, font: f.fontB, color: AZUL,
    });
    let x = MX + 130;
    PROTOCOLOS_DE_GORDURA.forEach((id) => {
      x = f.caixa({ x, y: f.y, rotulo: rotuloDoProtocolo(id) });
    });
    f.y -= 14;
    f.pagina.drawText(txtSeguro("ESTÁGIO MATURACIONAL"), {
      x: MX, y: f.y, size: 7.5, font: f.fontB, color: AZUL,
    });
    x = MX + 130;
    ESTAGIOS_MATURACIONAIS.forEach((e) => { x = f.caixa({ x, y: f.y, rotulo: e.rotulo }); });
    f.y -= 18;

    // ── As medidas, em três colunas ──
    const COLS = 3;
    const LARG_COL = (f.LARGURA - 24) / COLS;

    // Nenhum campo vem destacado. A folha é a mesma para qualquer
    // núcleo, e núcleo diferente pode usar protocolo diferente: marcar
    // os sítios de um protocolo faria o avaliador do outro deixar de
    // medir o que ele precisa.
    function grupo(titulo, itens, unidade) {
      f.faixa(`${titulo} (${unidade})`);
      const porColuna = Math.ceil(itens.length / COLS);
      const base = f.y;
      itens.forEach((item, i) => {
        const col = Math.floor(i / porColuna);
        const linha = i % porColuna;
        f.campo({
          x: MX + col * (LARG_COL + 12),
          y: base - linha * 15,
          largura: LARG_COL,
          rotulo: item.rotulo,
          // O grupo tem uma unidade, menos o primeiro, onde peso e
          // estatura convivem: lá cada campo diz a sua.
          unidade: item.unidade || unidade,
        });
      });
      f.y = base - porColuna * 15 - 6;
    }

    grupo("Peso e estatura", [
      { id: "peso", rotulo: "Peso", unidade: "kg" },
      { id: "estatura", rotulo: "Estatura", unidade: "cm" },
    ], "kg e cm");
    grupo("Dobras cutâneas", DOBRAS, "mm");
    grupo("Perímetros", PERIMETROS_POR_CAMPO, "cm");
    grupo("Diâmetros ósseos", DIAMETROS, "cm");
    grupo("Alturas e comprimentos", ALTURAS, "cm");

    f.y -= 2;
    f.linhasEmBranco("OBSERVAÇÕES", 2);
    f.assinatura(
      "Assinatura do avaliador",
      "Transcrever no sistema; o que ficar em branco não produz resultado.",
    );
  }

  if (!lista.length) umaFicha(null);
  else lista.forEach(umaFicha);

  const nomeArquivo = lista.length === 1
    ? `Ficha_de_coleta_${txtSeguro(lista[0].nome || lista[0].name || "atleta").replace(/\s+/g, "_")}.pdf`
    : `Fichas_de_coleta_${nucleoParaArquivo(nucleoNome, nucleo)}.pdf`;
  baixar(await f.doc.save(), nomeArquivo);
  return nomeArquivo;
}

// ============================================================
// A folha EM BRANCO, para preencher à mão
//
// Três fichas de papel saem daqui: a da avaliação física, a da avaliação
// técnica e a dos testes físicos. Elas levam a mesma moldura, o mesmo
// campo com linha para escrever e a mesma faixa de título de grupo, e
// por isso a moldura mora num lugar só.
//
// É a mesma decisão de `criarFolhaA4`, tomada pelo mesmo motivo: com três
// cópias, o dia em que o rodapé mudar vai corrigir uma e deixar duas.
//
// ── O que uma folha de papel tem que a de tela não tem ──────
//
// Linha para escrever, caixa para marcar e espaço. A folha impressa vai
// para a pista com o atleta esperando, e campo sem linha visível é campo
// que fica em branco: a primeira versão desenhava um traço curto
// encostado à direita, e no campo do avaliador, que é largo, parecia que
// não havia linha nenhuma.
// ============================================================
function criarFolhaEmBranco({
  doc, fontR, fontB, logoImg, subtitulo, rotuloNucleo,
  rodapeDireita = "Transcrever no sistema após a coleta",
}) {
  const LARGURA = A4W - 2 * MX;
  const LINHA = rgb(0.55, 0.6, 0.68);
  const f = { doc, fontR, fontB, LARGURA, LINHA, pagina: null, y: 0 };

  function cabecalho(pg) {
    pg.drawRectangle({ x: 0, y: 0, width: 6, height: A4H, color: AZUL });
    let logoLeftX = A4W - MX;
    if (logoImg) {
      const w = 34, h = w * logoImg.height / logoImg.width;
      logoLeftX = A4W - MX - w;
      pg.drawImage(logoImg, { x: logoLeftX, y: A4H - h - 14, width: w, height: h });
    }
    pg.drawText("REVELAR TALENTOS", { x: MX, y: A4H - 34, size: 10, font: fontB, color: AZUL });
    pg.drawText(txtSeguro(subtitulo), { x: MX, y: A4H - 46, size: 8, font: fontB, color: AZUL });
    pg.drawText(txtSeguro(`Núcleo ${rotuloNucleo}`), {
      x: MX, y: A4H - 56, size: 8, font: fontR, color: CINZA,
    });
    pg.drawLine({
      start: { x: MX, y: A4H - 64 }, end: { x: logoLeftX - 12, y: A4H - 64 },
      thickness: 0.5, color: BORDA,
    });
  }

  function rodape(pg) {
    pg.drawText(txtSeguro(IDENTIFICACAO.join(" · ")), {
      x: MX, y: 22, size: 7, font: fontR, color: CINZA,
    });
    const dir = txtSeguro(rodapeDireita);
    pg.drawText(dir, {
      x: A4W - MX - fontR.widthOfTextAtSize(dir, 7), y: 22, size: 7, font: fontR, color: CINZA,
    });
  }

  f.novaPagina = function novaPagina() {
    f.pagina = doc.addPage([A4W, A4H]);
    cabecalho(f.pagina);
    rodape(f.pagina);
    f.y = A4H - 84;
    return f.pagina;
  };

  // Um campo para preencher à mão: rótulo, linha e unidade. A linha
  // começa logo depois do rótulo e vai até o fim do campo.
  f.campo = function campo({ x, y, largura, rotulo, unidade }) {
    const nome = txtSeguro(rotulo);
    const pg = f.pagina;
    pg.drawText(nome, { x, y, size: 7.5, font: fontR, color: TEXTO });
    const fimDaLinha = x + largura - (unidade ? 18 : 0);
    const inicioDaLinha = x + fontR.widthOfTextAtSize(nome, 7.5) + 6;
    if (fimDaLinha > inicioDaLinha + 10) {
      pg.drawLine({
        start: { x: inicioDaLinha, y: y - 2 }, end: { x: fimDaLinha, y: y - 2 },
        thickness: 0.6, color: LINHA,
      });
    }
    if (unidade) {
      pg.drawText(unidade, { x: fimDaLinha + 4, y, size: 6.5, font: fontR, color: CINZA });
    }
  };

  // Caixa para marcar. Devolve o x onde a próxima caixa começa.
  f.caixa = function caixa({ x, y, rotulo }) {
    f.pagina.drawRectangle({
      x, y: y - 1.5, width: 8, height: 8,
      borderColor: LINHA, borderWidth: 0.8, color: BRANCO,
    });
    f.pagina.drawText(txtSeguro(rotulo), { x: x + 12, y, size: 7.5, font: fontR, color: TEXTO });
    return x + 12 + fontR.widthOfTextAtSize(txtSeguro(rotulo), 7.5) + 14;
  };

  // A faixa azul de título de grupo, igual à da folha de resultado.
  f.faixa = function faixa(titulo) {
    f.pagina.drawRectangle({ x: MX, y: f.y - 14, width: LARGURA, height: 14, color: BARRA_BG });
    f.pagina.drawText(txtSeguro(titulo), {
      x: MX + 7, y: f.y - 10, size: 7.5, font: fontB, color: AZUL,
    });
    f.y -= 22;
  };

  // A tarja de identificação do atleta, com os campos do dia.
  f.identificacao = function identificacao(atleta, campos) {
    const a = atleta || {};
    const nome = a.nome || a.name || "";
    const anos = idadeEm(a.dataNascimento);
    f.pagina.drawRectangle({
      x: MX, y: f.y - 44, width: LARGURA, height: 44,
      color: CINZA_CL, borderColor: BORDA, borderWidth: 0.8,
    });
    f.pagina.drawText(txtSeguro(nome || "Atleta: ______________________________"), {
      x: MX + 8, y: f.y - 15, size: 11, font: fontB, color: TEXTO,
    });
    const sub = [
      a.modalidade,
      a.dataNascimento ? `nasc. ${fmtDataBR(a.dataNascimento)}` : null,
      anos !== null ? `${anos} anos` : null,
      a.sexo || null,
    ].filter(Boolean).join("  ·  ");
    f.pagina.drawText(txtSeguro(sub), {
      x: MX + 8, y: f.y - 27, size: 7.5, font: fontR, color: CINZA,
    });
    let x = MX + 8;
    campos.forEach(c => {
      f.campo({ x, y: f.y - 39, largura: c.largura, rotulo: c.rotulo });
      x += c.largura + 25;
    });
    f.y -= 56;
  };

  // Linhas vazias para escrever à mão, embaixo de um título.
  // Um parágrafo de texto corrido, quebrado na largura da folha. A folha
  // em branco é quase toda campo vazio, mas protocolo de teste é texto, e
  // texto que não quebra sai cortado na margem.
  f.texto = function texto(conteudo, { size = 7, cor = CINZA, recuo = 0 } = {}) {
    quebrarTexto(txtSeguro(conteudo), fontR, size, LARGURA - recuo).forEach(ln => {
      f.pagina.drawText(ln, { x: MX + recuo, y: f.y, size, font: fontR, color: cor });
      f.y -= size + 3;
    });
  };

  // Abre página nova quando o que vem não cabe no pé desta. Sem isto, o
  // protocolo do circuito sairia escrito por cima da assinatura.
  f.garantir = function garantir(altura) {
    if (f.y - altura < 96) f.novaPagina();
  };

  f.linhasEmBranco = function linhasEmBranco(titulo, quantas) {
    f.pagina.drawText(txtSeguro(titulo), { x: MX, y: f.y, size: 7.5, font: fontB, color: AZUL });
    f.y -= 12;
    for (let i = 0; i < quantas; i += 1) {
      f.pagina.drawLine({
        start: { x: MX, y: f.y - i * 14 }, end: { x: A4W - MX, y: f.y - i * 14 },
        thickness: 0.5, color: BORDA,
      });
    }
    f.y -= quantas * 14 + 8;
  };

  f.assinatura = function assinatura(rotulo, aviso) {
    f.y -= 20;
    f.pagina.drawLine({
      start: { x: MX, y: f.y }, end: { x: MX + 200, y: f.y }, thickness: 0.6, color: LINHA,
    });
    f.pagina.drawText(txtSeguro(rotulo), {
      x: MX, y: f.y - 10, size: 7, font: fontR, color: CINZA,
    });
    if (aviso) {
      f.pagina.drawText(txtSeguro(aviso), {
        x: MX + 230, y: f.y - 10, size: 7, font: fontR, color: CINZA,
      });
    }
  };

  return f;
}

// A moldura de papel, montada com a logo e a data, para os três
// geradores de ficha em branco não repetirem a abertura do documento.
async function abrirFolhaEmBranco({ subtitulo, rotuloNucleo, rodapeDireita }) {
  const doc = await PDFDocument.create();
  const fontR = await doc.embedFont(StandardFonts.Helvetica);
  const fontB = await doc.embedFont(StandardFonts.HelveticaBold);
  let logoImg = null;
  try {
    logoImg = await doc.embedPng(await fetchBytes(`${process.env.PUBLIC_URL || ""}/relatorio/logo_revelar.png`));
  } catch (e) { /* segue sem logo */ }
  return criarFolhaEmBranco({
    doc, fontR, fontB, logoImg, subtitulo, rotuloNucleo, rodapeDireita,
  });
}

// ── A ficha de coleta da avaliação técnica ───────────────────
//
// Uma página por atleta, com os seis critérios da modalidade dele, o que
// se observa dentro de cada um e a escala impressa. Os critérios vêm de
// `avaliacaoTecnica.js`, a mesma lista que o formulário usa: papel e tela
// não podem pedir coisas diferentes.
export async function gerarPDFFichaDeAvaliacaoTecnica({ atletas, nucleo, nucleoNome }) {
  const lista = (Array.isArray(atletas) ? atletas : []).filter(Boolean);
  const f = await abrirFolhaEmBranco({
    subtitulo: "Ficha de coleta · avaliação técnica",
    rotuloNucleo: nucleoNome || nucleo || "",
    rodapeDireita: "Transcrever no sistema após a avaliação",
  });

  function umaFicha(atleta) {
    f.novaPagina();
    f.identificacao(atleta, [
      { rotulo: "Data", largura: 130 },
      { rotulo: "Avaliador", largura: 190 },
      { rotulo: "Tempo de prática", largura: 110 },
    ]);

    const modalidade = (atleta && atleta.modalidade) || "";
    const modelo = modeloDeCriterio(modalidade);

    f.pagina.drawText(txtSeguro("ESCALA"), { x: MX, y: f.y, size: 7.5, font: f.fontB, color: AZUL });
    f.pagina.drawText(txtSeguro("1 a 3 regular   ·   4 a 7 intermediário   ·   8 a 10 avançado   ·   a nota aceita decimal"), {
      x: MX + 60, y: f.y, size: 7.5, font: f.fontR, color: TEXTO,
    });
    f.y -= 18;

    if (modelo.observa && modelo.observa.length) {
      f.pagina.drawText(txtSeguro(`Em cada critério, observe: ${modelo.observa.join(", ").toLowerCase()}.`), {
        x: MX, y: f.y, size: 7.5, font: f.fontR, color: CINZA,
      });
      f.y -= 16;
    }

    // Os critérios, um por bloco, com espaço para a nota e para o que o
    // avaliador observa dentro dele. Modalidade sem modelo recebe seis
    // blocos com o nome do critério também em branco: núcleo novo pode
    // usar outros critérios, e a folha não pode travar nisso.
    const criterios = modelo.criterios.length
      ? modelo.criterios
      : [1, 2, 3, 4, 5, 6].map(i => ({ id: `c${i}`, rotulo: "" }));

    // Um título por grupo, na ordem da ficha. A do tênis de mesa tem
    // dezenove critérios em três grupos, e uma lista corrida de dezenove
    // linhas no papel faz quem avalia perder o lugar no meio da série de
    // golpes. Modalidade cuja ficha não tem grupo recebe um título só.
    let grupoNaFolha = null;
    criterios.forEach(c => {
      const grupo = c.grupo || `Critérios de ${modalidade || "modalidade"}`;
      if (grupo !== grupoNaFolha) {
        // Título de grupo no pé da página puxa o grupo inteiro para a
        // folha seguinte: sem isto, o cabeçalho fica órfão embaixo.
        if (f.y < 190) f.novaPagina();
        f.faixa(grupo);
        grupoNaFolha = grupo;
      }
      if (f.y < 120) f.novaPagina();
      const alturaDoBloco = 16 + (c.observa ? c.observa.length * 10 : 0);
      if (c.rotulo) {
        f.pagina.drawText(txtSeguro(c.rotulo), {
          x: MX, y: f.y, size: 9, font: f.fontB, color: TEXTO,
        });
        if (c.detalhe) {
          f.pagina.drawText(txtSeguro(c.detalhe), {
            x: MX + f.fontB.widthOfTextAtSize(txtSeguro(c.rotulo), 9) + 8, y: f.y,
            size: 7, font: f.fontR, color: CINZA,
          });
        }
      } else {
        f.campo({ x: MX, y: f.y, largura: 200, rotulo: "Critério" });
      }
      f.campo({
        x: MX + f.LARGURA - 150, y: f.y, largura: 150,
        rotulo: "Nota",
      });
      f.y -= 13;
      (c.observa || []).forEach(o => {
        f.pagina.drawText(txtSeguro(`· ${o}`), {
          x: MX + 10, y: f.y, size: 7, font: f.fontR, color: CINZA,
        });
        f.y -= 10;
      });
      f.y -= alturaDoBloco - 13 - (c.observa ? c.observa.length * 10 : 0) + 4;
    });

    f.y -= 4;
    f.linhasEmBranco("OBSERVAÇÕES", 3);
    f.assinatura(
      "Assinatura do avaliador",
      "Critério sem nota baixa a média, porque ela divide pelo total de critérios.",
    );
  }

  if (!lista.length) umaFicha(null);
  else lista.forEach(umaFicha);

  const nomeArquivo = lista.length === 1
    ? `Ficha_tecnica_${arquivoDoAtleta(lista[0])}.pdf`
    : `Fichas_tecnicas_${nucleoParaArquivo(nucleoNome, nucleo)}.pdf`;
  baixar(await f.doc.save(), nomeArquivo);
  return nomeArquivo;
}

// ── A ficha de coleta dos testes físicos ─────────────────────
//
// Três tentativas por teste, com as marcas de ausência para marcar, e as
// quatro capacidades com espaço para a nota. A bateria vem de
// `testesFisicos.js`, e a tabela de conversão da faixa de idade do
// atleta vai impressa no pé: é ela que o avaliador consulta na pista para
// transformar o resultado medido em nota.
export async function gerarPDFFichaDeTestes({ atletas, nucleo, nucleoNome }) {
  const lista = (Array.isArray(atletas) ? atletas : []).filter(Boolean);
  const f = await abrirFolhaEmBranco({
    subtitulo: "Ficha de coleta · testes físicos",
    rotuloNucleo: nucleoNome || nucleo || "",
    rodapeDireita: "Transcrever no sistema após a coleta",
  });

  function umaFicha(atleta) {
    f.novaPagina();
    f.identificacao(atleta, [
      { rotulo: "Data", largura: 130 },
      { rotulo: "Avaliador", largura: 190 },
      { rotulo: "Hora", largura: 110 },
    ]);

    const modalidade = (atleta && atleta.modalidade) || "";
    const bateria = bateriaDeTestes(modalidade);
    const testes = bateria.length ? bateria : TESTES_PARA_FOLHA_VAZIA;

    // ── Os testes, três tentativas cada ──
    f.faixa("Testes medidos · três tentativas, vale a melhor");
    const LARG_TENTATIVA = 74;
    testes.forEach(t => {
      f.pagina.drawText(txtSeguro(t.rotulo), { x: MX, y: f.y, size: 8.5, font: f.fontB, color: TEXTO });
      f.pagina.drawText(txtSeguro(
        `${t.unidade ? `em ${t.unidade}` : ""}${t.direcao === "menor" ? ", menos é melhor" : t.direcao ? ", mais é melhor" : ""}`,
      ), {
        x: MX + f.fontB.widthOfTextAtSize(txtSeguro(t.rotulo), 8.5) + 8, y: f.y,
        size: 6.5, font: f.fontR, color: CINZA,
      });
      f.y -= 13;
      [1, 2, 3].forEach((n, i) => {
        f.campo({
          x: MX + 10 + i * (LARG_TENTATIVA + 10), y: f.y, largura: LARG_TENTATIVA,
          rotulo: `${n}ª`, unidade: t.unidade,
        });
      });
      // As marcas de ausência ficam na mesma linha das tentativas. Elas
      // são informação, e não lixo: falta e dispensa médica dizem coisas
      // diferentes, e nenhuma das duas pode virar zero na média.
      let x = MX + 10 + 3 * (LARG_TENTATIVA + 10) + 10;
      MARCAS_DE_AUSENCIA.forEach(m => { x = f.caixa({ x, y: f.y, rotulo: m }); });
      f.y -= 19;

      // Os critérios de dentro do teste, quando ele tem. São nota de 1 a
      // 10 dadas por quem cronometrou, e no papel ficam ao lado das
      // tentativas porque é ali que o avaliador está olhando.
      (t.criterios || []).forEach((c, i) => {
        const col = i % 2;
        const largura = (f.LARGURA - 20) / 2 - 10;
        f.campo({
          x: MX + 10 + col * (largura + 10), y: f.y, largura,
          rotulo: c.rotulo, unidade: "de 1 a 10",
        });
        if (col === 1 || i === t.criterios.length - 1) f.y -= 16;
      });
      if ((t.criterios || []).length) f.y -= 4;
    });

    // ── O protocolo, de quem tiver ──
    //
    // O tempo só se compara com o de janeiro se a montagem for a mesma, e
    // quem monta o circuito no ginásio tem esta folha na mão.
    testes.filter(t => t.protocolo).forEach(t => {
      const pr = t.protocolo;
      const itens = [...(pr.medidas || []), ...(pr.execucao || [])];
      f.garantir(60 + itens.length * 10);
      f.faixa(`${t.rotulo} · como montar e aplicar`);
      f.texto(pr.resumo);
      f.y -= 2;
      itens.forEach(linha => {
        f.pagina.drawText(txtSeguro(`· ${linha}`), {
          x: MX, y: f.y, size: 7, font: f.fontR, color: CINZA,
        });
        f.y -= 10;
      });
      if (pr.origem) {
        f.y -= 4;
        f.texto(pr.origem, { size: 6.5 });
      }
      f.y -= 6;
    });

    // ── As capacidades, por nota ──
    f.faixa("Capacidades físicas · nota de 1 a 10");
    f.pagina.drawText(txtSeguro("1 a 3 regular   ·   4 a 7 intermediário   ·   8 a 10 avançado"), {
      x: MX, y: f.y, size: 7.5, font: f.fontR, color: TEXTO,
    });
    f.y -= 16;
    const capacidades = capacidadesDeModalidade(modalidade);
    const lista4 = capacidades.length
      ? capacidades
      : [1, 2, 3, 4].map(i => ({ id: `cap${i}`, rotulo: "" }));
    lista4.forEach(c => {
      if (c.rotulo) {
        f.pagina.drawText(txtSeguro(c.rotulo), { x: MX, y: f.y, size: 8.5, font: f.fontB, color: TEXTO });
        if (c.detalhe) {
          f.pagina.drawText(txtSeguro(c.detalhe), {
            x: MX + f.fontB.widthOfTextAtSize(txtSeguro(c.rotulo), 8.5) + 8, y: f.y,
            size: 6.5, font: f.fontR, color: CINZA,
          });
        }
      } else {
        f.campo({ x: MX, y: f.y, largura: 200, rotulo: "Capacidade" });
      }
      f.campo({ x: MX + f.LARGURA - 150, y: f.y, largura: 150, rotulo: "Nota" });
      f.y -= 16;
    });

    // ── A tabela de conversão ──
    //
    // Impressa por faixa de idade do atleta quando ele tem nascimento no
    // cadastro, e inteira quando não tem: na pista, consultar a faixa
    // errada é pior que ler três linhas a mais.
    const regras = conversoesParaFolha(modalidade, atleta);
    if (regras.length) {
      f.y -= 2;
      f.garantir(40 + regras.length * 10);
      f.faixa("O que o resultado medido sugere de nota");
      regras.forEach(r => {
        f.pagina.drawText(txtSeguro(r), { x: MX, y: f.y, size: 7, font: f.fontR, color: CINZA });
        f.y -= 10;
      });
      f.y -= 6;
      f.pagina.drawText(txtSeguro(
        "A tabela sugere, e quem decide a nota é quem avaliou. Ela cobre dois testes por modalidade.",
      ), { x: MX, y: f.y, size: 7, font: f.fontR, color: CINZA });
      f.y -= 14;
    }

    f.garantir(70);
    f.linhasEmBranco("OBSERVAÇÕES", 2);
    f.assinatura(
      "Assinatura do avaliador",
      "Transcrever no sistema; o que ficar em branco não produz resultado.",
    );
  }

  if (!lista.length) umaFicha(null);
  else lista.forEach(umaFicha);

  const nomeArquivo = lista.length === 1
    ? `Ficha_testes_${arquivoDoAtleta(lista[0])}.pdf`
    : `Fichas_testes_${nucleoParaArquivo(nucleoNome, nucleo)}.pdf`;
  baixar(await f.doc.save(), nomeArquivo);
  return nomeArquivo;
}

// As marcas de ausência, na ordem em que a tela as oferece.
const MARCAS_DE_AUSENCIA = ["Não realizado", "Falta", "Dispensa médica"];

// Quando a modalidade não tem bateria conhecida, a folha sai com quatro
// testes em branco, e não vazia: o avaliador escreve o que aplicou.
const TESTES_PARA_FOLHA_VAZIA = [
  { id: "t1", rotulo: "Teste", unidade: "" },
  { id: "t2", rotulo: "Teste", unidade: "" },
  { id: "t3", rotulo: "Teste", unidade: "" },
  { id: "t4", rotulo: "Teste", unidade: "" },
];

// As regras de conversão em texto, da faixa do atleta ou de todas.
function conversoesParaFolha(modalidade, atleta) {
  const tabela = CONVERSOES_DE_TESTE[modalidade];
  if (!tabela) return [];
  const anos = atleta ? idadeEm(atleta.dataNascimento) : null;
  // Com nascimento no cadastro, sai a régua de cada teste; sem ele, saem
  // todas. A faixa é escolhida por TESTE, e não uma só para a folha
  // inteira: alguns testes têm o corte etário deles, e imprimir apenas a
  // faixa do programa deixaria de fora a régua do circuito, que mora nas
  // faixas de 8 a 12, 13 a 16 e 17 a 21.
  if (anos !== null) {
    const linhas = [];
    bateriaDeTestes(modalidade).forEach(teste => {
      const faixa = faixaEtariaDoTesteEm(anos, teste.id);
      if (!faixa) return;
      (tabela[faixa.id] || [])
        .filter(r => r.teste === teste.id)
        .forEach(r => linhas.push(`Faixa ${faixa.rotulo}: ${r.texto}`));
    });
    if (linhas.length) return linhas;
  }
  const rotulos = [...FAIXAS_ETARIAS_DO_TESTE, ...FAIXAS_DO_CIRCUITO];
  return Object.entries(tabela).flatMap(([id, regras]) => {
    const rotulo = (rotulos.find(x => x.id === id) || {}).rotulo || id;
    return [`Faixa ${rotulo}`, ...regras.map(r => `  ${r.texto}`)];
  });
}

// ── A avaliação técnica, desenhada ───────────────────────────
//
// A mesma seção serve à folha só de avaliação técnica e à ficha
// completa, como em `desenharAvaliacaoFisica`. Recebe a série JÁ
// COMPILADA: refazer a conversão de tempo em nota aqui criaria um
// segundo lugar onde a nota é calculada, e no dia em que uma regra
// mudasse, papel e tela discordariam sem ninguém perceber.
function desenharAvaliacaoTecnica(f, { pontos, escores = [] }) {
  const n = numPt;
  const emBR = dataOuTraco;
  const ultimo = pontos[pontos.length - 1];
  const laudo = laudoTecnico(ultimo);

  f.secao(`Avaliação técnica de ${emBR(ultimo.data)}`);
  f.grade([
    { rotulo: "MÉDIA DOS CRITÉRIOS", valor: n(ultimo.media, 2), apoio: laudo ? laudo.classificacao : null },
    { rotulo: "SOMATÓRIO", valor: `${n(ultimo.somatorio, 1)} de ${ultimo.maximo}` },
    { rotulo: "CRITÉRIOS", valor: String(ultimo.divisor) },
    { rotulo: "AVALIADOR", valor: ultimo.avaliador || "não informado" },
  ]);

  // As notas, uma linha por critério. É o miolo da folha: a média diz
  // onde o atleta está, e esta tabela diz em quê.
  // O período de observação, quando a ficha o traz. A nota do técnico
  // resume uma semana de treino, e a folha impressa precisa dizer isso:
  // sem a linha, a data no alto parece a sessão em que tudo foi visto.
  if (ultimo.periodoDe || ultimo.periodoAte) {
    f.paragrafo(
      `Observação de ${dataOuTraco(ultimo.periodoDe)} a ${dataOuTraco(ultimo.periodoAte)}. `
      + "A nota resume o que o avaliador viu no período, e não uma sessão de treino.",
      { size: 7.5 },
    );
  }

  // A escala desta modalidade, com os nomes que ela dá às faixas.
  if (ultimo.escala) {
    f.paragrafo(
      "Escala: "
      + ultimo.escala.faixas.map((x) => `${x.de} a ${x.ate} ${x.rotulo.toLowerCase()}`).join(", ")
      + "."
      + (ultimo.escala.observacao ? ` ${ultimo.escala.observacao}` : ""),
      { size: 7 },
    );
  }

  const COLS_CRIT = [
    // O grupo entra como coluna porque a ficha do tênis de mesa tem
    // dezenove critérios: sem ele, a tabela é uma lista corrida e quem
    // lê não acha onde terminam os golpes.
    { t: "Grupo", w: 88, get: (c) => c.grupo || "—" },
    { t: "Critério", w: null, get: (c) => c.rotulo },
    { t: "Nota", w: 46, get: (c) => n(c.nota, 1) },
    { t: "Faixa", w: 76, get: (c) => (c.faixa ? c.faixa.rotulo : "—") },
    {
      t: "Origem",
      w: 150,
      // Ausência registrada, e não esquecimento: o critério saiu do
      // divisor, e a folha tem de dizer o motivo.
      get: (c) => (Number.isFinite(c.nota)
        ? "nota do avaliador"
        : c.ausencia ? rotuloDaAusenciaDoCriterio(c.ausencia) : "sem nota"),
    },
  ];
  desenharTabela(f, COLS_CRIT, ultimo.criterios);

  if (!ultimo.completa && ultimo.faltam.length) {
    f.paragrafo(
      `Ficaram sem nota: ${ultimo.faltam.join(", ")}. A média divide pelo total de `
      + "critérios, então ela resulta inferior ao desempenho efetivamente avaliado.",
    );
  }
  if (ultimo.ausentes && ultimo.ausentes.length) {
    f.paragrafo(
      `${ultimo.ausentes.map((a) => `${a.rotulo} (${a.motivo.toLowerCase()})`).join(", ")}. `
      + `Critério com ausência registrada sai do divisor, então a média deste dia foi tirada `
      + `de ${ultimo.divisor} critério(s) e não de ${ultimo.criterios.length}. Registrar a `
      + "ausência como nota baixa penalizaria o atleta por uma avaliação que ele não pôde "
      + "fazer.",
    );
  }

  if (laudo) {
    f.paragrafo(laudo.texto, { size: 8, cor: TEXTO });
    f.paragrafo(laudo.ressalva, { size: 7 });
  }

  // ── A série ──
  if (pontos.length > 1) {
    f.secao("Série do atleta");
    const ids = [];
    const rotulos = {};
    pontos.forEach((p) => p.criterios.forEach((c) => {
      if (!ids.includes(c.id)) ids.push(c.id);
      rotulos[c.id] = c.rotulo;
    }));
    const COLS = [
      { t: "Data", w: 58, get: (p) => emBR(p.data) },
      ...ids.map((id) => ({
        t: rotulos[id],
        w: null,
        get: (p) => {
          const c = p.criterios.find((x) => x.id === id);
          return c && Number.isFinite(c.nota) ? n(c.nota, 1) : "—";
        },
      })),
      { t: "Média", w: 44, get: (p) => n(p.media, 2) },
    ];
    desenharTabela(f, COLS, pontos);
  }

  // ── O escore global ──
  const comEscore = escores.filter((e) => Number.isFinite(e.global));
  if (comEscore.length) {
    desenharEscoreGlobal(f, comEscore);
  }
}

// ── Os testes físicos, desenhados ────────────────────────────
function desenharTestesFisicos(f, { pontos, escores = [] }) {
  const n = numPt;
  const emBR = dataOuTraco;
  const ultimo = pontos[pontos.length - 1];
  const laudo = laudoDosTestes(ultimo);

  f.secao(`Testes físicos de ${emBR(ultimo.data)}`);
  const medidos = ultimo.testes.filter((t) => t.valor !== null);
  f.grade([
    { rotulo: "TESTES COM RESULTADO", valor: `${medidos.length} de ${ultimo.testes.length}` },
    { rotulo: "MÉDIA DAS CAPACIDADES", valor: n(ultimo.media, 2) },
    { rotulo: "FAIXA DE IDADE", valor: ultimo.faixaEtaria ? ultimo.faixaEtaria.rotulo : "—" },
    { rotulo: "AVALIADOR", valor: ultimo.avaliador || "não informado" },
  ]);

  const COLS_TESTE = [
    { t: "Teste", w: null, get: (t) => t.rotulo },
    { t: "Resultado", w: 64, get: (t) => (t.valor !== null ? `${n(t.valor, t.unidade === "s" ? 2 : 1)} ${t.unidade}` : rotuloDaAusenciaPDF(t.ausencia)) },
    { t: "Tentativas", w: 92, get: (t) => (t.tentativas.length > 1 ? t.tentativas.map((v) => n(v, t.unidade === "s" ? 2 : 1)).join(" · ") : "—") },
    { t: "Evolução", w: 56, get: (t) => (Number.isFinite(t.evolucao) ? `${t.evolucao > 0 ? "+" : ""}${n(t.evolucao, 1)}%` : "—") },
    { t: "Capacidade", w: 120, get: (t) => t.capacidade },
  ];
  desenharTabela(f, COLS_TESTE, ultimo.testes);

  // ── O que o avaliador viu dentro de um teste ──
  //
  // Vem depois da tabela dos testes e antes das capacidades, porque é
  // leitura do teste e não capacidade do atleta: estas notas não entram
  // no somatório nem no escore global. O circuito do tênis de mesa é o
  // caso: um cronômetro, cinco coisas vistas.
  ultimo.testes
    .filter((t) => (t.criterios || []).length)
    .forEach((t) => {
      f.secao(`${t.rotulo} · critérios observados`);
      desenharTabela(f, [
        { t: "Critério", w: null, get: (c) => c.rotulo },
        { t: "Nota", w: 46, get: (c) => n(c.nota, 1) },
        { t: "Faixa", w: 96, get: (c) => (c.faixa ? c.faixa.rotulo : "—") },
      ], t.criterios);
      f.paragrafo(
        "Notas atribuídas pelo avaliador durante a aplicação do teste. Não integram o "
        + "somatório das capacidades nem o escore global: registram o que o tempo medido "
        + "não informa.",
        { size: 7 },
      );
      if (t.protocolo && t.protocolo.origem) {
        f.paragrafo(t.protocolo.origem, { size: 7 });
      }
    });

  f.secao("Capacidades físicas");
  const COLS_CAP = [
    { t: "Capacidade", w: null, get: (c) => c.rotulo },
    { t: "Nota", w: 46, get: (c) => n(c.nota, 1) },
    { t: "Faixa", w: 96, get: (c) => (c.faixa ? c.faixa.rotulo : "—") },
  ];
  desenharTabela(f, COLS_CAP, ultimo.capacidades);
  f.paragrafo(
    `Somatório de ${n(ultimo.somatorio, 1)} em ${ultimo.divisor} capacidades, média `
    + `${n(ultimo.media, 2)}. A média entra no escore global com peso de 25%, e divide pelo `
    + "total de capacidades, inclusive as que ficaram sem nota.",
    { size: 7.5 },
  );

  if (!ultimo.completa && ultimo.faltam.length) {
    f.paragrafo(`Coleta incompleta em: ${ultimo.faltam.join(", ")}.`);
  }
  if (laudo) {
    f.paragrafo(laudo.texto, { size: 8, cor: TEXTO });
    f.paragrafo(laudo.ressalva, { size: 7 });
  }

  // ── A série ──
  if (pontos.length > 1) {
    f.secao("Série do atleta");
    const ids = [];
    const metas = {};
    pontos.forEach((p) => p.testes.forEach((t) => {
      if (!ids.includes(t.id)) ids.push(t.id);
      metas[t.id] = t;
    }));
    const COLS = [
      { t: "Data", w: 58, get: (p) => emBR(p.data) },
      ...ids.map((id) => ({
        t: metas[id].rotulo,
        w: null,
        get: (p) => {
          const t = p.testes.find((x) => x.id === id);
          if (!t) return "—";
          return t.valor !== null ? n(t.valor, t.unidade === "s" ? 2 : 1) : rotuloDaAusenciaPDF(t.ausencia);
        },
      })),
      { t: "Capac.", w: 44, get: (p) => n(p.media, 2) },
    ];
    desenharTabela(f, COLS, pontos);
    // A direção sai dos testes DESTA modalidade. A frase antiga citava
    // os testes das três de uma vez, e a ficha de um skatista explicava o
    // tempo de pista do BMX.
    f.paragrafo(
      `${direcaoDosTestesEmTexto(ultimo.testes)} A evolução já sai com o sinal do desempenho, `
      + `e variação de até ${FAIXA_MORTA_DO_TESTE}% é lida como estável, porque é o erro do `
      + "próprio instrumento.",
      { size: 7 },
    );
  }

  const comEscore = escores.filter((e) => Number.isFinite(e.global));
  if (comEscore.length) {
    desenharEscoreGlobal(f, comEscore);
  }
}

function rotuloDaAusenciaPDF(codigo) {
  const marca = Object.values(AUSENCIAS_DO_TESTE).find((a) => a.codigo === codigo);
  return marca ? marca.rotulo : "—";
}

// ── O escore global, desenhado ───────────────────────────────
//
// Aparece nas três folhas que o mostram, e por isso é uma função: o
// número é o mesmo, a conta ao lado dele é a mesma, e a ressalva de que
// ele não compara modalidades é a mesma.
function desenharEscoreGlobal(f, escores) {
  const n = numPt;
  const ultimo = escores[escores.length - 1];
  f.secao("Escore global de desempenho");
  f.grade([
    { rotulo: "ESCORE", valor: n(ultimo.global, 1), apoio: ultimo.classificacao },
    {
      rotulo: "TÉCNICA",
      valor: n(ultimo.tecnica.media, 2),
      apoio: `peso 75%, entra com ${n(ultimo.tecnica.contribuicao, 2)}`,
    },
    {
      rotulo: "CAPACIDADES FÍSICAS",
      valor: n(ultimo.fisica.media, 2),
      apoio: `peso 25%, entra com ${n(ultimo.fisica.contribuicao, 2)}`,
    },
    // Conta os escores, e não as avaliações: a lista que chega aqui já
    // veio filtrada pelas ondas em que as DUAS metades existem. Rotular
    // de "avaliações" fazia a folha dizer que o atleta foi avaliado uma
    // vez quando ele tinha três avaliações técnicas e um escore.
    {
      rotulo: "ESCORES NO ANO",
      valor: String(escores.length),
      apoio: "ondas com as duas avaliações",
    },
  ]);
  if (ultimo.nivel) f.paragrafo(ultimo.nivel.descricao, { size: 8, cor: TEXTO });

  if (escores.length > 1) {
    const primeiro = escores[0];
    const evo = evolucaoDoEscoreGlobal(primeiro.global, ultimo.global);
    f.paragrafo(
      `De ${dataOuTraco(primeiro.data)} a ${dataOuTraco(ultimo.data)}, o escore foi de `
      + `${n(primeiro.global, 1)} para ${n(ultimo.global, 1)}, variação de `
      + `${evo.absoluta > 0 ? "+" : ""}${n(evo.absoluta, 1)} ponto, ou `
      + `${evo.percentual > 0 ? "+" : ""}${n(evo.percentual, 1)}%.`,
      { size: 8, cor: TEXTO },
    );
  }

  f.paragrafo(
    "O escore é a média dos critérios técnicos com peso de 75%, mais a média das capacidades "
    + "físicas com peso de 25%, na escala de 0 a 10. As faixas são: até 2,9 muito abaixo do "
    + "ideal, de 3 a 4,9 abaixo da média, de 5 a 6,9 regular, de 7 a 8,9 bom desempenho, de 9 "
    + "a 10 excelente. Avaliadores diferentes calibram a escala de modo diferente, então o "
    + "escore serve para acompanhar o atleta ao longo do tempo, e não para comparar atleta de "
    + "uma modalidade com atleta de outra.",
    { size: 7 },
  );
}

// ── A tabela de uma seção ────────────────────────────────────
//
// Cabeçalho, linhas e paginação, para as seções não repetirem o mesmo
// laço. `w` nulo divide o que sobra da largura entre as colunas livres,
// e a quebra de página pede a folha e repete o cabeçalho, como manda a
// nota de `criarFolhaA4`.
function desenharTabela(f, colunas, linhas) {
  const fixas = colunas.reduce((soma, c) => soma + (c.w || 0), 0);
  const livres = colunas.filter((c) => !c.w).length;
  const largura = colunas.map((c) => c.w || (f.LARGURA - fixas) / Math.max(1, livres));

  function cabecalho() {
    f.pagina.drawRectangle({ x: MX, y: f.y - 14, width: f.LARGURA, height: 14, color: CINZA_CL });
    let x = MX + 4;
    colunas.forEach((c, i) => {
      f.pagina.drawText(txtSeguro(c.t), { x, y: f.y - 10, size: 6.5, font: f.fontB, color: CINZA });
      x += largura[i];
    });
    f.y -= 18;
  }

  f.garantirEspaco(34);
  cabecalho();
  linhas.forEach((linha) => {
    f.garantirEspaco(14, cabecalho);
    let x = MX + 4;
    colunas.forEach((c, i) => {
      const bruto = txtSeguro(String(c.get(linha)));
      // Texto que não cabe na coluna é cortado com reticências, e não
      // deixado a invadir a coluna vizinha: duas colunas sobrepostas no
      // papel não se leem, e a tabela inteira perde o alinhamento.
      let texto = bruto;
      const limite = largura[i] - 6;
      if (f.fontR.widthOfTextAtSize(texto, 7) > limite) {
        while (texto.length > 1 && f.fontR.widthOfTextAtSize(`${texto}…`, 7) > limite) {
          texto = texto.slice(0, -1);
        }
        texto = `${texto}…`;
      }
      f.pagina.drawText(texto, { x, y: f.y, size: 7, font: f.fontR, color: TEXTO });
      x += largura[i];
    });
    f.y -= 13;
  });
  f.y -= 6;
}

// ── Os geradores de folha ────────────────────────────────────

export async function gerarPDFAvaliacaoTecnica({
  atleta, serie, escores = [], nucleo, nucleoNome,
}) {
  const a = atleta || {};
  const pontos = (Array.isArray(serie) ? serie : []).filter((p) => p && p.data);
  const f = await abrirFolhaA4({
    subtitulo: "Avaliação técnica",
    rotuloNucleo: nucleoNome || nucleo || "",
  });
  f.novaPagina();
  desenharIdentificacao(f, { atleta: a, titulo: "AVALIAÇÃO TÉCNICA" });

  if (!pontos.length) {
    f.paragrafo("Nenhuma avaliação técnica registrada para este atleta.", { size: 9, cor: TEXTO });
  } else {
    desenharAvaliacaoTecnica(f, { pontos, escores });
    f.assinaturas([
      "Técnico responsável pela modalidade",
      "Coordenação do núcleo",
      "Atleta ou responsável legal",
    ]);
  }

  const nomeArquivo = `Avaliacao_Tecnica_${arquivoDoAtleta(a)}_${nucleoParaArquivo(nucleoNome, nucleo)}.pdf`;
  baixar(await f.doc.save(), nomeArquivo);
  return nomeArquivo;
}

export async function gerarPDFTestesFisicos({
  atleta, serie, escores = [], nucleo, nucleoNome,
}) {
  const a = atleta || {};
  const pontos = (Array.isArray(serie) ? serie : []).filter((p) => p && p.data);
  const f = await abrirFolhaA4({
    subtitulo: "Testes físicos",
    rotuloNucleo: nucleoNome || nucleo || "",
  });
  f.novaPagina();
  desenharIdentificacao(f, { atleta: a, titulo: "TESTES FÍSICOS" });

  if (!pontos.length) {
    f.paragrafo("Nenhum teste físico registrado para este atleta.", { size: 9, cor: TEXTO });
  } else {
    desenharTestesFisicos(f, { pontos, escores });
    f.assinaturas([
      "Preparação física",
      "Coordenação do núcleo",
      "Atleta ou responsável legal",
    ]);
  }

  const nomeArquivo = `Testes_Fisicos_${arquivoDoAtleta(a)}_${nucleoParaArquivo(nucleoNome, nucleo)}.pdf`;
  baixar(await f.doc.save(), nomeArquivo);
  return nomeArquivo;
}

// A abertura do documento A4 do padrão da casa, com a logo e a data de
// emissão, para os geradores não repetirem as mesmas doze linhas.
async function abrirFolhaA4({ subtitulo, rotuloNucleo }) {
  const doc = await PDFDocument.create();
  const fontR = await doc.embedFont(StandardFonts.Helvetica);
  const fontB = await doc.embedFont(StandardFonts.HelveticaBold);
  let logoImg = null;
  try {
    logoImg = await doc.embedPng(await fetchBytes(`${process.env.PUBLIC_URL || ""}/relatorio/logo_revelar.png`));
  } catch (e) { /* segue sem logo */ }
  const hoje = new Date();
  const emitidoEm = `${String(hoje.getDate()).padStart(2, "0")}/${String(hoje.getMonth() + 1).padStart(2, "0")}/${hoje.getFullYear()}`;
  return criarFolhaA4({ doc, fontR, fontB, logoImg, subtitulo, rotuloNucleo, emitidoEm });
}
